// src/components/desk/MatchSuggestions.jsx — the match-suggestions cluster: gap/completeness
// helpers (BetterToKnow, clientGaps, CompletenessMeter, listingCompleteness, propertyGaps),
// the match-explain satellites (MatchExplain, ContactReveal, access requests, verdict actions,
// offers table, certainty bars), share helpers (listingDeepLink/CopyListingLink/WhatsAppShare),
// criteria diff (diffCriteriaOps), the TIER_*/STANCE_* chip vocab, and MatchSuggestions itself —
// extracted VERBATIM from IntakeTab.jsx (DESK_MODE_REWRITE_PLAN §3 decomposition,
// behavior-preserving). Used by both the client edit modal and the listing lineage modal —
// which is why it is its own file rather than living in either. Everything IntakeTab (or a
// sibling desk module) still references is exported and imported back there.

import { useEffect, useMemo, useRef, useState } from 'react'
import { t, tLog, getLanguage } from '../../lib/i18n.js'
import { districtLabel } from '../../lib/districtLabel.js'
import { intentLabel } from '../../lib/labels.js'
import { listingDeepLink, serializeSearch } from '../../lib/deskUrl.js'
import { Icon, IconText, ROLE_ICON } from '../Icon.jsx'
import { card, input, btn, chip, Badge, COUNTER, PriceHistoryBadge, pill, useMoney, Money, timeAgo } from './primitives.jsx'
import { formatMoney } from '../../lib/money.js'
import { ListingGallery, addressForDisplay } from './ListingGallery.jsx'
import { PlatformPill } from './HistoryCards.jsx'
import { coopLockLabel } from './CooperationChips.jsx'
import { platformKey } from '../ListingsTab.jsx'
import { MeetingModal, seedFromMatchRow } from './MeetingModal.jsx'
import { ShareModal } from './ShareModal.jsx'
import { NegotiationModal } from './NegotiationModal.jsx'
import {
  amendListingTerms, commitIntakeEnvelope, confirmRequirementActive, decideAccessRequest,
  fileAccessRequest, fileRequirementAccessRequest, getAccessRequests, getAgentListings, getDistricts,
  getOpenTouches, getPropertyMatchingClients, getRequirementMatches, getRequirementMatchesStored, getRequirementProbe, recordAttemptOutcome,
  revealContact, setListingPolicy, setMatchVerdict, setPropertyFeature,
} from '../../db/core.js'
// B113 escape hatch: the requirement -> committed-search mapping and the URL writer it
// rides. Both are the EXISTING mechanisms (saved searches use the same pair) — the hatch
// adds no navigation code of its own.
import { requirementToCommitted, committedToAdvFilters } from '../../lib/deskSearchState.js'
// ONE answer to "who can close this gap", shared with the v2 matrix. See CLIENT_GAP_RE below
// for what it replaces and why the replacement is not cosmetic.
// `clientFillFor` / `ruleIdOf` join it for round-2 §3: the client card's violet rows carry
// the same fill control the matrix's do, and which control closes a gap is this module's
// single answer for both surfaces.
import { whoAnswers, clientFillFor, ruleIdOf } from '../../lib/matchGaps.js'
// The check-label helpers (CHECK_MARK / humanMatchKey / deltaLabel and the feature-vocab
// cache they read) MOVED to matchBands.jsx (Q1 client-surface extraction): the band card
// needs them, MatchMatrix imports from this file, and a leaf module is the only home that
// closes no cycle. Re-exported unchanged in the bottom export block, so IntakeTab and
// MatchMatrix keep their existing imports. The band card pieces (bandsOf/Band/BandRow/
// GapRow/QuietRow/summarySentence/askedOf) are what the requirement card expands into.
// ⚠️ The CAPTURE CONTROLS moved there too (round-2 §3) — with the stance/term vocabularies
// and `ownerAnswerKind` they close over, which used to live in THIS file. The client card's
// fourth column opens the same three owner doors and the same client door the matrix does,
// and a second implementation of them is the drift B273 names. All six are re-exported
// unchanged at the bottom of this file.
import {
  CHECK_MARK, checkTier, bareCheckKey, logUnmappedMatchKey, ensureFeatureVocab,
  humanMatchKey, deltaLabel,
  bandsOf, Band, BandRow, GapRow, QuietRow, summarySentence, askedOf, sourceOf,
  STANCE_CYCLE, STANCE_STYLE, STANCE_ICON, TERM_CHIPS, NUM_TERM_CHIPS, ownerAnswerKind,
  OwnerFill, ClientFill, useAnswerDoor, knownFromDemand, RoleTag,
} from './matchBands.jsx'
// Completeness is a projection of the identity capability table (taxonomy.js, SEARCH_GRAMMAR §8)
// — a land record must never be "missing rooms" (owner report 2026-08-20). Applicability comes
// from the same rows the search/interview already read, never from per-kind conditionals here.
import { identityCapability } from '../../lib/taxonomy.js'
import { keepInView } from '../../lib/keepInView.js'

// ─── Share deep link (Phase 1a) ────────────────────────────────────────────
// listingDeepLink MOVED to lib/deskUrl.js (2026-07-25) — it is the write half of the
// '#datadesk/listing/<id>' hash form that file already parses, and ShareMenu needs it
// without importing this module (MatchSuggestions imports CooperationChips, so a share
// menu importing back from here would close a cycle). Still re-exported at the bottom
// of this file, so every existing consumer keeps working unchanged.
// Copies the bare deep link only — no address prefix (2026-07-15 request: agents want a
// clean pasteable URL). compact = emoji-only (match rows / mobile); otherwise the text label
// is hidden on small screens (`hidden sm:inline`) so the top-bar button collapses to 🔗 on
// mobile too.
function CopyListingLink({ listingId, compact = false }) {
  const [copied, setCopied] = useState(false)
  if (!listingId) return null
  async function copy(e) {
    e?.stopPropagation()   // match rows are click-to-open — don't also open the modal
    try {
      await navigator.clipboard.writeText(listingDeepLink(listingId))
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch { /* clipboard blocked (insecure context / denied) — no-op */ }
  }
  return (
    <button type="button" onClick={copy} title={t('desk_share_copy_tooltip')}
      className={compact
        ? 'text-xs text-muted-foreground hover:text-foreground shrink-0'
        : 'inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-border hover:border-primary/40 text-muted-foreground hover:text-foreground shrink-0'}>
      <span>{copied ? <Icon name="check" /> : <Icon name="link" />}</span>
      {!compact && <span className="hidden sm:inline">{copied ? t('desk_share_copied') : t('desk_share_copy_link')}</span>}
    </button>
  )
}

// Phase 1b — one-tap "ask this agent about this property". Rendered ONLY inside a
// revealed ContactReveal (so it inherits the same reveal gate + audit log), it opens a
// WhatsApp chat that is BOTH pre-addressed to the owning agent AND pre-fills the listing
// deep link. wa.me wants digits only; Georgian 9-digit numbers get 995 prepended (same
// convention as ContactRow). No listing id → renders nothing (URL would be dead).
function WhatsAppShare({ phone, listingId, address }) {
  if (!phone || !listingId) return null
  const digits = String(phone).replace(/\D/g, '')
  const wa = digits.length === 9 ? `995${digits}` : digits
  const url = listingDeepLink(listingId)
  const text = address ? `${address}\n${url}` : url
  return (
    <a href={`https://wa.me/${wa}?text=${encodeURIComponent(text)}`}
      target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
      title={t('desk_share_whatsapp_tooltip')}
      className="text-xs hover:text-foreground shrink-0"><Icon name="message-circle" /></a>
  )
}

const EDITABLE_CRITERIA_FIELDS = ['price_min', 'price_max', 'currency', 'area_sqm_min', 'area_sqm_max',
  'land_sqm_min', 'land_sqm_max',
  'rooms_min', 'rooms_max', 'bedrooms_min', 'bedrooms_max', 'max_drive_minutes',
  'floor_min', 'floor_max', 'floor_not_first', 'floor_not_last',
  'mortgage_ready', 'needs_deeded_parking', 'has_pets', 'has_children',
  'district_codes', 'district_must_codes', 'excluded_district_codes',
  'must_have_codes', 'nice_to_have_codes', 'must_not_have_codes', 'condition_codes',
  'household_codes', 'occupants', 'children_ages', 'move_in_date', 'check_out_date', 'required_power_kw',
  // B202 part 2 (2026-07-27): the two move-in money ceilings. Both were already in
  // execute_proposal's allowlist and in the intake contract — only the client-side
  // diff list and the agent's form were missing, so the AI could set them and no
  // human could correct them. The scorer reads both (deposit + prepayment checks).
  'max_deposit_months', 'prepayment_months',
  // 0381 (2026-08-03): the wanted complex. The fill form could WRITE it and nothing
  // could withdraw it — a client who tapped the wrong development was stuck with a
  // constraint the Desk could not see or clear, and their fill link is single-use.
  'complex_codes',
  // review 2026-09-23: proximity points (0267, B220). ClientEditModal's NearPointsControl and
  // the paste-to-enrich path both write form.near_points, and without this entry the diff never
  // saw them — the edit was silently dropped on Save. The server lists near_points in
  // AMENDABLE_FIELDS and sanitizes an amend op as "a set replaces the whole array; clear (null)
  // removes it", so it is diffed below as a whole-value SET/CLEAR, not an array 'replace'.
  'near_points']
// Structured-array fields compared by VALUE (JSON) and written as set/clear, never 'replace'.
const WHOLE_VALUE_FIELDS = ['near_points']
const ARRAY_EDIT_FIELDS = ['district_codes', 'district_must_codes', 'excluded_district_codes',
  'must_have_codes', 'nice_to_have_codes', 'must_not_have_codes', 'condition_codes', 'household_codes', 'children_ages',
  'complex_codes']

// Only fields that actually changed become ops — 'set'/'clear' for scalars (idempotent, but
// an accurate diff keeps the audit trail ("changed: price, districts") honest), 'replace' for
// arrays (order-insensitive comparison so re-saving an untouched multi-select is a no-op).
function diffCriteriaOps(before, after) {
  const ops = []
  for (const field of EDITABLE_CRITERIA_FIELDS) {
    const bv = before?.[field], av = after?.[field]
    if (WHOLE_VALUE_FIELDS.includes(field)) {
      // review 2026-09-23: an empty list is "no constraint" — the server sanitizes [] to null.
      const norm = v => (Array.isArray(v) && v.length ? v : null)
      const b = norm(bv), a = norm(av)
      if (JSON.stringify(b) !== JSON.stringify(a)) ops.push({ field, op: a === null ? 'clear' : 'set', value: a })
    } else if (ARRAY_EDIT_FIELDS.includes(field)) {
      const b = [...(bv || [])].sort(), a = [...(av || [])].sort()
      if (JSON.stringify(b) !== JSON.stringify(a)) ops.push({ field, op: 'replace', value: av || [] })
    } else {
      const bn = bv === undefined || bv === '' ? null : bv
      const an = av === undefined || av === '' ? null : av
      if (bn !== an) ops.push({ field, op: an === null ? 'clear' : 'set', value: an })
    }
  }
  return ops
}

const TIER_STYLE = {
  must: 'bg-green-600/20 border-green-600 text-green-500',
  nice: 'bg-blue-600/20 border-blue-600 text-blue-400',
  not:  'bg-red-600/20 border-red-600 text-red-400',
}
// TEXT marks, not icons: these are interpolated into template literals (tooltips,
// checkLabel) as well as rendered, and an <Icon> in a `${}` stringifies to
// [object Object]. Keep them as characters — the 2026-07-20 emoji sweep converted
// them and this is the revert, deliberately.
const TIER_ICON = { must: '✓', nice: '★', not: '✗' }

// STANCE_CYCLE / STANCE_STYLE / STANCE_ICON — moved to ./matchBands.jsx (round-2 §3): the
// capture controls moved there so the client card can open the same write doors, and they
// close over these three tables. Imported above, re-exported at the bottom of this file, so
// ManualPropertyModal and IntakeTab keep their existing imports unchanged.

// ─── StanceChip — ONE mechanism for every chip that carries a stance ──────────────────
// Owner ruling 2026-07-27: "we need exact mechanism for filtering chips — little menu to
// choose from — and we need that menu in the actual modal which opens from the property
// modal." Before this there were TWO grammars for the same act: the gap chips in this file
// popped a menu, while the property terms modal CYCLED (tap → allowed → preferred → not →
// only → off). The cycle is why nobody set "❗ only": four taps to reach it, four more to
// undo, and no way to see the choices without committing to one. Same chip, same colours,
// same icons, one interaction — pick from a list.
//
// Generic over its OPTIONS, not over its vocabulary: capture surfaces pass the four
// listing_policy stances; the Desk filter passes its two filter intents (see IntakeTab).
// That keeps one component without pretending a filter mode and a recorded stance are the
// same kind of thing — they are not, and conflating them is how a filter starts claiming
// the owner said something.
function StanceChip({ label, value, options, onPick, prompt, disabled = false, open, onToggle, size = 'md' }) {
  const cur = options.find(o => o.value === value) || null
  const pad = size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-xs'
  return (
    <span className="relative inline-flex">
      <button type="button" disabled={disabled} onClick={onToggle}
        className={`${pad} rounded-full border disabled:opacity-50 ${cur ? cur.style : 'border-border text-muted-foreground hover:bg-muted'}`}>
        {cur ? `${cur.icon} ` : ''}{label}
      </button>
      {open && (
        // Portal-free absolute popup — the same shape the gap chips already use, so the two
        // surfaces look identical. z-20 clears the chip grid; w-max keeps it off the
        // container's wrap width.
        <span ref={keepInView} className="absolute z-20 top-full left-0 mt-1 flex flex-wrap items-center gap-1 p-1.5 rounded-md border border-border bg-popover shadow-lg w-max max-w-[16rem]">
          {prompt && <span className="text-xs text-muted-foreground w-full mb-0.5">{prompt}</span>}
          {options.map(o => (
            <button key={o.value} type="button" disabled={disabled}
              onClick={() => onPick(o.value === value ? null : o.value)}
              className={pill('none', { door: true, extra: `disabled:opacity-50 ${o.style} ${o.value === value ? 'ring-1 ring-offset-1 ring-current' : ''}` })}>
              {o.icon} {o.label}
            </button>
          ))}
          {/* Clearing must be reachable in ONE act from any state — the cycle's worst
              property was that "none" sat behind however many taps you were away from it. */}
          {value != null && (
            <button type="button" disabled={disabled} onClick={() => onPick(null)}
              className={pill('neutral', { door: true, extra: 'text-muted-foreground' })}>
              {t('desk_stance_clear')}
            </button>
          )}
        </span>
      )}
    </span>
  )
}

// The four listing_policy stances as StanceChip options — the capture vocabulary, shared by
// the property terms modal and any future recording surface so they can never drift apart.
// ⚠️ A FUNCTION, not a module constant: the labels come from t(), and a constant evaluated at
// import time would freeze them in whichever language was active when the module loaded —
// the header's language toggle would then move every string on screen except these.
export const stanceOptions = () => STANCE_CYCLE.filter(Boolean).map(st => ({
  value: st, icon: STANCE_ICON[st], style: STANCE_STYLE[st], label: t(`desk_stance_${st}`),
}))

// ─── "Would be better to know…" — deterministic gap suggestions under both modals ──
// NOT an AI call: the high-value fields are known up front (they're what matching runs on),
// so after a parse (or while typing) the modal just lists which of them are still empty, in
// value order, capped so it reads as a nudge, not a scold. Adapter never invents; this never
// nags about fields the message legitimately couldn't contain — it only asks forward.
function BetterToKnow({ items, max = 5 }) {
  const show = items.filter(Boolean).slice(0, max)
  if (!show.length) return null
  return (
    <div className="rounded-lg border border-amber-400/40 bg-amber-50 dark:bg-amber-900/15 px-3 py-2">
      <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 mb-1"><IconText>{t('desk_gap_heading')}</IconText></p>
      <ul className="text-xs text-amber-800 dark:text-amber-200/90 space-y-0.5">
        {show.map(s => <li key={s}>· {s}</li>)}
      </ul>
    </div>
  )
}
// Returns structured gaps ({ id, label }) so the completeness meter can offer inline
// fill for the ones that map to a single editable criteria field (see FILLABLE_GAP).
// Consumers that only want the text call .map(g => g.label).
function clientGaps(party, reqInfo, criteria) {
  const gaps = []
  if (!party.phone?.trim()) gaps.push({ id: 'phone', label: t('desk_gap_client_phone') })
  if (criteria.price_max == null) gaps.push({ id: 'budget', label: t('desk_gap_client_budget') })
  if (!(criteria.district_codes || []).length && !(criteria.district_must_codes || []).length && !criteria.search_polygon) gaps.push({ id: 'location', label: t('desk_gap_client_location') })
  if (criteria.rooms_min == null && criteria.rooms_max == null && criteria.bedrooms_min == null) gaps.push({ id: 'rooms', label: t('desk_gap_client_rooms') })
  if (criteria.area_sqm_min == null && criteria.area_sqm_max == null) gaps.push({ id: 'size', label: t('desk_gap_client_size') })
  if (!reqInfo.urgency) gaps.push({ id: 'urgency', label: t('desk_gap_client_urgency') })
  if (!(criteria.must_have_codes || []).length) gaps.push({ id: 'dealbreakers', label: t('desk_gap_client_dealbreakers') })
  return gaps
}
// B41 (2026-07-17): completeness meter for the match card — "is a weak match weak because
// of DISAGREEMENT or because of MISSING data?" The per-row coverage % (D20) already answers
// it for the OTHER side of each row; this meter answers it for the ANCHOR entity the modal
// is open on. Reuses the same gap definitions the 🧠 "would be better to know" panels use,
// so both surfaces always agree on what "filled" means. Hover lists exactly what's missing.
const CLIENT_COMPLETENESS_TOTAL = 7   // clientGaps checks: phone/budget/location/rooms/size/urgency/dealbreakers
// Client gaps that map to a SINGLE editable criteria field, so the meter can offer a
// one-tap inline fill (number in, one amend op out) instead of "open the whole modal".
// Everything else (location/dealbreakers = multi-selects, urgency/phone = non-criteria)
// stays a passive nudge line that points the agent at the full editor. Values write as
// an amend_requirement op — the same gateway the Edit form uses, so provenance is
// identical (agent-recorded client demand), nothing new is invented.
const FILLABLE_GAP = {
  budget: { unit: 'desk_gap_fill_unit_usd',   apply: (c, v) => ({ ...c, price_max: v }) },
  rooms:  { unit: 'desk_gap_fill_unit_rooms', apply: (c, v) => ({ ...c, rooms_min: v, rooms_max: v }) },
  size:   { unit: 'desk_gap_fill_unit_sqm',   apply: (c, v) => ({ ...c, area_sqm_min: v }) },
}
// One editable gap: label + number input + save. Builds a single-field amend by diffing
// the current criteria against a one-field patch, so it reuses diffCriteriaOps exactly.
function GapFillRow({ gap, fill }) {
  const spec = FILLABLE_GAP[gap.id]
  const [val, setVal] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(false)
  if (!spec) return <div className="text-xs text-muted-foreground truncate" title={`${gap.label} — ${t('desk_gap_fill_more_hint')}`}>· {gap.label}</div>
  async function save() {
    const num = Number(val)
    if (!val.trim() || !Number.isFinite(num) || num <= 0) return
    setBusy(true); setErr(false)
    try {
      const ops = diffCriteriaOps(fill.criteria, spec.apply(fill.criteria, num))
      if (!ops.length) { setBusy(false); return }
      const cmd = { type: 'amend_requirement', target_requirement_id: fill.requirementId, person_party_id: fill.personPartyId, ops, human_edited: true }
      const resp = await commitIntakeEnvelope({
        raw_text: `[inline gap fill via Desk] requirement ${fill.requirementId}`,
        channel: 'scheduler', envelope: { decision: 'noop', confidence: 1, commands: [cmd] }, anchors: null, intake_id: null,
        agent_id: fill.agentId,
      })
      if ((resp.results || []).some(r => !r.executed)) throw new Error('refused')
      // The label rides along so the rematch bar can say WHAT was recorded (redline Q2's
      // beat 1). Every existing caller ignores the argument, so this is additive.
      fill.onSaved?.(gap.label)   // parent re-fetches → this gap drops off, meter ticks up
    } catch { setErr(true); setBusy(false) }
  }
  return (
    <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
      <span className="text-xs text-muted-foreground flex-1 min-w-0 truncate" title={gap.label}>{gap.label}</span>
      <input type="number" inputMode="decimal" step="any" value={val} disabled={busy}
        onChange={e => setVal(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') save() }}
        placeholder={t(spec.unit)}
        className="w-16 shrink-0 text-xs px-1.5 py-0.5 rounded border border-border bg-background" />
      <button type="button" disabled={busy || !val.trim()} onClick={save} title={t('desk_gap_fill_save_title')}
        className={`shrink-0 text-xs px-1.5 py-0.5 rounded border ${err ? 'border-destructive/50 text-destructive' : 'border-primary/50 text-primary'} disabled:opacity-40`}>
        {busy ? '…' : err ? <Icon name="alert-triangle" /> : <Icon name="check" />}
      </button>
    </div>
  )
}
function CompletenessMeter({ label, missing, total, fill = null }) {
  // Accept both the structured client gaps ({ id, label }) and the property side's
  // plain-string list — normalize so the passive path is byte-for-byte as before.
  const gaps = (missing || []).map(g => typeof g === 'string' ? { id: null, label: g } : g)
  const [open, setOpen] = useState(false)
  const filled = Math.max(0, total - gaps.length)
  const pct = Math.round((filled / total) * 100)
  const color = pct >= 80 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-400' : 'bg-destructive'
  const canFill = !!fill && gaps.some(g => FILLABLE_GAP[g.id])
  const inner = (
    <>
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="w-14 h-1.5 rounded-full bg-muted overflow-hidden inline-block">
        <span className={`block h-full ${color}`} style={{ width: `${pct}%` }} />
      </span>
      <span className="text-xs text-muted-foreground tabular-nums">{filled}/{total}</span>
      {canFill && <span className="text-xs text-muted-foreground/70">{open ? '▴' : '▾'}</span>}
    </>
  )
  if (!canFill) {
    return (
      <span className="inline-flex items-center gap-1.5 shrink-0 cursor-help"
        title={gaps.length ? `${t('desk_completeness_missing')}: ${gaps.map(g => g.label).join(', ')}` : t('desk_completeness_full')}>
        {inner}
      </span>
    )
  }
  return (
    <div className="shrink-0">
      <button type="button" onClick={() => setOpen(o => !o)} title={t('desk_gap_fill_title')}
        className="inline-flex items-center gap-1.5 cursor-pointer">
        {inner}
      </button>
      {open && (
        <div className="mt-1.5 w-full max-w-[15rem] rounded-lg border border-border bg-muted/30 p-2 space-y-1.5">
          {gaps.map(g => <GapFillRow key={g.id || g.label} gap={g} fill={fill} />)}
        </div>
      )}
    </div>
  )
}
// Property-side completeness from a LISTING ROW (the lineage modal's shape — it has no
// workspace form to run propertyGaps over). Same spirit: only fields matching actually
// consumes. Contact phone deliberately excluded — the contact resolver loads separately
// and "no phone on the row" ≠ "no contact known" (reshares resolve via the chain).
// A field counts toward the record only when the kind's capability row says it CAN exist:
// 'M' (meaningless) never counts, 'U' (unknown until subtype — listing rows carry no subtype)
// never nags, and an unknown/absent kind falls back to counting everything, which is the
// pre-capability behaviour and fails toward asking rather than silently shrinking the meter.
const capCounts = v => v === undefined || v === 'R' || v === 'O'
function listingCompleteness(l) {
  const cap = identityCapability({ kind: l.kind })
  const missing = []
  let total = 0
  const check = (applies, absent, label) => { if (!applies) return; total++; if (absent) missing.push(label) }
  check(true, l.price == null, t('desk_gap_property_price'))
  check(capCounts(cap.rooms), l.rooms == null, t('desk_gap_property_rooms_fallback'))
  check(capCounts(cap.area), l.area_sqm == null, t('desk_gap_property_area_fallback'))
  // Plot size counts only where it is REQUIRED (land, house) — 'O' (commercial) would turn an
  // ordinary office record into a permanent nag. Land rows often carry the plot in area_sqm
  // (the portals' one area field), so where building area is meaningless either field satisfies.
  check(cap.landArea === 'R', (l.land_area_sqm ?? (cap.area === 'M' ? l.area_sqm : null)) == null,
    t('desk_gap_property_land_area'))
  check(capCounts(cap.floor), l.floor == null, t('desk_gap_property_floor'))
  check(capCounts(cap.condition), !l.condition, t('desk_gap_property_condition'))
  check(true, !l.district && !l.district_code, t('desk_gap_property_district'))
  check(true, l.lat == null || l.lng == null, t('desk_gap_property_pin'))
  return { missing, total }
}
function propertyGaps(prop, offer, facts, owner) {
  const cap = identityCapability({ kind: prop.kind })
  const gaps = []
  if (offer.price === '' || offer.price == null) gaps.push(t('desk_gap_property_price'))
  if (!owner.phone?.trim()) gaps.push(t('desk_gap_property_phone'))
  // The manual form has ONE area field (area_sqm) — for land it holds the plot, so the ask
  // survives but wears the land-area label. Rooms is asked only where rooms can exist.
  const roomsApply = capCounts(cap.rooms)
  const areaApply = capCounts(cap.area) || cap.landArea === 'R'
  const areaLabel = capCounts(cap.area) ? t('desk_gap_property_area_fallback') : t('desk_gap_property_land_area')
  if (roomsApply && areaApply && facts.rooms === '' && facts.area_sqm === '') gaps.push(t('desk_gap_property_rooms_area'))
  else if (areaApply && facts.area_sqm === '') gaps.push(areaLabel)
  else if (roomsApply && facts.rooms === '') gaps.push(t('desk_gap_property_rooms_fallback'))
  if (!prop.district_code) gaps.push(t('desk_gap_property_district'))
  if (prop.lat == null || prop.lng == null) gaps.push(t('desk_gap_property_pin'))
  if (capCounts(cap.floor) && facts.floor === '') gaps.push(t('desk_gap_property_floor'))
  if (!prop.cadastral_code?.trim()) gaps.push(t('desk_gap_property_cadastral'))
  return gaps
}

// ─── Match suggestions — the scorer's output, rendered under the modals ─────────
// Explanation-first (the score is the LAST thing on the row): each match shows its
// non-pass checks as chips (? unknown / ≈ near-miss / ✗ fail) and its actionable
// deltas ("→ price_above_budget $10,000") — the agent sees WHY before how-much.
// Server-side re-score per open (core_match.js) — no persistence, no staleness.
// CHECK_MARK / checkTier / bareCheckKey / logUnmappedMatchKey / ensureFeatureVocab /
// humanMatchKey — moved to ./matchBands.jsx (Q1 client-surface extraction). Imported
// above and re-exported below, so every existing consumer keeps working unchanged.
// Phase 1 classification → badge. REJECT never reaches the queue (server-filtered);
// a missing/unknown classification renders no badge rather than a wrong one.
const CLASS_BADGE = {
  READY:          { key: 'desk_match_class_ready',     cls: 'border-emerald-600/50 bg-emerald-600/15 text-emerald-600 dark:text-emerald-400' },
  NEGOTIATE:      { key: 'desk_match_class_negotiate', cls: 'border-amber-500/50 bg-amber-500/15 text-amber-600 dark:text-amber-400' },
  VERIFY:         { key: 'desk_match_class_verify',    cls: 'border-sky-500/50 bg-sky-500/15 text-sky-600 dark:text-sky-400' },
  DISCUSS_DEMAND: { key: 'desk_match_class_discuss',   cls: 'border-violet-500/50 bg-violet-500/15 text-violet-600 dark:text-violet-400' },
}
function ClassBadge({ classification }) {
  const b = CLASS_BADGE[classification]
  if (!b) return null
  return (
    <span title={t(`${b.key}_hint`)}
      className={`${COUNTER} shrink-0 border font-semibold cursor-help ${b.cls}`}>
      {t(b.key)}
    </span>
  )
}
// deltaLabel — moved to ./matchBands.jsx (Q1 client-surface extraction). Imported above.
// Density pass (2026-07-17): every delta rides on a check (scorer's `add` collects
// check.delta), so "✗ location" + "→ district doesn't match" were one fact as two chips.
// A visible check now absorbs its own delta — the delta's text wins (it carries the
// number: "over budget $37" beats "≈ price") but keeps the check's severity styling.
// `hideKeys` drops checks the caller hoisted to a section-level strip (property-side
// unknowns on the mirror direction) together with their deltas.
function MatchExplain({ checks = [], deltas = [], hideKeys = null }) {
  const interesting = checks.filter(k => k.status !== 'pass' && !(hideKeys && hideKeys.has(k.key)))
  if (!interesting.length && !deltas.length) return null
  // Signature match, not identity — checks[i].delta and deltas[j] are distinct objects
  // after the JSON round-trip even when they are the same delta.
  const dKey = d => `${d.kind}|${d.feature || d.subject || d.field || ''}|${d.amount_usd ?? ''}`
  // Check chips dedupe by rendered text too — three policy checks (pets/children/
  // occupants) have distinct keys but humanize to the identical "ask owner" line,
  // which read as the same chip printed three times. Fails win over unknowns when
  // labels collide, and a dropped duplicate still consumes its delta so the same
  // fact can't resurface as an amber chip below.
  // ⚠️ AN UNKNOWN CHIP NAMES ITS SUBJECT; ONLY A FAIL QUOTES ITS DELTA. Reported from
  // production 2026-08-13: one card carried three chips reading
  // `? ჰკითხე მესაკუთრეს — პირობა უცნობია`, `… — პირობები უცნობია`,
  // `… — როდის თავისუფლდება?` — three different subjects rendered as one repeated
  // sentence, which reads as a glitch and tells the agent nothing about WHICH condition to
  // raise. The dedupe below could not help: the strings differ by a word, so none is an
  // exact duplicate. A delta sentence is the right chip for a FAIL, where the number is the
  // point (`ფასი +$120`), and the wrong one for an unknown, where the SUBJECT is. This is
  // the chip grammar every other surface already uses — the matrix's columns, the phone's
  // chip line — nouns from the DB vocabulary.
  const chipText = k => (k.status !== 'unknown' && k.delta) ? deltaLabel(k.delta) : humanMatchKey(k.key)
  const checkLabel = k => `${CHECK_MARK[k.status]} ${chipText(k)}`
  const byLabel = new Map()
  for (const k of [...interesting].sort((a, b) => (a.status === 'fail' ? 0 : 1) - (b.status === 'fail' ? 0 : 1))) {
    if (!byLabel.has(checkLabel(k))) byLabel.set(checkLabel(k), k)
  }
  const shown = [...byLabel.values()].slice(0, 4)
  const consumed = new Set()
  for (const k of checks) {
    if (!k.delta) continue
    if (shown.includes(byLabel.get(checkLabel(k))) || (hideKeys && hideKeys.has(k.key))) consumed.add(dKey(k.delta))
  }
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {shown.map(k => (
        <span key={k.key} title={k.detail}
          className={pill('none', { extra: k.status === 'fail'
            ? 'border-destructive/50 text-destructive' : 'border-border text-muted-foreground' })}>
          {CHECK_MARK[k.status]} {chipText(k)}
        </span>
      ))}
      {/* Deduped by rendered text — several policy_gap deltas humanize to the same line,
          and two identical "ask owner" chips on one row read as a glitch. */}
      {[...new Set(deltas.filter(d => !consumed.has(dKey(d))).map(d => `→ ${deltaLabel(d)}`))]
        .slice(0, 2).map(label => (
        <span key={label} className={pill('warn')}>
          {label}
        </span>
      ))}
    </div>
  )
}
// The classifier's cheapest-next-action (D11) — one plain-language line under the chips:
// "what do I DO with this match", which is the whole point of Phase 1.
// The server's label is English (recommendation.mjs 'Present to client') — the same
// panel-speaks-English defect the matrix fixed on 2026-08-08, so the KIND is translated
// here and the server label survives only as the fallback for a kind this map predates.
const PRIMARY_ACTION_KEY = {
  present: 'msug_act_present', verify: 'msug_act_verify',
  negotiate: 'msug_act_negotiate', ask_client: 'msug_act_ask_client',
}
// Q5 (mobile): full-width, 36px tall, centred, with a subtle border — the redline's
// "primary action full-width 36px". `sm:` restores the plain truncating text line exactly:
// block display, zero border-width, zero radius, zero padding, `sm:truncate`. The mobile
// centring is done with flex (`items-center justify-center`) rather than `text-center`
// precisely so no text-align has to be re-asserted at ≥sm.
//
// ⚠️ IT STAYS A <p>, NEVER A <button>. There is no handler behind this line — it is the
// classifier's directive ("present to the client"), not a door. A bordered 36px block already
// flirts with looking pressable; giving it a button element or a hover state would be the
// 2026-08-06 lock-with-no-action defect, which is a control that names an action the viewer
// cannot take. The border is `border-border` (the card's own hairline), not `border-primary`,
// for the same reason.
function PrimaryAction({ action }) {
  const label = (action?.kind && PRIMARY_ACTION_KEY[action.kind]) ? t(PRIMARY_ACTION_KEY[action.kind]) : action?.label
  if (!label) return null
  return (
    <p title={label}
      className="text-xs text-primary/90 mt-1 flex items-center justify-center min-h-[36px] px-2
        border border-border rounded-lg
        sm:block sm:min-h-0 sm:px-0 sm:border-0 sm:rounded-none sm:truncate">
      ▸ {label}
    </p>
  )
}
// Part C1 (2026-07-14): THE contact control. Phone numbers never ship in API
// payloads and are never printed openly — this control fetches one on tap via
// POST /api/core/contact/reveal, which LOGS the reveal (who, whose contact,
// which property, when). The log is the audit trail AND the Part-C2 activity
// heartbeat. One component everywhere (user rule: same place, same look, so
// agents build the habit) — modals render it bottom-left; table rows inline.
// shareListingId/shareAddress (Phase 1b): when present, a revealed phone also offers a
// pre-addressed + pre-filled WhatsApp share for THIS listing — only the callers that point
// at a specific property (match row / offers) pass them, so generic reveals are unchanged.
// B111 (2026-07-22): the ONE TAP that turns a reveal into knowledge.
//
// ⚠️ THE WHOLE DESIGN CONSTRAINT: agents will not fill in forms. The attempt row
// already exists (the server seeds it as PENDING at reveal time), so this is one
// button press and nothing else — no modal, no required field, no blocking the
// number. If reporting ever costs more than a tap it stops happening, the table
// empties, and every downstream reader concludes "nobody called" — which is
// worse than having no table at all.
//
// Four primary outcomes cover the overwhelming majority of calls; the rest sit
// behind "…" so the common case stays a single press. `agreed` is deliberately
// in the overflow: it is rare, and it is the one that will eventually promote a
// lead into supply (B116), so it should be a deliberate reach, not a mis-tap.
// B111 THE NUDGE (owner ruling 2026-07-22: this matters more than wiring
// promotion — "human behavior follows reminders far more reliably than good
// intentions"). Without it the one-tap depends on habit, and `revealed` rows
// nobody resolved quietly become "everything we never learned".
// Contextual on purpose: a count plus the actual list, each row one tap from
// done. Renders nothing when there is nothing owed.
function OpenTouchNudge({ onOpen }) {
  const [data, setData] = useState(null)
  const [open, setOpen] = useState(false)
  const load = () => getOpenTouches().then(setData).catch(() => setData(null))
  useEffect(() => { load() }, [])
  if (!data?.open) return null
  return (
    <div className="mb-3 rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2">
      <button className="text-xs font-medium text-amber-700 dark:text-amber-400"
        onClick={() => setOpen(o => !o)}>
        <Icon name="alert-triangle" /> {tLog('desk_touch_nudge', { n: data.open })} {open ? '▲' : '▼'}
      </button>
      {open && (
        <div className="mt-2 flex flex-col gap-1">
          {/* Context (owner 2026-07-24: "user only sees a number, can't remember what
              property"). The row now leads with the property ADDRESS, then the contact
              (name/phone); clicking the property opens its modal so the reveal is never a
              context-free number waiting for an outcome. The outcome buttons stay to the right. */}
          {data.items.map(it => {
            const canOpen = onOpen && it.listing_id
            return (
            <div key={it.id} className="flex items-center gap-2 text-xs">
              <span className="text-muted-foreground shrink-0">{(it.revealed_at || '').slice(5, 16).replace('T', ' ')}</span>
              <span className="min-w-0 truncate">
                {it.address
                  ? <button type="button" disabled={!canOpen}
                      onClick={() => canOpen && onOpen(it.listing_id)}
                      className={`truncate ${canOpen ? 'text-foreground hover:text-primary hover:underline cursor-pointer' : 'text-foreground'}`}
                      title={it.address}>{it.address}</button>
                  : null}
                <span className="text-muted-foreground">{it.address && it.name ? ' · ' : ''}{it.name || (it.address ? '' : '—')}</span>
              </span>
              <span className="ml-auto shrink-0"><AttemptOutcome attemptId={it.id} onDone={load} /></span>
            </div>
          )})}
        </div>
      )}
    </div>
  )
}

const ATTEMPT_PRIMARY = ['no_answer', 'interested', 'refused', 'already_taken']
const ATTEMPT_MORE    = ['callback_requested', 'wrong_number', 'language_barrier', 'agreed']
function AttemptOutcome({ attemptId, onDone }) {
  const [done, setDone] = useState(null)
  const [res, setRes] = useState(null)      // B116: what the `agreed` door did
  const [busy, setBusy] = useState(null)
  const [more, setMore] = useState(false)
  const [err, setErr] = useState(null)
  // B116: tapping `agreed` advances an existing cooperation to `agreed`, which is
  // what promotes the lead into matchable supply. It can NEVER create one — a
  // cooperation carries commission, min price and exclusivity that a button
  // cannot know. So when there is none (or several), say so plainly instead of
  // letting the agent believe the lead moved.
  if (done) return (
    <span className="inline-flex items-center gap-1.5 flex-wrap">
      <span className="text-xs text-muted-foreground"><Icon name="check" /> {t('desk_attempt_' + done)}</span>
      {res?.advanced && <span className="text-xs text-emerald-600">{t('desk_attempt_promoted')}</span>}
      {res?.needs_cooperation && <span className="text-xs text-amber-600">{t('desk_attempt_needs_coop')}</span>}
      {res?.ambiguous?.length > 0 && <span className="text-xs text-amber-600">{t('desk_attempt_ambiguous')}</span>}
    </span>
  )
  async function tap(o, e) {
    e?.stopPropagation?.()
    setBusy(o); setErr(null)
    try { const r = await recordAttemptOutcome(attemptId, o); setRes(r); setDone(o); onDone?.() }
    catch (ex) { setErr(ex.message) } finally { setBusy(null) }
  }
  const btn = 'text-xs rounded border border-border px-1.5 py-px hover:bg-accent disabled:opacity-50'
  return (
    <span className="inline-flex items-center gap-1 flex-wrap" title={t('desk_attempt_hint')}>
      {(more ? [...ATTEMPT_PRIMARY, ...ATTEMPT_MORE] : ATTEMPT_PRIMARY).map(o => (
        <button key={o} className={btn} disabled={!!busy} onClick={e => tap(o, e)}>
          {busy === o ? '…' : t('desk_attempt_' + o)}
        </button>
      ))}
      {!more && <button className={btn} onClick={e => { e.stopPropagation(); setMore(true) }}>…</button>}
      {err && <span className="text-destructive text-xs" title={err}><Icon name="alert-triangle" /></span>}
    </span>
  )
}

function ContactReveal({ targetPartyId, propertyId, listingId, context, compact, ownerOfProperty, shareListingId, shareAddress }) {
  const [state, setState] = useState({ phase: 'idle' }) // idle | busy | shown | error
  if (!targetPartyId && !ownerOfProperty) return null
  async function show(e) {
    e?.stopPropagation?.()
    setState({ phase: 'busy' })
    try {
      const d = await revealContact({ targetPartyId, propertyId, listingId, context, ownerOfProperty })
      setState({ phase: 'shown', phone: d.phone, email: d.email, name: d.name, attemptId: d.attempt_id || null })
    } catch (err) { setState({ phase: 'error', msg: err.message }) }
  }
  if (state.phase === 'shown') return state.phone
    ? <span className="inline-flex items-center gap-1.5 flex-wrap">
        <a href={`tel:${state.phone}`} onClick={e => e.stopPropagation()} className={compact ? 'text-primary' : 'text-sm text-primary font-medium'}><Icon name="phone" /> {state.phone}</a>
        {shareListingId && <WhatsAppShare phone={state.phone} listingId={shareListingId} address={shareAddress} />}
        {/* B111: the server seeded a PENDING attempt for this reveal — one tap closes it.
            No attempt_id (colleague's number, or an admin with no agent party) → nothing renders. */}
        {state.attemptId && <AttemptOutcome attemptId={state.attemptId} />}
      </span>
    : state.email
    // email-only parties (C1 extension 2026-07-20): the reveal already returned it — a
    // "no contact" answer when there IS an email would send the agent hunting elsewhere
    ? <a href={`mailto:${state.email}`} onClick={e => e.stopPropagation()} className={compact ? 'text-primary' : 'text-sm text-primary font-medium'}><Icon name="mail" /> {state.email}</a>
    : <span className="text-muted-foreground">{t('desk_contact_no_phone')}</span>
  if (state.phase === 'error') return <span className="text-destructive text-xs" title={state.msg}><Icon name="alert-triangle" /></span>
  return (
    <button onClick={show} disabled={state.phase === 'busy'}
      className={compact
        ? 'text-xs text-primary hover:underline'
        : 'text-xs rounded border border-border px-2 py-1 text-primary hover:bg-accent'}>
      {state.phase === 'busy' ? '…' : t('desk_contact_reveal')}
    </button>
  )
}
// Part C3 (2026-07-14): the non-rep's escalation path. Shown under the rep
// banner: file "representatives not responding" with deal context → reps get
// an in-app notice → ADMIN decides (immediately, no timer). On grant, the
// owner ContactReveal appears here — a STANDING permission for THIS property
// only (narrow by user ruling: "only grant what has been asked, each time").
// Subject is a property (C3 owner-contact release, admin decides) OR a requirement
// (0158 client-contact release, the requirement's REPRESENTATIVE decides). One
// panel, one habit — the grant is always scoped to the one subject it names.
function AccessRequestPanel({ propertyId, listingId, requirementId }) {
  const [mine, setMine] = useState(undefined)      // undefined loading | null none | request row
  const [open, setOpen] = useState(false)
  const [context, setContext] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const subjectMatch = r => propertyId ? r.property_id === propertyId : r.requirement_id === requirementId
  // The subject a response belongs to. `mine` is reset on every subject change so the
  // previous subject's request never shows under the new one while it loads, and a
  // late response (fetch or file-refresh) for a subject no longer current is dropped.
  const subjectKey = `${propertyId || ''}|${requirementId || ''}`
  const currentSubject = useRef(subjectKey)
  currentSubject.current = subjectKey
  useEffect(() => {
    setMine(undefined); setOpen(false); setContext(''); setErr(null)
    if (!propertyId && !requirementId) return
    let gone = false
    getAccessRequests('mine')
      .then(d => { if (!gone) setMine((d.requests || []).find(subjectMatch) || null) })
      .catch(() => { if (!gone) setMine(null) })
    return () => { gone = true }
  }, [propertyId, requirementId])   // eslint-disable-line react-hooks/exhaustive-deps
  if ((!propertyId && !requirementId) || mine === undefined) return null
  async function file() {
    setBusy(true); setErr(null)
    const filedFor = subjectKey
    try {
      if (propertyId) await fileAccessRequest(propertyId, context)
      else await fileRequirementAccessRequest(requirementId, context)
      const d = await getAccessRequests('mine')
      if (currentSubject.current !== filedFor) return
      setMine((d.requests || []).find(subjectMatch) || null)
      setOpen(false)
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }
  if (mine?.status === 'granted') return (
    <div className="text-xs flex items-center gap-2 mt-1.5">
      <span className="text-green-600 dark:text-green-400"><Icon name="check-circle" /> {t('desk_access_granted')}</span>
      {/* Requirement grants: no inline reveal here — reopen the card and the footer's
          contact pill renders (the payload's presence flags honor the grant). */}
      {propertyId && <ContactReveal ownerOfProperty propertyId={propertyId} listingId={listingId} context="granted access request" />}
    </div>
  )
  if (mine?.status === 'pending') return (
    <p className="text-xs text-muted-foreground mt-1.5"><Icon name="hourglass" /> {t('desk_access_pending')}</p>
  )
  // ⚠️ A DENIAL IS NOT FINAL (owner ruling 2026-07-24: "let it be re-asked, deny
  // can be by mistake"). The server always allowed this — 0161's unique index and
  // the handler's pre-check both cover only pending|granted — but this branch used
  // to return a dead-end line, so the agent had no way back and the denial looked
  // permanent. The reason still shows; the ask simply falls through to the form
  // below. Same for a revoked grant: circumstances change, and re-asking is the
  // cheap, auditable move (every file writes an access.requested event, so a
  // pestering pattern is visible rather than prevented by a dead UI).
  const wasDeclined = mine?.status === 'rejected' || mine?.status === 'revoked'
  return (
    <div className="mt-1.5">
      {/* The previous decision stays visible — re-asking should be an informed
          act, not a reflex, and the note usually says what would change the answer. */}
      {wasDeclined && (
        <p className="text-xs text-muted-foreground mb-1">
          <Icon name={mine.status === 'revoked' ? 'lock' : 'ban'} />{' '}
          {t(mine.status === 'revoked' ? 'desk_access_revoked' : 'desk_access_rejected')}
          {mine.decision_note ? ` — ${mine.decision_note}` : ''}
        </p>
      )}
      {!open ? (
        <button className="text-xs text-muted-foreground hover:text-foreground underline decoration-dotted"
          onClick={() => setOpen(true)}><IconText>{t(wasDeclined ? 'desk_access_ask_again' : 'desk_access_request_link')}</IconText></button>
      ) : (
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          <input className="rounded border border-input px-2 py-1 bg-background w-64"
            placeholder={t('desk_access_context_ph')} value={context}
            onChange={e => setContext(e.target.value)} />
          <button className={btn('primary')} disabled={busy || context.trim().length < 5} onClick={file}>
            {busy ? '…' : t('desk_access_file')}
          </button>
          <button className={btn('ghost')} onClick={() => setOpen(false)}>✕</button>
          {err && <span className="text-destructive"><Icon name="alert-triangle" /> {err}</span>}
        </div>
      )}
    </div>
  )
}
// Part C3 (2026-07-14): the rep's in-app notice — "someone has a client for your
// property and reports you unreachable." In-app only for v1 (user decision).
// Polls every 2 min; the requester's contact is one tap away (agent contacts are
// always revealable), so answering is trivially easy. Admin resolves the request
// itself — this banner exists to wake the rep BEFORE the admin has to act.
function AccessRequestsNotice() {
  const [reqs, setReqs] = useState([])
  const [reloadKey, setReloadKey] = useState(0)
  const onDecided = () => setReloadKey(k => k + 1)
  useEffect(() => {
    let gone = false
    const load = () => getAccessRequests('against-me')
      .then(d => { if (!gone) setReqs(d.requests || []) })
      .catch(() => {}) // admin/unbridged viewers just see no banner
    load()
    const iv = setInterval(load, 120000)
    return () => { gone = true; clearInterval(iv) }
  }, [reloadKey])
  if (!reqs.length) return null
  return (
    <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 px-3 py-2 mb-3 space-y-1">
      {reqs.map(r => (
        <div key={r.id} className="text-sm flex flex-wrap items-center gap-2">
          <span><Icon name="bell" /> {r.requirement_id
            ? tLog('desk_access_notice_req', { name: r.requester.name || '?', client: r.requirement?.client_name || r.requirement?.title || '?' })
            : <>{tLog('desk_access_notice', { name: r.requester.name || '?' })}{districtLabel(r.district_path, getLanguage()) ? ` (${districtLabel(r.district_path, getLanguage())})` : ''}</>}</span>
          {r.context && <span className="text-xs text-muted-foreground"><Icon name="message-circle" /> {r.context}</span>}
          <ContactReveal compact targetPartyId={r.requester.party_id}
            propertyId={r.property_id} context="answering access request" />
          {/* 0158: requirement rows are DECIDED HERE, by the representative — never
              routed through admin (owner: "the owner of the client relationship
              controls access"). Property rows keep the C3 admin flow. */}
          {r.requirement_id && (
            <span className="flex items-center gap-1.5">
              <button className={pill('ok', { door: true })}
                onClick={() => decideAccessRequest(r.id, 'grant').then(onDecided).catch(() => {})}>
                <Icon name="check" /> {t('desk_access_grant_btn')}
              </button>
              <button className={pill('none', { door: true, extra: 'border-destructive/50 text-destructive hover:bg-destructive/10' })}
                onClick={() => decideAccessRequest(r.id, 'reject').then(onDecided).catch(() => {})}>
                <Icon name="x" /> {t('desk_access_deny_btn')}
              </button>
            </span>
          )}
        </div>
      ))}
    </div>
  )
}
// PriceHistoryBadge — moved to ./desk/primitives.jsx (plan §3 decomposition).
// ObservedPriceHistoryBadge — moved to ./desk/primitives.jsx (plan §3 decomposition).
// Who this offer belongs to, in one line — shared by the footer row and its title=.
function ownerLabel(o) {
  const own = o.ownership
  if (own?.is_own) return t('desk_match_your_listing')
  if (own?.represented_by?.agent_name) return own.represented_by.agent_name
  // "owner's own ad — no agent in between" is a CLAIM, so only make it when the server
  // actually resolved that label. The old `|| market_listing` fallback asserted it for
  // any unlabelled row too — harmless while this table only rendered on multi-agent
  // clusters, an invented fact once every card shows the footer.
  return own?.label === 'market_listing' ? t('desk_match_market_listing') : ''
}
// One physical property, one row per representing agent, cheapest-first (server-
// sorted — never re-sort here, that would silently re-introduce steering).
// Published (pool-consented) offers show price/commission; every row that HAS a
// reachable representative shows the contact control.
//
// Renders for a SINGLE offer too (owner rule 2026-07-20: "make that universal for
// every suggested card even if there is one contact"). It used to need ≥2, so a
// one-agent card answered "who do I call" with a mid-card OwnershipLine instead and
// a two-agent card answered it twice, in two different layouts. This footer is now
// the ONE place a match card names who to call, so OwnershipLine was dropped from
// the card — keeping both duplicated the same agent + reveal button on every
// single-offer card. The row deliberately keeps its score chip even when it repeats
// the header's (single offer ⇒ same number): the card face already showed the best
// offer's score above the cluster table before this change.
function MatchOffersTable({ offers, onOpenListing, address, myCooperation: mc }) {
  if (!offers?.length && !mc) return null
  // My own terms, from my own participant row — never a lock here (see the server note:
  // an agent's own numbers are not behind the blind-first gate). Empty is a real state:
  // the cooperation exists but nobody recorded numbers yet, and saying so beats a blank.
  const myTerms = !mc ? [] : [
    mc.commission_percent != null ? tLog('desk_match_coop_pct', { pct: mc.commission_percent }) : null,
    mc.min_price != null ? tLog('desk_match_coop_min', { v: Number(mc.min_price).toLocaleString(), cur: mc.min_price_currency || '' }).trim() : null,
    mc.exclusive ? t('desk_match_coop_exclusive') : null,
  ].filter(Boolean)
  return (
    <div className="mt-1.5 border-t border-border/60 pt-1.5 space-y-1">
      {/* MY cooperation, first and unmistakable (owner request 2026-07-20): when I also
          cooperate with this owner, the answer to "who do I call" is the OWNER, not the
          competing agents listed below — so it leads, in primary colour, with my own
          terms next to it. The reveal goes through the same logged C1 door; the server
          grants it because an active participant passes the representative check. */}
      {mc && (
        <div className="rounded-md border border-primary/40 bg-primary/5 px-1.5 py-1 space-y-0.5"
          onClick={e => e.stopPropagation()}>
          <p className="text-xs font-semibold text-primary" title={t('desk_match_my_coop_hint')}>
            <Icon name="handshake" size={11} /> {t('desk_match_my_coop')}
          </p>
          <div className="flex items-center justify-between gap-2 text-xs">
            <span className="text-muted-foreground truncate">
              {mc.contact_name || t('desk_match_my_coop_owner')}
            </span>
            <span className="flex flex-wrap justify-end items-center gap-1.5 shrink-0">
              <span className="text-muted-foreground">
                {myTerms.length ? myTerms.join(' · ') : t('desk_match_my_coop_no_terms')}
              </span>
              {/* targetPartyId, NOT ownerOfProperty: the latter makes the server re-resolve
                  the property's default contact through the fallback chain, which can land
                  on a different party than the cooperation contact named to its left — a
                  name and a number that don't belong to each other is the one failure mode
                  worth designing out. propertyId still rides along: it is what the gate
                  checks (active participant ⇒ representative ⇒ owner contact allowed). */}
              <ContactReveal compact targetPartyId={mc.contact_party_id} propertyId={mc.property_id}
                context="match card — my cooperation" shareAddress={address} />
            </span>
          </div>
        </div>
      )}
      {offers.map(o => (
        <div key={o.listing_id}
          className="flex items-center justify-between gap-2 text-xs cursor-pointer hover:text-foreground"
          onClick={(e) => { e.stopPropagation(); onOpenListing?.(o.listing_id) }}>
          {/* title=: the market-listing label is a full sentence in Georgian and this
              column truncates — an agent name never needs it, that one does. */}
          <span className="text-muted-foreground truncate" title={ownerLabel(o)}>
            {ownerLabel(o)}
          </span>
          {/* flex-wrap: a published row can now carry price + history + commission +
              contact + score at once (the contact is no longer gated on !published),
              which is more than a phone's row width. Same failure the card header hit. */}
          <span className="flex flex-wrap justify-end items-center gap-1.5 shrink-0">
            {o.published && o.price != null && <Money amount={o.price} currency={o.currency} />}
            {o.published && <PriceHistoryBadge count={o.price_change_count} history={o.price_history} />}
            {o.published && o.commission_percent != null && <span className="text-muted-foreground">{o.commission_percent}%</span>}
            {/* No longer gated on !published (2026-07-20): the mid-card OwnershipLine this
                footer replaced showed the reveal regardless of pool consent, so gating here
                would have LOST the contact on published single-offer cards. Publishing terms
                is about numbers, not about whether you may phone the agent — and the reveal
                itself is still server-enforced + logged, the button is not the disclosure. */}
            {!o.ownership?.is_own && o.ownership?.represented_by?.agent_party &&
              <ContactReveal compact targetPartyId={o.ownership.represented_by.agent_party}
                propertyId={o.property_id} listingId={o.listing_id} context="match offer"
                shareListingId={o.listing_id} shareAddress={address} />}
            <span className={`${COUNTER} bg-primary/10 text-primary font-semibold tabular-nums`}>
              {Math.round((o.score ?? 0) * 100)}%
            </span>
          </span>
        </div>
      ))}
    </div>
  )
}
// (OwnershipLine lived here — WHO to call about this listing, D14/D22/D26. Removed
// 2026-07-20 when MatchOffersTable became universal: it answered the same question,
// for the card's best offer only, in a second layout. Its rules survive in
// ownerLabel() above; the footer row carries the reveal control.)
// The representing agent's OWN deal context on their match row (owner model 2026-07-13):
// their cooperation terms (what another agent needs to know to deal — the split) and the
// owner conditions THEY recorded against THEIR version. Scorer verdicts live in the
// chips above; this line is the human-readable terms themselves.
function MatchTermsLine({ cooperation, restrictions }) {
  const coopBits = []
  if (cooperation?.commission_percent != null) coopBits.push(tLog('desk_match_coop_pct', { pct: cooperation.commission_percent }))
  // Real coops often carry only a floor price, no % — the chip must still show the deal
  // terms that DO exist (15 of the top 50 live rows were min_price-only, 2026-07-13).
  if (cooperation?.min_price != null) coopBits.push(tLog('desk_match_coop_min', { v: Number(cooperation.min_price).toLocaleString(), cur: cooperation.min_price_currency || '' }).trim())
  if (cooperation?.exclusive) coopBits.push(t('desk_match_coop_exclusive'))
  // Numbers gated (Track 2): the terms exist but are earned — a lock, not a blank, so the
  // agent knows to join/contribute rather than assume nothing was captured. The lock is
  // suppressed when the cooperation has no terms AT ALL (B72): there is nothing to earn,
  // and a lock there reads as "someone is hiding numbers from you" when none exist.
  if (cooperation && cooperation.can_see_numbers === false && cooperation.has_terms !== false
      && cooperation.commission_percent == null && cooperation.min_price == null)
    coopBits.push(coopLockLabel(cooperation))
  // Agent-recorded only — this line is "conditions THEY recorded" (comment above); scrape-
  // derived stances (source_kind='scrape', 2026-07-15) are the ad's own signal, already
  // consumed by the scorer, and would render mislabeled (or duplicate a subject) here.
  // Legacy payloads without source_kind → treated as agent (all rows were agent then).
  const stances = (restrictions?.policies || []).filter(p => (p.source_kind ?? 'agent') === 'agent').slice(0, 4)
  const nums = []
  const rt = restrictions?.terms
  if (rt?.deposit_months != null) nums.push(tLog('desk_terms_summary_deposit', { n: rt.deposit_months }))
  if (rt?.min_lease_months != null) nums.push(tLog('desk_terms_summary_minlease', { n: rt.min_lease_months }))
  if (rt?.max_occupants != null) nums.push(tLog('desk_terms_summary_maxocc', { n: rt.max_occupants }))
  if (rt?.cash_only === true) nums.push(t('desk_sale_cash_only_yes'))
  if (!coopBits.length && !stances.length && !nums.length) return null
  const subjectName = s => String(s).replace(/^pol_/, '').replace(/^household_/, '').replace(/_/g, ' ')
  return (
    <div className="flex flex-wrap items-center gap-1 mt-1">
      {coopBits.length > 0 && (
        <span className={pill('none', { extra: 'border-primary/40 text-primary font-medium' })}>
          <Icon name="handshake" /> {coopBits.join(' · ')}
        </span>
      )}
      {stances.map(p => (
        <span key={p.subject} className={pill('none', { extra: STANCE_STYLE[p.stance] || 'border-border text-muted-foreground' })}>
          {STANCE_ICON[p.stance] || '·'} {subjectName(p.subject)}
        </span>
      ))}
      {nums.length > 0 && <span className="text-xs text-muted-foreground">{nums.join(' · ')}</span>}
    </div>
  )
}
// Agent roll-up for the lineage modal's POSTED ON list (2026-07-16 redesign): an agent
// with 3 platform reposts used to take 3 near-identical rows — the question is "what is
// this agent's price and where did they post", so collapsed shows ONE line: name +
// platform pills + distinct price(s) with delta vs the reference + freshest age. Click
// expands the full per-platform rows (diffs, open/edit links) exactly as before.
function AgentPostingsGroup({ header, rows, renderSibling, refRow, priceDeltaToken }) {
  const [open, setOpen] = useState(false)
  const { display, rate } = useMoney()
  // Distinct prices — one agent posting 265/265/265 shows "265 USD" once; genuinely
  // different prices across platforms all show (that disagreement matters).
  // Deduped on the DISPLAYED text (2026-07-20), not on price+currency: once everything
  // renders in one currency, the same agent's "1000 GEL" and "380 USD" postings ARE the
  // same price and showing them as two chips read as a disagreement that isn't there.
  const prices = []
  for (const s of rows) {
    if (s.price == null) continue
    const m = formatMoney(s.price, s.currency, display, rate)
    if (!m) continue
    if (!prices.some(p => p.key === m.text)) prices.push({ key: m.text, row: s, approx: m.approx, observed: m.observed })
  }
  // One pill PER PLATFORM, not per posting (2026-07-21, owner-caught): an agent who
  // reposted the same property three times on MyHome used to get three identical MyHome
  // pills, which read as three different ads rather than one platform. Grouped the same
  // way the prices above are — collapse the repetition, keep the multiplicity visible.
  // Keyed on platformKey so myhome/myhome.ge/... normalize together; unmapped sources
  // fall back to their raw string rather than collapsing into one "?" bucket.
  const platforms = []
  for (const s of rows) {
    const key = platformKey(s.source) || s.source || '?'
    const hit = platforms.find(p => p.key === key)
    if (hit) hit.count++
    else platforms.push({ key, source: s.source, count: 1 })
  }
  const freshest = rows.map(s => s.listed_at).filter(Boolean).sort().pop()
  return (
    <div>
      <div className="flex items-center justify-between gap-2 text-sm py-1 cursor-pointer rounded hover:bg-accent/50"
        onClick={() => setOpen(o => !o)}>
        <span className="min-w-0 truncate">
          <span className="text-xs font-semibold text-muted-foreground">{header}</span>
          <span className="ml-1.5 inline-flex flex-wrap items-center gap-1 align-middle">
            {platforms.map(p => <PlatformPill key={p.key} source={p.source} count={p.count} />)}
          </span>
        </span>
        {/* wraps, never shrink-0 (mobile fix 2026-07-23): an agent with several distinct
            prices pushed this strip to ~470px on a 390px phone — the whole modal grew a
            horizontal scrollbar. justify-end keeps the desktop right-alignment. */}
        <span className="flex flex-wrap items-center justify-end gap-x-2 gap-y-0.5 min-w-0 text-xs text-muted-foreground">
          {prices.map(p => <span key={p.key}
            title={p.approx ? tLog('desk_price_observed_as', { observed: p.observed }) : undefined}>
            {p.key}{priceDeltaToken(p.row, refRow)}</span>)}
          {freshest && <span title={new Date(freshest).toLocaleString()}>{timeAgo(freshest)}</span>}
          <span className="text-muted-foreground/60">{open ? '▴' : '▾'}</span>
        </span>
      </div>
      {open && <div className="pl-2 border-l border-border/40">{rows.map(s => renderSibling(s, refRow))}</div>}
    </div>
  )
}

// Certainty bar (2026-07-16 match-card redesign): score% vs coverage% forced mental math
// ("90% on 40% verified" vs "78% on 95% verified") — one stacked bar makes trust visual:
// green = verified pass, amber = partial, gray = unknown (the risk), red = verified fail.
// Widths are check COUNTS, not the scorer's weights — this is a reading aid, the ranking
// itself stays the server's queue order.
function CertaintyBar({ checks = [] }) {
  if (!checks.length) return null
  const n = { pass: 0, partial: 0, unknown: 0, fail: 0 }
  for (const k of checks) n[k.status] = (n[k.status] || 0) + 1
  const total = checks.length
  const pct = c => Math.round((c / total) * 100)
  const seg = (count, cls) => count > 0 &&
    <span key={cls} className={cls} style={{ width: `${(count / total) * 100}%` }} />
  const tip = checks.map(k => `${CHECK_MARK[k.status] || '?'} ${humanMatchKey(k.key)}`).join('  ')
  return (
    <span className="block w-full max-w-[12rem]">
      <span className="flex h-1.5 w-full rounded-full overflow-hidden bg-muted mt-1" title={tip}>
        {seg(n.pass, 'bg-green-500')}
        {seg(n.partial, 'bg-amber-400')}
        {seg(n.unknown, 'bg-muted-foreground/25')}
        {seg(n.fail, 'bg-red-500')}
      </span>
      {/* §11: never color-only — the segments carry their numbers in words. People can't
          decode unlabeled multi-color bars under time pressure (designer round 4). */}
      <span className="flex flex-wrap gap-x-2 text-xs text-muted-foreground mt-0.5">
        {n.pass > 0 && <span><b className="text-green-600 dark:text-green-500">{pct(n.pass)}%</b> {t('desk_match_seg_pass')}</span>}
        {n.partial > 0 && <span><b className="text-amber-600 dark:text-amber-400">{pct(n.partial)}%</b> {t('desk_match_seg_partial')}</span>}
        {n.unknown > 0 && <span><b className="text-foreground/70">{pct(n.unknown)}%</b> {t('desk_match_seg_unknown')}</span>}
        {n.fail > 0 && <span><b className="text-red-500">{pct(n.fail)}%</b> {t('desk_match_seg_fail')}</span>}
      </span>
    </span>
  )
}

// WHO can answer an unknown (one regex, used by triage grouping, the hoisted owner strip,
// the client-gaps chip and the breakdown): the scorer's client-side unknowns all carry
// "…stated" / "requirement has no…" details — those are OUR profile-filling work; every
// other unknown is a missing property/owner fact.
// ⚠️ SUPERSEDED AS A DECISION PROCEDURE (2026-08-08). This regex answered "is this gap the
// client's side?" by pattern-matching the scorer's ENGLISH engineer prose, and it was right
// only for the four delta-less checks it was written against. Everything added since —
// notably match-score's `household_gap` ("the owner restricted X; the client profile does not
// say whether it applies", actions:['ask_client']) — matched neither alternative and was
// therefore routed to the OWNER, which put a question only the client can answer into the
// strip that dials the owner.
//
// `whoAnswers()` in src/lib/matchGaps.js reads `delta.actions`, the scorer's own declaration,
// and keeps this regex as the fallback for checks with no delta at all. The constant stays
// exported because it is part of this module's published surface; nothing in the Desk should
// test it directly any more.
const CLIENT_GAP_RE = /stated|requirement has no/

// Full check-by-check breakdown (2026-07-18 request: "see what parts match, what parts
// unknown, what don't"): the card's chip row stays selective — this panel, opened by
// tapping the certainty bar, lists EVERY check in five buckets, unknowns split by WHO
// can answer them (owner/property data vs the client's own unstated criteria).
function MatchBreakdown({ checks = [] }) {
  const buckets = { pass: [], partial: [], fail: [], askOwner: [], askClient: [] }
  for (const c of checks) {
    if (c.status === 'pass') buckets.pass.push(c)
    else if (c.status === 'partial') buckets.partial.push(c)
    else if (c.status === 'fail') buckets.fail.push(c)
    else (whoAnswers(c) === 'client' ? buckets.askClient : buckets.askOwner).push(c)
  }
  const row = (items, mark, cls, labelKey) => items.length > 0 && (
    <div key={labelKey} className="flex flex-wrap items-start gap-1">
      <span className="text-xs text-muted-foreground w-28 shrink-0 pt-0.5">{t(labelKey)} · {items.length}</span>
      {items.map(c => (
        <span key={c.key} title={c.detail} className={pill('none', { extra: cls })}>
          {mark} {c.delta ? deltaLabel(c.delta) : humanMatchKey(c.key)}
        </span>
      ))}
    </div>
  )
  return (
    <div className="mt-1.5 space-y-1 rounded-md border border-border/60 bg-muted/30 p-2" onClick={e => e.stopPropagation()}>
      {row(buckets.pass, '✓', 'border-green-600/40 text-green-600 dark:text-green-400', 'desk_match_bd_pass')}
      {row(buckets.partial, '≈', 'border-amber-500/40 text-amber-600 dark:text-amber-400', 'desk_match_bd_partial')}
      {row(buckets.fail, '✗', 'border-destructive/50 text-destructive', 'desk_match_bd_fail')}
      {row(buckets.askOwner, '?', 'border-sky-500/40 text-sky-600 dark:text-sky-400', 'desk_match_bd_ask_owner')}
      {row(buckets.askClient, '?', 'border-violet-500/40 text-violet-600 dark:text-violet-400', 'desk_match_bd_ask_client')}
    </div>
  )
}
// The certainty bar as the breakdown's toggle — proportions at a glance, one tap for
// the named facts behind each segment.
function CertaintyDetail({ checks = [] }) {
  const [open, setOpen] = useState(false)
  if (!checks.length) return null
  return (
    <div>
      <button type="button" className="flex items-center gap-1.5 w-full max-w-[13rem] text-left cursor-pointer"
        title={t('desk_match_bd_toggle_hint')}
        onClick={e => { e.stopPropagation(); setOpen(o => !o) }}>
        <CertaintyBar checks={checks} />
        <span className="text-xs text-muted-foreground/70 mt-1">{open ? '▴' : '▾'}</span>
      </button>
      {open && <MatchBreakdown checks={checks} />}
    </div>
  )
}
// The CLIENT side's own blanks, always visible (2026-07-18): criteria the client simply
// hasn't stated — fill-the-profile work for OUR side, deliberately styled apart from the
// sky "ask owner" gaps. The full list lives in the breakdown; this is the headline.
function ClientGapsChip({ gaps }) {
  if (!gaps?.length) return null
  const names = gaps.slice(0, 3).map(c => humanMatchKey(c.key)).join(', ')
  return (
    <div className="mt-1">
      <span className={pill('none', { extra: 'border-violet-500/40 text-violet-600 dark:text-violet-400' })}
        title={gaps.map(c => c.detail).join('\n')}>
        <Icon name="clipboard-list" /> {tLog('desk_match_client_gaps', { list: names })}{gaps.length > 3 ? ` +${gaps.length - 3}` : ''}
      </span>
    </div>
  )
}
// Owner-policy certainty gaps: the scorer's per-check `certainty` (v0.6.0) flags a stance
// that isn't a clean pass because the evidence is weak (estimated = one unconfirmed source)
// or contested (conflicting = independent sources disagree). Ships on every row in m.checks
// — no server change. These are the score's knowledge-quality drag, distinct from the
// client's own unstated blanks (ClientGapsChip) and the owner-data unknowns (ask-owner).
// status !== 'unknown' (BACKLOG B73): an UNKNOWN check is not weak OWNER evidence — it is
// a blank. The common case is an owner 'only' stance the client doesn't carry with an
// empty household profile, whose detail literally reads "client profile unrecorded". It
// was rendering through ReducedBecause as "single unconfirmed source", i.e. owner-
// verification framing ("what phone call to the owner firms this up?") for something only
// the CLIENT can answer. Wrong action, wrong person. Client blanks belong to ClientGapsChip.
const certaintyGaps = checks => (checks || [])
  .filter(c => (c.certainty === 'estimated' || c.certainty === 'conflicting')
            && c.status !== 'pass' && c.status !== 'unknown')

// "Reduced because …" (2026-07-19): the plain-language reasons the score is depressed by
// weak/contested owner facts — each names the field whose owner-verification would firm up
// the match ("what phone call improves this?"). Consolidated headline; its checks are hidden
// from the chip row (same move as ClientGapsChip) so the same fact isn't printed twice.
function ReducedBecause({ gaps = [] }) {
  if (!gaps.length) return null
  const subject = k => String(k).replace(/^policy:/, '').replace(/^pol_/, '').replace(/^household_/, '').replace(/_/g, ' ')
  const reason = c => t(c.certainty === 'conflicting' ? 'desk_match_reduced_conflict' : 'desk_match_reduced_single')
  const shown = gaps.slice(0, 3)
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1" title={gaps.map(c => c.detail).filter(Boolean).join('\n')}>
      <span className="text-xs text-amber-600 dark:text-amber-400 font-medium whitespace-nowrap"><Icon name="alert-triangle" /> {t('desk_match_reduced_because')}</span>
      {shown.map(c => (
        <span key={c.key} className={pill('warn')}>
          {subject(c.key)}: {reason(c)}
        </span>
      ))}
      {gaps.length > 3 ? <span className="text-xs text-muted-foreground">+{gaps.length - 3}</span> : null}
    </div>
  )
}
// Registration age with staleness coloring (2026-07-18 request: "see very clearly when
// it was registered and how long has been after that"). Muted under 2 weeks, amber to
// 45 days, red past that — an old unserved demand/listing reads very differently from
// yesterday's, and the score alone hides it. Thresholds are a first guess; tune freely.
// "Last confirmed beats registered" (same session): when a last-activity clock exists
// (requirement.updated_at — bumped by amends AND the one-tap confirm; listing.updated_at
// per B40), the VISIBLE age and its color follow the freshest activity, and the tooltip
// carries both dates.
function RegisteredAgo({ iso, activeIso, titleKey }) {
  const clock = activeIso && (!iso || activeIso > iso) ? activeIso : iso
  if (!clock) return null
  const days = (Date.now() - new Date(clock).getTime()) / 86400000
  const cls = days > 45 ? 'text-red-500 dark:text-red-400 font-semibold'
    : days > 14 ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'
  const tip = [
    iso ? `${t(titleKey)}: ${new Date(iso).toLocaleDateString()}` : null,
    activeIso && activeIso !== iso ? `${t('desk_match_last_activity_title')}: ${new Date(activeIso).toLocaleDateString()}` : null,
  ].filter(Boolean).join('\n')
  return (
    <span className={`text-xs shrink-0 ${cls}`} title={tip}>
      <Icon name="clock" /> {timeAgo(clock)}
    </span>
  )
}
// NEW since last look (0110): the other side registered after this viewer's previous
// open of the same list. Server-computed; absent on a first-ever open by design.
function NewBadge({ show }) {
  if (!show) return null
  return (
    <span className={`${COUNTER} shrink-0 bg-primary text-primary-foreground font-semibold`}>
      {t('desk_match_new_badge')}
    </span>
  )
}
// One-tap freshness confirmation (0110): shown only when the demand has gone quiet
// (>14 days without activity — same threshold the age chip turns amber at) and the
// client isn't another agent's (masked rows route through their representative).
// Optimistic: flips the age chip fresh immediately; the event lands in background.
function ConfirmActiveButton({ m, confirmedAt, onConfirmed }) {
  const [busy, setBusy] = useState(false)
  const clock = confirmedAt || m.last_activity_at || m.created_at
  if (!clock || m.ownership?.represented_by) return null
  if ((Date.now() - new Date(clock).getTime()) / 86400000 <= 14) return null
  async function go(e) {
    e.stopPropagation()
    setBusy(true)
    try {
      await confirmRequirementActive(m.requirement_id)
      onConfirmed(m.requirement_id)
    } catch (err) { console.error('confirm-active failed:', err.message) } finally { setBusy(false) }
  }
  return (
    <button type="button" disabled={busy} onClick={go} title={t('desk_match_confirm_active_hint')}
      className={pill('ok', { door: true })}>
      {busy ? '…' : t('desk_match_confirm_active')}
    </button>
  )
}
// Follow-through (2026-07-18): a 'proposed' row is an OPEN loop until a viewing
// happens — "proposed 12d ago · no viewing yet" (amber past 5 days) closes the gap
// between this panel and the Viewings pipeline. viewing_after is server-computed
// per PROPERTY (viewings carry no client link — honest granularity, see core_match).
function ProposedFollowUp({ verdict }) {
  if (verdict?.state !== 'proposed' || !verdict.updated_at) return null
  const days = (Date.now() - new Date(verdict.updated_at).getTime()) / 86400000
  const ago = tLog('desk_match_proposed_ago', { ago: timeAgo(verdict.updated_at) })
  if (verdict.viewing_after) return (
    <span className={pill('ok')}>
      <Icon name="send" /> {ago} · {t('desk_match_viewing_after')}
    </span>
  )
  return (
    <span className={pill(days > 5 ? 'warn' : 'neutral', { extra: days > 5 ? '' : 'text-muted-foreground' })}>
      <Icon name="send" /> {ago} · {t('desk_match_no_viewing_yet')}
    </span>
  )
}

// Triage grouping (2026-07-16, advice round 3): assign each surviving match to ONE group
// by its most FIXABLE blocker — verify-a-missing-fact beats stretch-the-budget beats
// accept-a-compromise. Computed here from the scorer's own checks (they already ship on
// every row — no server change): 'fail' statuses are the blockers; 'unknown' counts as
// verifiable ONLY when it's the PROPERTY's data that's missing ("floor unobserved",
// "no stance") — an unknown from the CLIENT not stating a criterion ("no budget stated",
// "requirement has no intent") is the fill-criteria prompt's job, not a phone call to an
// owner, so it never drags a row into the verify bucket.
// `move_in` replaces `handover` here (scorer v0.9.0): a date gap is the most negotiable
// thing on the list — it is literally a question of who waits. `handover` stays for any
// stored signal produced before 0220.
// `prepayment` joins on the same reasoning as `deposit` (scorer v0.10.0): "also bring the
// last month up front" is one of the first things an owner drops to close a tenant, and a
// new check that isn't listed here would quietly demote a workable match to a hard blocker.
// A check added to the scorer must be classified here in the same commit.
const NEGOTIABLE_KEYS = new Set(['price', 'deposit', 'prepayment', 'financing', 'pay_currency', 'handover', 'move_in'])
// Deltas that name a concession somebody has to make. A match carrying one is NOT "ready
// to offer" — nothing is wrong with it, but it cannot be handed over without a
// conversation first, and saying "ready" would send the agent in unprepared.
const CONCESSION_KINDS = new Set(['move_in_gap'])
function matchGroupKey(m) {
  const checks = m.checks || []
  const fails = checks.filter(c => c.status === 'fail')
  const verifiable = checks.some(c => c.status === 'unknown' && whoAnswers(c) === 'owner')
  const concession = (m.deltas || []).some(d => CONCESSION_KINDS.has(d.kind))
    || checks.some(c => c.delta && CONCESSION_KINDS.has(c.delta.kind))
  if (!fails.length) return concession ? 'stretch' : verifiable ? 'verify' : 'ready'
  if (fails.every(c => NEGOTIABLE_KEYS.has(c.key))) return 'stretch'
  return fails.length === 1 ? 'compromise' : 'longshot'
}
const GROUP_ORDER = ['pinned', 'ready', 'verify', 'stretch', 'compromise', 'longshot']
// §11 readiness tiers for the CLIENT direction (matching clients under a property): the
// decided order ready → confirm-info → concession; border-left is the tier's one color
// signal, NO opacity dimming (a concession match can still be today's work). Maps the
// existing triage groups: verify = "an answerable question blocks it"; every fail-carrying
// group is a concession conversation.
const READINESS_TIER = {
  ready:      { labelKey: 'desk_match_tier_ready',      text: 'text-green-600 dark:text-green-500', dot: 'bg-green-500',  border: 'border-l-green-500' },
  confirm:    { labelKey: 'desk_match_tier_confirm',    text: 'text-amber-600 dark:text-amber-400', dot: 'bg-amber-400',  border: 'border-l-amber-400' },
  concession: { labelKey: 'desk_match_tier_concession', text: 'text-red-500',                       dot: 'bg-red-500',    border: 'border-l-red-500' },
}
const readinessTierOf = m => {
  const g = matchGroupKey(m)
  return g === 'ready' ? 'ready' : g === 'verify' ? 'confirm' : 'concession'
}
const GROUP_LABEL_KEY = {
  pinned: 'desk_match_grp_pinned', ready: 'desk_match_grp_ready', verify: 'desk_match_grp_verify',
  stretch: 'desk_match_grp_stretch', compromise: 'desk_match_grp_compromise', longshot: 'desk_match_grp_longshot',
}
// ── The client direction's group table (matching-UI v2, the client surface) ─────────────
// Same unit as the matrix panel: classifyMatch's CLASSIFICATION, not the client-derived
// matchGroupKey — MatchMatrix's GROUPS and the stored match_edge rows both speak
// classification, so grouping by it is what lets the instant layer's counts BE the live
// layer's counts instead of a different number that jumps when the detail arrives.
// RULE 5 (redline): dot · name · count · clause; a group at zero keeps its heading, greyed,
// with the sentence saying what would fill it. Labels/clauses are all pre-existing reviewed
// rows (desk_match_class_*) — no Georgian invented here.
const CLASS_GROUPS = [
  { key: 'READY',          labelKey: 'desk_match_grp_ready',       meansKey: 'desk_match_class_ready_hint',     tone: 'text-emerald-600 dark:text-emerald-400', dot: 'bg-emerald-500', emptyKey: 'mv2_group_ready_empty' },
  { key: 'VERIFY',         labelKey: 'desk_match_class_verify',    meansKey: 'desk_match_class_verify_hint',    tone: 'text-sky-600 dark:text-sky-400',         dot: 'bg-sky-500',     emptyKey: 'msug_grp_none' },
  { key: 'NEGOTIATE',      labelKey: 'desk_match_class_negotiate', meansKey: 'desk_match_class_negotiate_hint', tone: 'text-amber-600 dark:text-amber-400',     dot: 'bg-amber-500',   emptyKey: 'msug_grp_none' },
  { key: 'DISCUSS_DEMAND', labelKey: 'desk_match_class_discuss',   meansKey: 'desk_match_class_discuss_hint',   tone: 'text-violet-600 dark:text-violet-400',   dot: 'bg-violet-500',  emptyKey: 'msug_grp_none' },
]
// classification → its label key, derived from the table above so the diff bar can NAME a
// group without a second copy of the mapping.
const GROUP_LABEL_OF = Object.fromEntries(CLASS_GROUPS.map(g => [g.key, g.labelKey]))
// ── The sort control (round 2 §5) ────────────────────────────────────────────────────────
// Four orders, one active, named in the panel header. Naming the SORT is a statement about
// the list; a score would be a claim about a property, and only that is forbidden — so this
// control is how a capped list stays honest without ever rendering a number on a card.
// ⚠️ `open` (ნაკლები კითხვა) is the queue's own logic — fewest unanswered questions first —
// and the designer's note is that it is the one an agent with an hour actually uses.
// The KEY is a server argument: each order changes which rows survive the cap.
// The four dimensions the breadth line can name, mapped to nouns that ALREADY EXIST as
// reviewed rows (the ladder's own `not_counted` labels) — the same word for the same idea
// on both screens, and no new Georgian for a sentence that is mostly the designer's.
const BREADTH_NOUN = {
  district: 'desk_crit_districts', budget: 'desk_crit_budget',
  rooms: 'desk_crit_rooms', area: 'msug_nc_area',
}
// ⚠️ Round 3 §3: `instant` is not decoration. Only `fit` can be served by the stored table,
// so the other three wait for the live pass — and the designer's ruling is that the control
// must SAY so rather than leaving the default's speed a mystery. `~1წმ` after the label.
const SORT_ORDERS = [
  { key: 'fit',   labelKey: 'msug_sort_fit',   instant: true },
  { key: 'fresh', labelKey: 'msug_sort_fresh' },
  { key: 'cheap', labelKey: 'msug_sort_cheap' },
  { key: 'open',  labelKey: 'msug_sort_open' },
]
const SORT_PREF_KEY = (agentId) => `desk.msug.sort${agentId ? `.${agentId}` : ''}`
// design §8 ("Five cards per group"): the rest sit behind an expand-in-place row — inside
// the group, pushing the list down, never a popover (the matrix overflow row's rule).
const GROUP_CAP = 5
// A row whose classification this table predates lands in VERIFY — "confirm first" is the
// only honest default for a bucket we cannot name.
const classGroupOf = m => {
  const c = String(m.classification || '').toUpperCase()
  return CLASS_GROUPS.some(g => g.key === c) ? c : 'VERIFY'
}
// The detail-arrives-late token (v2 answers Q3): a SHAPE, not a glyph — it cannot be
// misread as `–` or `?` because it is not a character. Twin of MatchMatrix's LoadingCell
// (not imported: MatchMatrix already imports from this file, and a back-import closes a
// module cycle for three lines of markup).
function LoadingBar() {
  return <span className="inline-block w-[22px] h-[8px] rounded-[2px] bg-muted animate-pulse align-middle"
    title={t('mv2_details_loading')} />
}
// Session latch: true once a stored read came back live-shaped (see the fetch effect).
let _storedArmMissing = false

// ── The rematch moment (redline Q2, 06-client-surface.md) ────────────────────────────────
// The one genuinely new component on this surface, and the redline is emphatic about WHY it
// is not a toast: the recount is the result of the agent's OWN work, it arrives in two beats
// seconds apart, and it changes a list they are in the middle of reading. So: ONE bar,
// directly under the panel header, sticky with it, that UPDATES IN PLACE across three beats
// and never stacks.
//
//   beat 1 · the answer is recorded   ✓ ჩაწერილია: <subject> · ითვლება…
//   beat 2 · the stored recount       +N ბარათი · M გადავიდა ჯგუფში „…"   + the loading shape
//   beat 3 · the live pass lands      same sentence, shape swapped for [ნახე ახლები] [✕]
//
// Two rules that look cosmetic and are not:
//   • "Say what moved, not that something happened" — two clauses at most, a count and the
//     most significant reclassification. Never "სია განახლდა".
//   • A recount that changed NOTHING still shows the bar (auto-dismissing), because silence
//     after a save reads as a failed save. That is information, not noise.
const DIFF_AUTO_DISMISS_MS = 6000   // the no-change bar only
const NEW_CARD_HOLD_MS = 4000       // arrival rule held, then it fades (400ms, in CSS)
// §2: three chips in ten seconds produce ONE bar. The debounce sits on the recount, never on
// the press — each row confirms itself the instant it is answered.
const IN_BAND_COALESCE_MS = 900
// Which destination group a move is worth NAMING when several moved at once. Ranked by what
// it means to an agent's next action, not by size: something arriving in "ready to offer" is
// the sentence worth reading even if more rows shuffled elsewhere.
const MOVE_SIGNIFICANCE = ['READY', 'NEGOTIATE', 'VERIFY', 'DISCUSS_DEMAND']
function diffOf(prev, rows, groupFn) {
  if (!prev) return null
  const added = []
  const movedTo = new Map()
  for (const m of rows) {
    const key = m.property_id
    if (!key) continue
    const was = prev.get(key)
    const now = groupFn(m)
    if (was === undefined) { added.push(key); continue }
    if (was !== now) movedTo.set(now, (movedTo.get(now) || 0) + 1)
  }
  let movedGroup = null
  for (const g of MOVE_SIGNIFICANCE) if (movedTo.has(g)) { movedGroup = g; break }
  if (!movedGroup && movedTo.size) movedGroup = [...movedTo.keys()][0]
  return { added, movedGroup, movedN: movedGroup ? movedTo.get(movedGroup) : 0 }
}
function DiffBar({ diff, onSeeNew, onDismiss }) {
  if (!diff) return null
  const counting = diff.phase === 'recording' || diff.phase === 'stored'
  const nothing = diff.phase === 'live' && !diff.added?.length && !diff.movedN
  // Two clauses, in the redline's order: what arrived, then what moved.
  // §2: when the bar fires for an in-band answer it must NAME THE CAUSE — the agent should
  // be able to connect it to the chip they just pressed — rather than saying "the list
  // updated", which is the sentence this whole component exists to avoid.
  const clauses = diff.kind === 'inband'
    ? [tLog('msug_diff_added', { n: diff.added.length }), t('msug_diff_same_answer')]
    : [
      diff.added?.length ? tLog('msug_diff_added', { n: diff.added.length }) : null,
      diff.movedN ? tLog('msug_diff_moved', { n: diff.movedN, group: t(GROUP_LABEL_OF[diff.movedGroup] || 'desk_match_grp_ready') }) : null,
    ].filter(Boolean)
  return (
    <div className={`sticky top-0 z-10 -mx-1 px-2 py-1.5 mb-1.5 rounded-lg border flex items-center gap-2 ${
      nothing ? 'bg-card border-border text-muted-foreground'
      : counting ? 'bg-amber-500/10 border-amber-500/40'
      : 'bg-emerald-500/10 border-emerald-500/40'}`}>
      {diff.phase === 'recording' ? (
        <span className="text-xs text-foreground truncate">
          <span className="text-emerald-500">✓</span> {diff.subject
            ? tLog('msug_diff_recorded_subject', { subject: diff.subject })
            : t('msug_diff_recorded')} · <span className="text-muted-foreground">{t('msug_diff_counting')}</span>
        </span>
      ) : nothing ? (
        <span className="text-xs truncate">{t('msug_diff_nochange')}</span>
      ) : (
        <span className="text-xs text-foreground truncate">{clauses.join(' · ')}</span>
      )}
      <span className="ml-auto flex items-center gap-1.5 shrink-0">
        {counting && diff.phase === 'stored' && <LoadingBar />}
        {diff.phase === 'live' && !nothing && !!diff.added?.length && (
          <button type="button" className="text-xs px-2 py-0.5 rounded-lg border border-border hover:bg-accent/40"
            onClick={onSeeNew}>{t('msug_diff_see_new')}</button>
        )}
        {diff.phase === 'live' && (
          <button type="button" className="text-xs text-muted-foreground hover:text-foreground px-1"
            title={t('desk_lineage_close_button')} onClick={onDismiss}>✕</button>
        )}
      </span>
    </div>
  )
}
// The row count BOTH arms ask for. It is the server's own cap (core_match.js clamps to 50)
// and the same number the mirror panel uses (MatchMatrix's ROW_LIMIT) — one constant so the
// instant layer and the live pass can never disagree about how many cards exist.
const STORED_ROW_LIMIT = 50
// Emoji→SVG (2026-07-18): group glyphs ride the components; i18n strings are words only.
const GROUP_ICON = {
  pinned: 'pin', ready: 'check', verify: 'help-circle', stretch: 'banknote',
  compromise: 'map-pin', longshot: 'trash', proposed: 'send', rejected: 'x',
}

// Per-match workflow actions (0102): the list must shrink and evolve as the agent works —
// shortlist pins to top, proposed/viewing/deal tag the row, rejected collapses it to one
// grey line at the bottom (with reason + undo). Optimistic: local state flips immediately,
// the POST lands in the background; on error the row snaps back.
// BtnLabel — moved to ./desk/primitives.jsx (plan §3 decomposition).

const VERDICT_RANK = { shortlist: 0, undefined: 1, null: 1, proposed: 2, deal: 2 }
// Emoji→SVG migration (2026-07-18, Icon.jsx rules): the glyph rides the component,
// the i18n string is words only — one icon per verdict meaning.
const VERDICT_ICON = { shortlist: 'pin', proposed: 'send', rejected: 'x' }
function VerdictActions({ state, onSet, busy, inline = false }) {
  // Mobile (owner-caught 2026-07-20): three worded chips per match row overflow a phone —
  // icon-only below sm:, the word joins on wider screens. title keeps the meaning hoverable,
  // and the ACTIVE chip always shows its word (state must stay readable, not just tinted).
  // Q5 hit targets: icon-only made these chips the SMALLEST doors on the phone card — the
  // pill is h-7 (28px) and the label that padded it out is hidden below sm:. `h-9 sm:h-7`
  // lifts them to 36px on a phone and hands the pill its own height back at ≥640px. It rides
  // in `extra` so the pill factory stays the one place geometry is defined.
  const chip = (s, labelKey, active) => (
    <button type="button" disabled={busy} title={t(labelKey)}
      className={pill('none', { door: true, extra: `h-9 sm:h-7 transition-colors ${active
        ? 'border-primary bg-primary/10 text-primary font-medium'
        : 'border-border text-muted-foreground hover:border-primary/40'}` })}
      onClick={e => { e.stopPropagation(); onSet(active ? 'clear' : s) }}>
      <Icon name={VERDICT_ICON[s]} size={11} /><span className={active ? '' : 'hidden sm:inline'}>{t(labelKey)}</span>
    </button>
  )
  return (
    <div className={`flex flex-wrap gap-1 ${inline ? 'shrink-0' : 'mt-1.5'}`} onClick={e => e.stopPropagation()}>
      {/* 'viewing' state removed entirely 2026-07-16 (0103 dropped it from the DB CHECK
          too) — viewings are tracked by the real Viewings pipeline, not a match-row toggle. */}
      {chip('shortlist', 'desk_match_act_shortlist', state === 'shortlist')}
      {chip('proposed', 'desk_match_act_proposed', state === 'proposed')}
      {chip('rejected', 'desk_match_act_reject', state === 'rejected')}
    </div>
  )
}

// §11 secondary actions — ONE list, TWO containers (owner rulings 2026-07-24). Desktop
// keeps the inline icon strip; a phone gets the dominant button plus a single ⋯ holding
// every secondary. Two rulings landed here together:
//  1. "do we really need action popup for one button?" — no. This replaced VerdictMoreMenu,
//     a ⋯ whose menu contained exactly ONE item (reject): two taps and a popover to reach a
//     single button. Reject is now just another entry — inline on desktop, in the sheet on
//     mobile. Demoting a rare/destructive action doesn't require hiding it in a menu; a
//     rejection is one tap to undo (rejectedRows keeps its undo line below).
//  2. "we can have one big button and action popup on mobile view" — below sm: the strip
//     collapses so the primary gets the width instead of five 32px squares competing with it.
// ⚠️ Adding an action = ONE entry in the caller's array. Neither container is edited, so the
// two views cannot drift into offering different actions — the failure the old split had.
function RowActions({ items }) {
  const [open, setOpen] = useState(false)
  const list = items.filter(Boolean)
  if (!list.length) return null
  const iconCls = a => `shrink-0 w-8 h-8 rounded-lg border inline-flex items-center justify-center disabled:opacity-50 ${
    a.active ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground'}`
  return (<>
    <span className="hidden sm:flex items-center gap-2">
      {list.map(a => (
        <button key={a.key} type="button" disabled={a.busy} title={a.title}
          className={iconCls(a)} onClick={a.onClick}>
          <Icon name={a.icon} size={13} />
        </button>
      ))}
    </span>
    {/* Mobile: one ⋯, everything inside. 44px trigger and 44px rows — this menu is
        portal-free (it sits inside the row's own stacking context, which the match list
        does not clip), so it can't reuse .desk-popover's rule; the sizes are explicit. */}
    <span className="relative sm:hidden shrink-0" onClick={e => e.stopPropagation()}>
      <button type="button" title={t('desk_match_more_actions')}
        className="w-11 h-11 rounded-lg border border-border text-muted-foreground hover:border-primary/40 inline-flex items-center justify-center"
        onClick={() => setOpen(o => !o)}>⋯</button>
      {open && (
        /* 🚨 `w-max` WITH NO CEILING RAN OFF THE LEFT EDGE OF THE PHONE (owner screenshot,
           2026-08-19: every row's label clipped mid-word — "…უგზავნეთ ეს ობიექტი კლიენტს").
           The trigger is pinned to the row's right edge and the panel hangs leftwards from
           it, so the panel's width IS its distance from the right edge of the screen. Its
           widest row is `share_tooltip` — "გაუგზავნეთ ეს ობიექტი კლიენტს", 29 Georgian
           characters — and `w-max` let that one string decide, unbounded, how far left the
           whole menu reached. On a 360px phone it reached past zero.
           This menu is deliberately portal-free (it lives in the row's own stacking context,
           which the match list does not clip), so unlike ShareMenu/PillOverflow it gets no
           `popoverPos` clamp for free — the ceiling has to be stated here.
           `100vw - 2rem` keeps a 16px margin on both edges at any viewport; `18rem` stops it
           sprawling on a tablet. Rows then TRUNCATE instead of overflowing, and `title`
           carries the full label for anyone who needs it. */
        <span className="absolute z-20 bottom-full right-0 mb-1 flex flex-col p-1 rounded-md border border-border bg-popover shadow-lg w-max max-w-[min(18rem,calc(100vw-2rem))]">
          {list.map(a => (
            <button key={a.key} type="button" disabled={a.busy} title={a.title}
              className={`text-left text-xs px-3 min-h-[44px] rounded hover:bg-accent/60 flex items-center gap-2 ${a.active ? 'text-primary' : 'text-muted-foreground'}`}
              onClick={() => { setOpen(false); a.onClick() }}>
              <Icon name={a.icon} size={13} />
              <span className="min-w-0 truncate">{a.title}</span>
            </button>
          ))}
        </span>
      )}
    </span>
  </>)
}

// Answerable boolean rental/sale TERM gaps (B42 completion, 2026-07-19): the listing_terms
// columns whose scorer UNKNOWN hoists as an "ask the owner" gap AND whose answer is a clean
// yes/no — TERM_CHIPS and NUM_TERM_CHIPS moved to ./matchBands.jsx (round-2 §3), with
// `ownerAnswerKind` and the capture controls that read them. Imported at the top of this
// file and re-exported at the bottom, so nothing that referenced them here had to change.
// Number-entry popover body for a numeric term gap — its own input state (can't use a hook
// per row inside the chip .map). Enter or the ✓ saves; empty/invalid is a no-op.
function NumTermInput({ chip, busy, onSave }) {
  const [val, setVal] = useState('')
  const commit = () => { const n = Number(val); if (Number.isFinite(n) && n >= chip.min && n <= chip.max) onSave(n) }
  return (
    <>
      <span className="text-xs text-muted-foreground w-full mb-0.5">{t(chip.promptKey)}</span>
      <span className="flex items-center gap-1">
        <input type="number" min={chip.min} max={chip.max} value={val} autoFocus disabled={busy}
          onChange={e => setVal(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commit() } }}
          className="w-14 px-1.5 py-0.5 rounded border border-border bg-background text-xs disabled:opacity-50" />
        <span className="text-xs text-muted-foreground">{t(chip.unitKey)}</span>
        <button type="button" disabled={busy || val === ''} onClick={commit}
          className={pill('none', { door: true, extra: 'disabled:opacity-50 bg-green-600/20 border-green-600 text-green-500' })}>
          <Icon name="check" />
        </button>
      </span>
    </>
  )
}
// ─── HoistedStrip — the shared "ask the owner once" block ────────────────────────────────
// Extracted 2026-08-08 (B568) so the v2 matrix panel can render the SAME strip instead of
// growing a second one. Nothing about its behaviour changed in the move: same amber
// presence, same tap-to-answer menus, same three write doors (setListingPolicy /
// setPropertyFeature / amendListingTerms), same rule about WHICH gaps are answerable.
//
// It owns its own openChip/busy state because that state is purely presentational; the
// CALLER owns the data (which gaps hoisted, and what to do after an answer lands), because
// only the caller knows how to re-score itself.
//
//   hoisted  Map<checkKey, { n, detail }> — the caller's own hoist rule decided membership.
//            The v1 panel hoists any verifiable unknown seen on ≥2 rows; the v2 panel
//            filters harder (owner-answerable subjects only, ranked by leverage). That
//            difference is a POLICY difference and stays with the caller — the strip only
//            knows how to render and answer what it is given.
// ownerAnswerKind — moved to ./matchBands.jsx (round-2 §3) beside the doors it decides
// between; imported at the top and re-exported unchanged at the bottom.
// `labelOf` lets a caller supply a better name for a check key than humanMatchKey's generic
// wrap — the v2 panel passes one that resolves `policy:pol_foreigners` to the subject's own
// Georgian noun instead of the sentence "მესაკუთრის წესი — foreigners — უცნობია", which
// leaks the raw code. Defaulted, so the v1 panel's chips are byte-identical until it passes
// one too.
function HoistedStrip({ hoisted, canRecord, listingId, propertyId, agentId, onAnswered, onError,
  labelOf = humanMatchKey }) {
  const [openChip, setOpenChip] = useState(null)
  const [chipBusy, setChipBusy] = useState(false)
  if (!hoisted || !hoisted.size) return null
  const run = async (fn) => {
    if (chipBusy) return
    setChipBusy(true)
    try { await fn(); setOpenChip(null); await onAnswered?.() }
    catch (e) { onError?.(e.message) }
    finally { setChipBusy(false) }
  }
  const recordPolicy  = (subject, stance) => listingId && run(() => setListingPolicy(listingId, subject, stance, null, agentId))
  const recordFeature = (code, present)   => run(() => setPropertyFeature(propertyId, code, present, agentId))
  // agentId is passed here for the same reason as its siblings (0221): a value answered
  // from a match panel must be able to say who said it.
  const recordTerm    = (field, value)    => listingId && run(() => amendListingTerms(listingId, [{ op: 'set', field, value }], agentId))
  return (
    <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-2.5 mb-2">
      <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 mb-1.5">
        <Icon name="phone" /> {tLog('desk_match_owner_gap_headline', { n: [...hoisted.values()].reduce((a, h) => a + h.n, 0) })}
      </p>
      <div className="flex flex-wrap items-center gap-1">
      {[...hoisted.entries()].sort((a, b) => b[1].n - a[1].n).map(([k, h]) => {
        // Answerable-in-place gaps (rep agent only): POLICY stances, AMENITY presence,
        // + boolean TERMS (pets/children/cash-only). Everything else (the condition/class
        // ladder, numeric terms like deposit) stays a read-only hint.
        const subject = k.startsWith('policy:') ? k.slice(7) : null
        const fm = /^(?:must|mustnot):(.+)$/.exec(k)
        // conditions/classes are a mutually-exclusive ladder, not a boolean amenity
        const amenity = fm && !/^(?:class_|cls_|frame_)/.test(fm[1]) ? fm[1] : null
        // bare key (no policy:/must: prefix) that names an answerable boolean term
        const term = !subject && !fm ? TERM_CHIPS[k] : null
        // …or a numeric term (occupants) → number-input popover (2026-07-24)
        const numTerm = !subject && !fm && !term ? NUM_TERM_CHIPS[k] : null
        const answerable = canRecord && ((subject && listingId) || amenity || (term && listingId) || (numTerm && listingId))
        const chipInner = <>? {labelOf(k)} <span className="opacity-60">×{h.n}</span></>
        // §11 color budget: amber = "confirm something".
        const chipCls = pill('warn')
        if (!answerable) return <span key={k} title={h.detail} className={chipCls}>{chipInner}</span>
        return (
          <span key={k} className="relative inline-flex">
            <button type="button" title={h.detail} onClick={() => setOpenChip(o => o === k ? null : k)}
              className={`${chipCls} cursor-pointer hover:bg-amber-500/20`}>{chipInner}</button>
            {openChip === k && (
              <span ref={keepInView} className="absolute z-20 top-full left-0 mt-1 flex flex-wrap items-center gap-1 p-1.5 rounded-md border border-border bg-popover shadow-lg w-max max-w-[16rem]">
                {subject ? (<>
                  <span className="text-xs text-muted-foreground w-full mb-0.5">{t('desk_policy_answer_prompt')}</span>
                  {STANCE_CYCLE.filter(Boolean).map(st => (
                    <button key={st} type="button" disabled={chipBusy} onClick={() => recordPolicy(subject, st)}
                      className={pill('none', { door: true, extra: `disabled:opacity-50 ${STANCE_STYLE[st]}` })}>
                      {STANCE_ICON[st]} {t(`desk_stance_${st}`)}
                    </button>
                  ))}
                </>) : term ? (<>
                  <span className="text-xs text-muted-foreground w-full mb-0.5">{t('desk_term_answer_prompt')}</span>
                  <button type="button" disabled={chipBusy} onClick={() => recordTerm(term.field, term.pos.v)}
                    className={pill('none', { door: true, extra: 'disabled:opacity-50 bg-green-600/20 border-green-600 text-green-500' })}>
                    <Icon name="check" /> {t(term.pos.label)}
                  </button>
                  <button type="button" disabled={chipBusy} onClick={() => recordTerm(term.field, term.neg.v)}
                    className={pill('none', { door: true, extra: 'disabled:opacity-50 bg-red-600/20 border-red-600 text-red-400' })}>
                    <Icon name="x" /> {t(term.neg.label)}
                  </button>
                </>) : numTerm ? (
                  <NumTermInput chip={numTerm} busy={chipBusy} onSave={n => recordTerm(numTerm.field, n)} />
                ) : (<>
                  <span className="text-xs text-muted-foreground w-full mb-0.5">{t('desk_feature_answer_prompt')}</span>
                  <button type="button" disabled={chipBusy} onClick={() => recordFeature(amenity, true)}
                    className={pill('none', { door: true, extra: 'disabled:opacity-50 bg-green-600/20 border-green-600 text-green-500' })}>
                    <Icon name="check" /> {t('desk_feature_has')}
                  </button>
                  <button type="button" disabled={chipBusy} onClick={() => recordFeature(amenity, false)}
                    className={pill('none', { door: true, extra: 'disabled:opacity-50 bg-red-600/20 border-red-600 text-red-400' })}>
                    <Icon name="x" /> {t('desk_feature_absent')}
                  </button>
                </>)}
              </span>
            )}
          </span>
        )
      })}
      </div>
    </div>
  )
}

// Session district dictionary (designer vocab rule 2026-07-23): raw codes like
// TBS_AVLABARI must never render. Module promise-cache, same pattern as the features
// vocab — every MatchSuggestions instance shares one fetch.
let _districtsCache = null
let _districtsPromise = null
function districtDisplay(code, list) {
  const d = (list || []).find(x => x.code === code)
  if (!d) return String(code).replace(/^[A-Z]{3}_/, '').replace(/_/g, ' ')
  return (getLanguage() === 'ka' ? (d.name_ka || d.name_en) : (d.name_en || d.name_ka)) || code
}

// B113 — "no good fit? search leads for this requirement → N leads".
// The count is fetched, never estimated: the button states a number the click then
// delivers, which is the same preview-equals-delivery rule the facet counts follow. A
// count that failed to load renders as no number at all rather than as 0 — "0 leads" and
// "we could not ask" are different sentences, and only one of them is a reason not to click.
function LeadEscapeHatch({ criteria, intent, propertyCategory = null, requirementId = null, clientName = null, onLeave, leadSince = undefined }) {
  // The requirement id rides along so the derived restriction lenses arrive at the Desk
  // KNOWING whose they are (the `restrictionsFrom` marker — deskSearchState.js). Without
  // it the hatch still works exactly as before; it just cannot tell the panel that the pet
  // lens is this client's answer rather than the agent's own choice, which is the
  // distinction 6b's summary card is made of.
  const committed = useMemo(() => requirementToCommitted(criteria, intent, requirementId, propertyCategory), [criteria, intent, requirementId, propertyCategory])
  const filters = useMemo(() => committedToAdvFilters(committed, {}), [committed])
  const [n, setN] = useState(undefined)   // undefined = loading, null = could not ask
  useEffect(() => {
    let live = true
    setN(undefined)
    // ⚠️ `since` = the window the Leads feed this click opens will use (the Desk's period, 30 days by default).
    // Counting all-time promised more leads than the destination shows (Desk search audit 2026-10-29).
    getAgentListings({ scope: 'leads', limit: 1, filters, since: leadSince })
      .then(r => { if (live) setN(r.total ?? null) })
      .catch(() => { if (live) setN(null) })
    return () => { live = false }
  }, [filters, leadSince])
  // Nothing stated → the hatch would open an unfiltered 18k scan, which is not a search.
  if (!Object.keys(committed.filters).length) return null
  return (
    <div className="mt-2 pt-2 border-t border-border/50 flex items-center gap-2 flex-wrap">
      <span className="text-xs text-muted-foreground">{t('desk_match_no_fit')}</span>
      <button type="button"
        className="text-xs px-2 py-1 rounded-lg border border-border hover:bg-accent/40 inline-flex items-center gap-1"
        // ⚠️ CLOSE FIRST, NAVIGATE ON THE NEXT TICK (measured live 2026-09-21: the button landed on the
        // Desk home for every client). Setting the hash and then closing the cards raced: closeCards
        // re-rendered IntakeTab, whose URL writer replaceState'd '#datadesk' for the now-empty stack
        // BEFORE the queued hashchange fired — so the restore read '#datadesk' and the search was lost.
        // The writer's effects flush with the click's render; the timeout runs after them.
        onClick={() => { const hash = serializeSearch(committed); onLeave?.(); setTimeout(() => { window.location.hash = hash }, 0) }}>
        <Icon name="search" size={11} />
        {n === undefined ? t('desk_match_search_leads')
          : n === null ? t('desk_match_search_leads')
          : tLog('desk_match_search_leads_n', { n })}
      </button>
    </div>
  )
}

// ── B569, the Desk half: the concession ladder on an empty result ───────────────────────
// Renders `constraint_diagnosis`, which the server ships ONLY when the queue came back
// empty (so a stale diagnosis can never sit beside a populated list). Copy obligations,
// from the backlog row: every count reads as a DIRECTION ("დაახლ."), never a promise an
// agent could quote to a client; when `excluded_after_count` is true the blocker is BY
// CONSTRUCTION not in the counted table, so the panel says that plainly instead of
// quoting a probe; and a probe that opens zero is KEPT — "this concession doesn't help"
// is a real answer, often the useful one.
const DIAG_PROBE_KEY = { budget: 'msug_diag_budget', district: 'msug_diag_district', rooms: 'msug_diag_rooms' }
const DIAG_NC_KEY = {
  polygon: 'msug_nc_polygon', area: 'msug_nc_area', must_have: 'msug_nc_must_have',
  must_not_have: 'msug_nc_must_not_have', condition: 'msug_nc_condition',
  floor: 'msug_nc_floor', complex: 'msug_nc_complex',
}
// ⚠️ ROUND 3 §1 — WHAT THE `ნახე` OPENS, AND WHY IT IS NOT A MATCH LIST. These rows passed
// the counted predicate with ONE condition relaxed and nothing has scored them: the server
// says so with `scored: false`, and this view has to say so too. So it deliberately does not
// reuse the match card — no verdicts, no chips, no primary action, nothing that would let it
// be read as "these fit". It is the evidence behind a number the agent is about to quote to
// a client on the phone, which is the whole reason the designer granted this one button.
function ProbeResults({ probe, onBack, onOpenListing }) {
  const label = t(DIAG_PROBE_KEY[probe.constraint] || 'msug_diag_title')
  return (
    <div className="rounded-lg border border-amber-500/40 bg-amber-500/[.06] p-3 space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-xs font-semibold text-foreground flex-1 min-w-0">
          {label} — {tLog('msug_diag_opens', { n: probe.count })}
        </p>
        <button type="button" className={`${btn('ghost')} text-xs min-h-[36px] sm:min-h-0`}
          onClick={onBack}>{t('msug_probe_back')}</button>
      </div>
      {/* The caveat is part of the answer, not a footnote — the same rule the ladder's own
          numbers carry. An agent quoting "six" to a client must know these are unscored. */}
      <p className="text-xs text-muted-foreground">{t('msug_probe_unscored')}</p>
      <div className="space-y-1">
        {(probe.listings || []).map(l => (
          <button key={l.listing_id} type="button"
            onClick={() => onOpenListing?.(l.listing_id)}
            className="w-full text-left rounded-lg border border-border px-2.5 py-1.5 hover:border-primary/40 min-h-[36px] sm:min-h-0">
            <span className="text-xs text-foreground truncate block">{l.address || districtDisplay(l.district_code) || '—'}</span>
            <span className="text-[11px] text-muted-foreground">
              {/* The same facts line the match card uses, same reviewed abbreviations —
                  one grammar for a property's face, wherever it appears. */}
              {[l.kind,
                l.rooms != null ? tLog('desk_entry_rooms_abbrev', { rooms: l.rooms }) : null,
                l.area_sqm != null ? tLog('desk_entry_area_abbrev', { area: l.area_sqm }) : null,
                l.price != null ? `${l.price} ${l.currency || ''}`.trim() : null,
                l.source].filter(Boolean).join(' · ')}
            </span>
          </button>
        ))}
      </div>
      {probe.truncated && (
        <p className="text-xs text-muted-foreground/70">
          {tLog('msug_cut_named', { n: (probe.listings || []).length, total: probe.count })}
        </p>
      )}
    </div>
  )
}
function ConstraintLadder({ diag, requirementId, onOpenListing }) {
  const [probe, setProbe] = useState(null)      // the loaded result, when the agent asked
  const [probeBusy, setProbeBusy] = useState(null)
  const [probeErr, setProbeErr] = useState(null)
  if (!diag) return <p className="text-xs text-muted-foreground">{t('desk_match_none_eligible')}</p>
  const probes = (diag.probes || []).filter(p => DIAG_PROBE_KEY[p.constraint])
  const notCounted = (diag.not_counted || []).map(c => DIAG_NC_KEY[c] ? t(DIAG_NC_KEY[c]) : c)
  async function openProbe(constraint) {
    setProbeBusy(constraint); setProbeErr(null)
    try { setProbe(await getRequirementProbe(requirementId, constraint)) }
    catch (e) { setProbeErr(e.message) }
    finally { setProbeBusy(null) }
  }
  if (probe) return <ProbeResults probe={probe} onBack={() => setProbe(null)} onOpenListing={onOpenListing} />
  return (
    <div className="rounded-lg border border-border p-3 space-y-1.5">
      <p className="text-xs font-semibold text-muted-foreground">{t('msug_diag_title')}</p>
      {diag.excluded_after_count ? (
        // The count admitted rows and the scorer rejected them all: the obstacle is NOT
        // in this table. Naming a counted constraint here would send the agent to
        // renegotiate a budget that was never the obstacle (the 3987b539 lesson).
        <p className="text-xs text-foreground">
          {tLog('msug_diag_downstream', { n: diag.counted_total })}
          {notCounted.length > 0 && <span className="text-muted-foreground"> {tLog('msug_diag_not_counted', { list: notCounted.join(', ') })}</span>}
        </p>
      ) : (<>
        {/* Q3 redline (06-client-surface): three row weights. The BEST probe is promoted —
            amber tint, 3px left rule, amber count — so the agent knows which concession to
            raise first. A zero row is RECESSED, never removed and never a button: it is
            what an agent quotes in a hard call, and it must survive every "clean up the
            empty states" instinct. Grid per redline (the ნახე action track is deferred —
            see B679: there is no server arm yet whose click delivers the probe's number,
            and a button that opens something OTHER than its count is a lie). */}
        {probes.map(p => {
          const opens = p.gain != null && p.gain > 0
          const best = opens && diag.binding === p.constraint
          const value = p.gain == null ? t('msug_diag_unmeasured')
            : opens ? tLog('msug_diag_opens', { n: p.gain }) : t('msug_diag_opens_nothing')
          return (
            <div key={p.constraint}
              // Round-2: KEEP THE SPACE the `ნახე` arm will occupy (the 76px third track),
              // so the row does not re-lay-out the day it lands — and until then the row is
              // not clickable AT ALL: a hover state with no destination is worse than a
              // plain row, and this row genuinely has nowhere to go yet.
              className={`grid grid-cols-[minmax(0,1fr)_106px_76px] gap-x-2 items-baseline px-2 py-[3px] rounded-sm ${
                best ? 'bg-amber-500/10 border-l-[3px] border-l-amber-500'
                : !opens ? 'bg-muted/40' : ''}`}>
              <span className={`text-xs truncate ${!opens ? 'text-muted-foreground/70' : best ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                {t(DIAG_PROBE_KEY[p.constraint])}
              </span>
              <span className={`text-xs tabular-nums whitespace-nowrap ${
                best ? 'text-amber-600 dark:text-amber-400 font-medium' : 'text-muted-foreground/70'}`}>{value}</span>
              {/* ⚠️ ROUND 3 §1 — ONE BUTTON, TOP ROW ONLY, and the asymmetry IS the design.
                  The best probe row is the one an agent acts on mid-call — "drop the pool
                  and I have six" — and a "six" they cannot show is a claim they must take on
                  faith with a client on the line. The other rows exist to prove the SHAPE of
                  the constraint; nobody clicks a row that says `1 ობიექტი`, so they keep the
                  reserved width and stay unclickable rather than growing a hover state with
                  no destination. */}
              {best && opens ? (
                <button type="button" disabled={probeBusy === p.constraint}
                  className={`${btn('outline')} text-xs min-h-[36px] sm:min-h-0 sm:py-0 border-amber-500/60 text-amber-600 dark:text-amber-400 disabled:opacity-50`}
                  onClick={() => openProbe(p.constraint)}>
                  {probeBusy === p.constraint ? <LoadingBar /> : t('msug_probe_see')}
                </button>
              ) : <span aria-hidden="true" />}
            </div>
          )
        })}
        {/* The escalation row: when no concession we control opens anything, the only honest
            action left is a conversation with the client — separated, full width, last. */}
        {diag.all_probes_zero && (
          <p className="text-xs text-foreground border-t border-border/60 pt-1.5 mt-1.5">
            {t('msug_diag_zero_all')} {t('msug_diag_call_client')}
          </p>
        )}
      </>)}
      <p className="text-[11px] text-muted-foreground/70">{t('msug_diag_foot')}</p>
    </div>
  )
}

// ── ClientCardBands — the expanded band card, and its FOURTH column (round-2 §3) ─────────
// The expansion moved out of the row's JSX into a component for one reason, and it is not
// tidiness: **the fetch follows the expansion.** `useAnswerDoor` costs four requests for the
// listing it is given, and a hook cannot be called inside a `.map`, so leaving it at panel
// level would have meant either one fetch per row (4 × 50 requests on open, for cards nobody
// opened) or no per-property context at all. A component that is MOUNTED only while the card
// is open makes "per expanded card, not for all 50" a structural fact rather than a promise.
//
// It owns the bands, the summary sentence and the controls; the panel keeps the list.
//
// ── Round-2 §3: `ვისი ჩასაწერია`, and EVERY row fills it ──────────────────────────────────
// *"Answerable where we represent — but never expressed as a control that is sometimes
// missing. An absent control reads as a bug."* So the fourth cell has three arms and no
// fourth: stance chips when the property is ours, the representative's NAME + ↗ when it is
// not, and the ↗ alone on an unclaimed listing (a name is not invented to fill the space).
// **Never "you may not answer this"** — no disabled control, no lock, no permission tooltip.
//
// ⚠️ CLIENT-SIDE (violet) ROWS ARE NOT CONDITIONAL. *"Client-side rows are always ours."*
// The client belongs to THIS panel — it is anchored on their demand — so who represents the
// property has no bearing on whether their own fact can be recorded. Gating the violet rows
// on ownership would refuse an agent the right to write down what their own client said.
//
// ⚠️ `onOpenDemand` is deliberately UNDEFINED for ClientFill here: this panel IS the demand
// side, so "open the demand" would open the card the agent is already standing in. Where
// `clientFillFor` has no honest one-tap widget the cell falls back to the client ROLE tag —
// information about whose record it is, which is what the column asks — never a blank.
function ClientCardBands({ m, bandAns, agentId, onOpenListing, openDemand, fillCtx, onAnswered }) {
  // The door, per EXPANDED card. `is_own` decides whether its owner arm renders controls,
  // but the READ is paid either way: the recorded stances are what a represented row's
  // bands show as already-known, and they are the same four requests.
  const { ans, runFill, fillBusy } = useAnswerDoor({
    listingId: m.listing_id, propertyId: m.property_id, agentId, onAnswered,
  })
  // Until the door lands, the labels keep the panel's session vocabulary (already resolved)
  // rather than blanking; once it lands `ans` is strictly richer — it adds this listing's own
  // rules, stances and terms, which is what lets a `rule:` row render the owner's sentence
  // and the ობიექტზე column read a recorded term.
  const ctx = ans.loaded ? ans : bandAns
  const b = bandsOf(m.checks, m.blockers)
  // The conditional წყარო column, decided per row: one decorated check is proof the
  // server ships provenance, so the whole column earns its header.
  const hasSource = (m.checks || []).some(c => c && c.source)
  const isOwn = m.ownership?.is_own === true
  const repName = m.ownership?.represented_by?.agent_name || null
  // The ghost action, at row scale. Same icon and same title as the card's own ↗ — one door,
  // named the same way in both places — because what it opens is the same property card.
  const openProperty = (
    <button type="button" title={t('msug_open_property')}
      className="shrink-0 w-6 h-6 rounded-md border border-border text-muted-foreground
        hover:border-primary/40 hover:text-foreground inline-flex items-center justify-center"
      onClick={e => { e.stopPropagation(); onOpenListing?.(m.listing_id) }}>
      <Icon name="external-link" size={11} />
    </button>
  )
  // ONE rule, applied to the blocking tiers and the quiet tier alike — the quiet rows are the
  // ones an agent fills in the same breath as the loud ones, and a column that answered them
  // differently would be a second grammar on one card.
  // ⚠️ ROUND 3 §4 — COLUMN 4 NAMES A PLACE: a widget, a representative, or a RECORD — never
  // a role. That is what killed the `კლიენტი | კლიენტი` repetition: column 2 is *who knows
  // the answer*, column 4 is *where it gets written*, and the duplication was the symptom of
  // this column drifting toward "who". The quiet tier's door is not a fallback for a missing
  // widget either — it is where that field ACTUALLY gets filled: a field like
  // `წინასწარი გადახდა` belongs on the client's record, not typed into a match row.
  const recordDoor = () => {
    // `openDemand` IS the panel's `onRefineCriteria` (see the call site) — the client
    // record's own editor. Naming the prop that lives in this component's scope, not the
    // panel's: reaching for `onRefineCriteria` here crashed the card, and because the crash
    // took the whole expansion with it the symptom looked like "the door renders empty".
    const go = openDemand
    // Dashed, like the round-1 quiet tier — it reads as optional at a glance, which is the
    // entire point of that tier. A different label from the blocking tier's `＋ შეავსე`
    // because it GOES somewhere rather than opening an input.
    const cls = 'text-xs inline-flex items-center gap-1 rounded-lg border border-dashed border-border/70 px-2 min-h-[36px] sm:min-h-0 sm:py-0.5'
    if (!go) {
      // No destination for this viewer. Still a PLACE, never a role — and never a button
      // that goes nowhere.
      return <span className={`${cls} text-muted-foreground/70`}>{t('msug_on_requirement')}</span>
    }
    return (
      <button type="button" className={`${cls} text-muted-foreground hover:text-foreground hover:border-primary/40`}
        onClick={e => { e.stopPropagation(); go() }}>
        ＋ {t('msug_on_requirement')} <Icon name="external-link" size={11} />
      </button>
    )
  }
  const whoseCell = (c, { quiet = false } = {}) => {
    // The loading bar covers EVERY arm, not only the chips. The alternative is an ↗ that
    // appears instantly beside a cell that is still deciding whether it is a control — the
    // card rewriting itself under the agent's eyes, which is the withdrawal the redline
    // forbids, one component further out.
    if (!ans.loaded) return <LoadingBar />
    if (whoAnswers(c) === 'client') {
      // §4: ONLY the quiet tier gets the door. A blocking field is worth typing straight
      // into the row; a record-keeping one is not, and sending it to the record is the
      // correct action rather than a consolation for having no widget.
      if (quiet) return recordDoor()
      const fill = fillCtx
        ? clientFillFor(c, { predicate: ans.rulesById?.get(ruleIdOf(c.key))?.predicate || null })
        : null
      // ⚠️ ClientFill's multi-select branch renders a `＋ შეავსე` DOOR rather than a widget,
      // and on this surface that door needs somewhere to go. `onOpenDemand` is genuinely
      // undefined here — IntakeTab does not pass it to the requirement direction, because
      // the demand this panel is anchored on is the modal the agent is already inside — so
      // the door is offered only when the panel was given the modal's OWN editor
      // (`onRefineCriteria`, the same control and the same write gate the over-broad line
      // uses). Without it the row falls back to the tag below: a button naming an action the
      // viewer has no destination for is the defect this panel has already shipped twice.
      const needsDoor = fill && (fill.editor || fill.kind === 'codes')
      if (fill && (!needsDoor || openDemand)) {
        return <ClientFill check={c} fill={fill} m={fillCtx} agentId={agentId}
          busy={fillBusy} onRun={runFill} onOpenDemand={openDemand} />
      }
      // No honest one-tap widget for this subject. The cell still ANSWERS ITS HEADER — the
      // record is the client's — which is the point of round-2 §3: absence-as-meaning is
      // what makes a UI feel broken, and this is never a lock or a refusal.
      return <RoleTag role="client" className="text-xs text-muted-foreground" />
    }
    if (isOwn) {
      return <OwnerFill checkKey={c.key} busy={fillBusy} canRecord listingId={m.listing_id}
        propertyId={m.property_id} agentId={agentId} onRun={runFill} ans={ans} />
    }
    // Not ours. The NAME is the answer to "whose record is this" and it is information the
    // agent wanted anyway — they are going to call that person. An open listing nobody has
    // claimed has no name to print, so it gets the door alone rather than an invented one.
    return (
      <span className="inline-flex items-center gap-1 min-w-0">
        {repName && <span className="text-xs text-muted-foreground truncate" title={repName}>{repName}</span>}
        {openProperty}
      </span>
    )
  }
  return (
    // Q5 (mobile): the bands INVERT — შესავსები first, the ✓/✗ bands after.
    // The phone card is read out loud mid-call and the thing to say is the open
    // question, not the eight things that already matched. Desktop keeps the
    // designed ✓ → ✗ → შესავსები order, so this is one `order-first sm:order-none`
    // on the one band that moves, never a duplicated JSX branch.
    // ⚠️ `flex flex-col` here is layout-neutral at ≥sm: the bands are full-width
    // blocks with equal mt-1.5 margins, so stretch-aligned flex items sit exactly
    // where the block boxes did.
    // stopPropagation: a tap INSIDE the open band card must not collapse it — agents
    // drag-select values from these rows, and now they also press controls in them.
    <div className="flex flex-col" onClick={e => e.stopPropagation()}>
      <Band labelKey="mv2_band_why" count={b.why.length}
        headers={['mv2_col_client_asks', 'mv2_col_on_property', hasSource ? 'mv2_col_source' : null]}
        tint="bg-emerald-500/[.06] text-emerald-600 dark:text-emerald-400">
        {b.why.map(c => <BandRow key={c.key} check={c} ans={ctx} m={m}
          lever={hasSource ? <span className="text-xs text-muted-foreground/70 whitespace-nowrap">{sourceOf(c, ctx)}</span> : null} />)}
      </Band>
      <Band labelKey="mv2_band_mismatch" count={b.mismatch.length}
        headers={['mv2_col_client_asks', 'mv2_col_on_property', null]}
        tint="bg-red-500/[.06] text-red-500 dark:text-red-400">
        {b.mismatch.map(c => <BandRow key={c.key} check={c} ans={ctx} m={m} />)}
      </Band>
      <Band labelKey="mv2_band_to_fill"
        count={b.ownerBlocking.length + b.clientBlocking.length}
        show={b.ownerBlocking.length + b.clientBlocking.length + b.quiet.length}
        // The third header is the round-2 column. It is NOT "action": the cell names whose
        // record the answer becomes, and on the rows we do not represent that is a person,
        // not a button.
        headers={['mv2_col_who_answers', 'mv2_col_known_today', 'msug_col_whose']}
        className="order-first sm:order-none"
        tint="bg-amber-500/[.06] text-amber-600 dark:text-amber-400">
        {b.ownerBlocking.map(c => (
          <GapRow key={c.key} check={c} ans={ctx} tone="text-amber-500"
            known={t('mv2_not_asked_yet')} leverage={0} control={whoseCell(c)} />
        ))}
        {/* `known` is what the demand already states (askedOf reads the check's
            own pair, then the digest) — empty is the honest answer when nobody
            recorded it. Same helper the matrix uses, from the same module. */}
        {b.clientBlocking.map(c => (
          <GapRow key={c.key} check={c} ans={ctx} tone="text-violet-500"
            known={knownFromDemand(c, m)} leverage={0} control={whoseCell(c)} />
        ))}
        {/* The quiet tier: nobody asked and nothing hangs on it. Visibly
            lighter, never counted in the numbers above. */}
        {!!b.quiet.length && (<>
          <p className="text-[10px] uppercase tracking-wide px-2.5 pt-1 text-muted-foreground/70">
            {t('mv2_blocks_nothing')} <span className="opacity-70">· {b.quiet.length}</span>
          </p>
          <div className="opacity-60">
            {b.quiet.map(c => (
              <QuietRow key={c.key} check={c} ans={ctx} control={whoseCell(c, { quiet: true })} />
            ))}
          </div>
        </>)}
      </Band>
      {(() => {
        const s = summarySentence(b)
        return s ? <p className="text-xs text-muted-foreground mt-1.5">{s}</p> : null
      })()}
    </div>
  )
}

function MatchSuggestions({ kind, id, onOpenListing, onOpenDemand, completeness = null, refreshKey = 0,
  listingId = null, agentId = null, canRecord = false, address = null,
  criteria = null, intent = null, propertyCategory = null, clientName = null, onLeaveToSearch = null, onRefineCriteria = null, leadSince = undefined }) {
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)
  const [districts, setDistricts] = useState(() => _districtsCache || [])
  useEffect(() => {
    if (_districtsCache) return
    _districtsPromise ??= getDistricts().then(d => { _districtsCache = d; return d }).catch(() => { _districtsPromise = null; return [] })
    let live = true
    _districtsPromise.then(d => { if (live && d.length) setDistricts(d) })
    return () => { live = false }
  }, [])
  // The feature vocabulary feeds humanMatchKey's names (DB rows first). Holding the resolved
  // MAP (rather than the old bare re-render tick) does the tick's job — chips drawn before it
  // resolves would otherwise keep their degraded fallback until some unrelated state change —
  // AND gives the band card's `ans` context below something to name its checks from.
  const [featVocab, setFeatVocab] = useState(null)
  useEffect(() => {
    let live = true
    ensureFeatureVocab().then(v => { if (live) setFeatVocab(v || null) })
    return () => { live = false }
  }, [])
  // The band card's answer context (Q1, client surface). MatchMatrix builds its `ans` from
  // the LISTING's own rows (restriction rules, stances, terms — useListingAnswers). None of
  // that exists on this surface: the anchor is the DEMAND and the rows are properties, so
  // every per-property map is deliberately EMPTY and the band readers degrade to blank
  // cells (their own rule: a column with no backing renders empty, never a guess). Only the
  // feature vocabulary carries over — it names the checks, and it is the same session-cached
  // map humanMatchKey reads (matchBands.jsx) — reshaped to the {name, question} rows
  // conditionLabel/cardCheckLabel expect, exactly as useListingAnswers builds them.
  const bandAns = useMemo(() => {
    const ka = getLanguage() === 'ka'
    return {
      vocab: new Map([...(featVocab || new Map())].map(([code, f]) => [code, {
        name: (ka ? (f.name_ka || f.name_en) : (f.name_en || f.name_ka)) || code,
        question: (ka ? (f.question_ka || f.question_en) : (f.question_en || f.question_ka)) || null,
      }])),
      rulesById: new Map(), policyBySubject: new Map(), restrictionVocab: null,
      terms: null, declined: new Set(), declinedRows: new Map(),
    }
  }, [featVocab])
  // In-place answer to a hoisted "ask the owner" gap (2026-07-19, B42): the representing
  // agent taps a chip and answers it without leaving the panel; then the panel re-scores
  // and the answered chip drops off. The three write doors and the answerability rule live
  // in HoistedStrip (extracted 2026-08-08, B568) — this component owns only the re-score,
  // because only it knows what its own list is.
  async function afterAnswer() {
    const d = await getPropertyMatchingClients(id)  // re-score: answered gap drops off
    setData(d)
  }
  // Meeting creation from a match row (MEETINGS_MODEL §3, entry point 3). The
  // ANCHOR is the side this panel is opened on — the row supplies the other side.
  // kind='requirement' → the panel is anchored on the client's demand, rows are
  // properties; kind='property' → anchored on the property, rows are clients.
  // The anchor's client party id is deliberately NOT resolved here: the server
  // derives it from requirement_id (one place to be right — see core_meeting.js).
  const [meetingSeed, setMeetingSeed] = useState(null)
  const [shareSeed, setShareSeed] = useState(null)   // client share link (§5) from a match row
  // The negotiation thread (§6/§11). A match row is one of only two surfaces that
  // holds BOTH sides — property AND client — which is exactly what a negotiation's
  // identity is, so this is where one can be started at all.
  const [negoSeed, setNegoSeed] = useState(null)
  const meetingAnchor = kind === 'requirement'
    ? { requirementId: id }
    : { propertyId: id, propertyLabel: address || null }
  // B78 soft gate: per-VIEW override, never stored — a different requirement resets it.
  const [forceBroad, setForceBroad] = useState(false)
  const [bindBusy, setBindBusy] = useState(false)
  useEffect(() => { setForceBroad(false) }, [kind, id])
  // §5's sort — and round 3 §3: it persists PER AGENT, not per panel open. An agent who
  // works `ნაკლები კითხვა` works it on every client; resetting to the default each time a
  // card opens would make them re-press it all day. Stored locally rather than on the party
  // row because it is a view preference, not a fact about anyone.
  const [sort, setSort] = useState(() => {
    try { return localStorage.getItem(SORT_PREF_KEY(agentId)) || 'fit' } catch { return 'fit' }
  })
  const pickSort = (k) => {
    setSort(k)
    try { localStorage.setItem(SORT_PREF_KEY(agentId), k) } catch { /* private mode */ }
  }
  // Two-layer render (matching-UI v2 §0): the stored match_edge read paints the structure
  // — group headers, counts, order, which properties — while the live scoring pass runs.
  // The live payload always wins; a stored read failing is never an error the agent sees
  // (the live path is the truth and carries its own error handling).
  const [stored, setStored] = useState(null)
  // ── The client card's own recount key (round-2 §3) ────────────────────────────────────
  // An answer recorded inside an expanded card must make the answered row LEAVE its band,
  // and the only thing that knows the row is gone is the server. So the panel's fetch keys
  // on this counter as well as on `refreshKey` — the parent's key still works exactly as it
  // did, this one is the panel's own.
  // ⚠️ IT MUST NOT TOUCH THE REMATCH BAR. `beginRecount` stays wired to the completeness
  // meter's fill and to nothing else: that fill edits the client's criteria and genuinely
  // re-classifies the list, which is what the bar reports. One stance on one property
  // removes one row from one band, and a bar announcing that would teach agents to ignore
  // the bar on the day it matters.
  const [answeredKey, setAnsweredKey] = useState(0)
  // ── the rematch moment's state (redline Q2) ──────────────────────────────────────────
  // `snapshot` is property_id → group as the agent last SAW it; it is taken the moment a
  // recount is triggered, never on arrival, because the whole question the bar answers is
  // "what changed since you looked away".
  const snapshotRef = useRef(null)
  // Card order as first rendered, so a later payload can never yank a card the agent is
  // mid-read. Cleared only when the ANCHOR changes — a recount must not reshuffle, and
  // neither may the stored→live handoff (the designer's own two-layer table puts ORDER in
  // the instant column, so the stored order IS the order; live fills detail, not sequence).
  const orderRef = useRef(null)
  const [diff, setDiff] = useState(null)
  const [newIds, setNewIds] = useState(null)     // amber: cards that moved as a CONSEQUENCE
  const [greenIds, setGreenIds] = useState(null) // green: the card the agent answered on
  const firstLoadRef = useRef(true)
  // The panel root. "ნახე ახლები" finds its target by DOM MARKER inside this subtree
  // (`[data-new-card]`) rather than by a conditional `ref` on the card: a ref that is
  // attached only while a predicate holds silently returns null when anything about the
  // predicate's timing shifts, and the button then does nothing — which is exactly what
  // happened here, and it looked identical to a broken scroll. A marker is also assertable
  // from a test: you can see it in the DOM.
  const panelRef = useRef(null)
  // A different client is a different question: drop everything.
  // ⚠️ `sort` belongs in here too, and for the opposite reason to the one it looks like.
  // The frozen order exists to stop the list reshuffling under a cursor mid-read — but a
  // sort change IS the agent asking for a different order, so keeping the old positions
  // would silently defeat the control they just pressed (the new rows would arrive and
  // then be re-sorted back into the previous sequence).
  useEffect(() => {
    snapshotRef.current = null; orderRef.current = null; firstLoadRef.current = true
    setDiff(null); setNewIds(null)
  }, [id, sort])
  useEffect(() => {
    if (!id) return
    let gone = false
    setData(null); setErr(null); setStored(null)
    // ⚠️ BOTH ARMS MUST ASK FOR THE SAME NUMBER OF ROWS, and this was measured wrong once:
    // the stored arm asked for the 50-row server cap while the live call kept the db
    // layer's default of 8, so an agent watched the groups paint ~50 cards and then SHRINK
    // to 8 when the live pass landed — the exact "the two layers must agree" rule the
    // design's §0 is built on, broken by a default nobody looked at. (Invisible in the
    // harness, where both fixtures were the same ten rows by construction.)
    // 50 is also what the mirror panel asks for (MatchMatrix's ROW_LIMIT) and what the
    // group counts need to mean anything: the server scores every candidate either way and
    // `limit` only slices what it serialises, so this buys payload, not scoring cost.
    const call = kind === 'requirement'
      ? getRequirementMatches(id, { force: forceBroad, limit: STORED_ROW_LIMIT, sort })
      : getPropertyMatchingClients(id)
    call.then(d => { if (!gone) setData(d) }).catch(e => { if (!gone) setErr(e.message) })
    // ⚠️ The instant layer is skipped for every order except the default. `match_edge` can
    // be ordered by score (which IS `fit`) and by nothing else the other three need, so a
    // stored paint under `იაფი` would show the best-fitting rows while the header said
    // "cheapest" — a wrong first paint, which is worse than a slower one. The server
    // refuses those too; this just avoids paying for the round trip.
    if (kind === 'requirement' && !forceBroad && sort === 'fit' && !_storedArmMissing)
      getRequirementMatchesStored(id, { limit: STORED_ROW_LIMIT }).then(d => {
        // A server that predates ?stored=1 ignores the param and runs the FULL scoring
        // pass — so this "instant" call would quietly double the cost of every open.
        // The response says which arm answered (stored:true); anything else means the
        // arm is not deployed, and we stop paying for the probe for the whole session.
        if (d?.stored) { if (!gone) setStored(d) }
        // A declared refusal ("this arm cannot serve that order") is NOT the same as an
        // old server that ignored the parameter, and latching on it would disable the
        // instant layer for the whole session over a sort we already skip.
        else if (!d?.unsupported_sort) _storedArmMissing = true
      }).catch(() => {})
    return () => { gone = true }
  }, [kind, id, refreshKey, forceBroad, sort, answeredKey])
  // Workflow verdicts (0102): local overrides layered over the server-enriched m.verdict —
  // optimistic flip, background POST, snap back on error. Keyed by the OTHER side's id
  // (property for demand→listings, requirement for the mirror), matching the table's key.
  const [verdicts, setVerdicts] = useState({})
  const [verdictBusy, setVerdictBusy] = useState(false)
  // 0110 one-tap confirm — optimistic per-requirement "fresh as of" overrides.
  const [confirmedAt, setConfirmedAt] = useState({})
  const vKey = m => kind === 'requirement' ? m.property_id : m.requirement_id
  const effVerdict = m => {
    const local = verdicts[vKey(m)]
    return local !== undefined ? local : (m.verdict?.state ?? null)
  }
  const effReason = m => {
    const local = verdicts[`${vKey(m)}#reason`]
    return local !== undefined ? local : (m.verdict?.reason ?? null)
  }
  async function applyVerdict(m, state) {
    let reason = null
    if (state === 'rejected') {
      reason = window.prompt(t('desk_match_reject_reason_prompt')) // eslint-disable-line no-alert
      if (reason === null) return // cancelled
    }
    const key = vKey(m)
    const prev = { state: effVerdict(m), reason: effReason(m) }
    setVerdicts(v => ({ ...v, [key]: state === 'clear' ? null : state, [`${key}#reason`]: reason }))
    setVerdictBusy(true)
    try {
      await setMatchVerdict({
        requirementId: kind === 'requirement' ? id : m.requirement_id,
        propertyId: kind === 'requirement' ? m.property_id : id,
        state, reason,
      })
    } catch {
      setVerdicts(v => ({ ...v, [key]: prev.state, [`${key}#reason`]: prev.reason }))
    } finally { setVerdictBusy(false) }
  }
  // Shortlist pins to top, untouched rows keep the server's queue order, already-actioned
  // (proposed/viewing/deal) sink below them, rejected collapse to grey lines at the bottom.
  // Mirror direction only (2026-07-16): within a verdict tier, URGENT clients come first —
  // "who do I call first" is the whole question when you're holding a property.
  const URGENCY_SORT = { immediate: 0, soon: 1 }
  const uRank = m => kind === 'property' ? (URGENCY_SORT[m.urgency] ?? 2) : 0
  // `data` (the live pass) always wins; `stored` only ever paints while it is in flight.
  const src = data || stored
  const detailPending = !data && !!stored
  // ⚠️ ROUND 3 §3 — A RE-SORT NEVER SHOWS A SPINNER. The two-layer promise was that the
  // STRUCTURE never waits, and it still doesn't: only three of the four orders have to wait
  // for the live pass, and while they do the panel keeps the same rows at the same count and
  // height, re-rendered as the district-titled skeleton the stored layer already defines.
  // The panel does not empty, collapse, or take an overlay — the agent's place on the page
  // survives the wait. (Materialising the other three orders was refused: three indexes and
  // a recount path to remove a one-second wait on an occasional control is the wrong trade.)
  const lastRowsRef = useRef(null)
  const liveRows = src?.matches || []
  useEffect(() => { if (liveRows.length) lastRowsRef.current = liveRows }, [liveRows])
  const rawRows = liveRows.length ? liveRows
    : (!src && lastRowsRef.current?.length
        ? lastRowsRef.current.map(m => ({ ...m, address: null, checks: [], evidence: undefined, stored: true }))
        : liveRows)
  // ORDER IS STICKY (redline Q2's last rule: "the list never reorders under the cursor
  // mid-read. Cards are inserted"). The first payload to arrive fixes the sequence; every
  // later one — the live pass replacing the stored paint, or a recount — keeps known cards
  // where they were and appends the arrivals, which is what makes an inserted card
  // noticeable rather than a reshuffle nobody can follow.
  const allRows = useMemo(() => {
    if (kind !== 'requirement' || !rawRows.length) return rawRows
    const known = orderRef.current
    if (!known) return rawRows
    return rawRows.map((m, i) => [m, i]).sort((a, b) => {
      const ka = known.get(a[0].property_id), kb = known.get(b[0].property_id)
      return (ka ?? Number.MAX_SAFE_INTEGER) - (kb ?? Number.MAX_SAFE_INTEGER) || (a[1] - b[1])
    }).map(([m]) => m)
  }, [rawRows, kind])
  // Beat 2 and 3. The snapshot was taken when the answer was recorded (beginRecount), so
  // each arriving payload is measured against WHAT THE AGENT LAST SAW, not against the
  // previous payload — otherwise the live pass would diff against the stored paint and
  // report "nothing changed" for a recount that changed plenty.
  const [newFading, setNewFading] = useState(false)
  useEffect(() => {
    if (kind !== 'requirement' || !allRows.length) return
    if (!orderRef.current) {
      orderRef.current = new Map(allRows.map((m, i) => [m.property_id, i]))
    } else {
      let next = orderRef.current.size
      for (const m of allRows) if (!orderRef.current.has(m.property_id)) orderRef.current.set(m.property_id, next++)
    }
    if (!snapshotRef.current) return
    const snap = snapshotRef.current
    const phase = data ? 'live' : 'stored'
    // ⚠️ ROUND 3 §2 — THE BAR'S SUBJECT IS THE LIST, NOT THE ANSWER. It was invented for the
    // agent who looks AWAY to fill a gap and comes back to a list that changed underneath
    // them. An in-band chip press is the opposite situation: their finger is on the row,
    // they are looking at it, and the thing they want confirmed is THAT ROW — which
    // confirms itself, in place, by leaving the შესავსები band as a green answered row with
    // its provenance. Firing a global bar for that would announce, at the top of the panel,
    // something happening under the agent's cursor — and once per chip press it becomes
    // wallpaper on the exact surface the bar exists to protect.
    // So the bar owns only the EXCEPTION: cards OTHER than the one under the cursor moved.
    const d = diffOf(snap.map, allRows, classGroupOf)
    if (snap.kind === 'inband') {
      const others = d.added.filter(pid => !snap.sources.has(pid))
      // Never `+0 ბარათი`: if only the answered card moved, there is no bar at all. The
      // round-1 `სია არ შეიცვალა` line does NOT apply here — that exists because a meter
      // fill has no local receipt and silence would read as a failed save; an in-band press
      // has a receipt an inch from the finger, so silence is correct and a bar would be noise.
      if (phase === 'live') {
        setDiff(others.length ? { phase: 'live', kind: 'inband', added: others, movedN: 0, movedGroup: null } : null)
        // Two arrival colours, or the agent cannot tell their own card from its consequences:
        // GREEN is the card they answered on, AMBER the cards that moved because of it.
        setGreenIds(snap.sources.size ? new Set(snap.sources) : null)
        setNewIds(others.length ? new Set(others) : null)
        snapshotRef.current = null
      }
      return
    }
    setDiff(prev => ({ ...(prev || {}), ...d, phase, subject: prev?.subject ?? null }))
    if (phase === 'live') {
      setNewIds(d.added.length ? new Set(d.added) : null)
      // ⚠️ NOTHING IS OPENED HERE, and the earlier build was wrong to. A count that
      // promised more than the screen showed was a real defect, but auto-opening the
      // agent's fold was the wrong repair (round-2 §4): it moves something they set. The
      // cap gives instead — see the `5 + arrivals` render below — and the only thing that
      // opens a shut group is the agent pressing `ნახე ახლები`.
      snapshotRef.current = null   // consumed: the live pass is the verdict
    }
  }, [allRows, data, kind])
  // The amber rule: held, then faded — never simply switched off, because a rule that
  // vanishes between glances leaves the agent unsure whether they imagined it.
  // ⚠️ ONLY THE PAINT EXPIRES. `newIds` deliberately survives the fade: it is what
  // "ნახე ახლები" aims at, and the bar carrying that button stays until the agent
  // dismisses it. Clearing the ids on the 4s timer (the first build did) left a live
  // button pointing at nothing — every press after four seconds silently did nothing,
  // which is the "a control must name an action the viewer HAS" rule broken by a timer.
  // The ids are released with the bar: on dismiss, on the next recount, on a new anchor.
  useEffect(() => {
    if (!newIds?.size && !greenIds?.size) return
    setNewFading(false)
    const hold = setTimeout(() => setNewFading(true), NEW_CARD_HOLD_MS)
    // The GREEN rule is a receipt, not a queue marker: nothing aims at it, so unlike the
    // amber ids it can be released on its own timer once it has settled.
    const green = setTimeout(() => setGreenIds(null), NEW_CARD_HOLD_MS + 500)
    return () => { clearTimeout(hold); clearTimeout(green) }
  }, [newIds, greenIds])
  // A recount that moved nothing still gets its bar, and takes itself away.
  useEffect(() => {
    if (diff?.phase !== 'live' || diff.added?.length || diff.movedN) return
    const t = setTimeout(() => { setDiff(null); setNewIds(null) }, DIFF_AUTO_DISMISS_MS)
    return () => clearTimeout(t)
  }, [diff])
  // Beat 1: fired at the exact moment an answer is recorded, which is the only moment that
  // knows WHAT was recorded. Snapshot first — the refetch is already on its way.
  function beginRecount(subject) {
    snapshotRef.current = { kind: 'meter', sources: new Set(), map: new Map(allRows.map(m => [m.property_id, classGroupOf(m)])) }
    setNewIds(null); setGreenIds(null)
    setDiff({ phase: 'recording', subject: subject || null, added: [], movedN: 0, movedGroup: null })
  }
  // §2's other half: an in-band answer. The row confirms itself immediately (the card
  // re-reads its own stances), and the PANEL recount is DEBOUNCED — "batch the presses,
  // debounce on the recount, not the press". Three chips in ten seconds must produce one
  // coalesced bar, never three, and the snapshot is taken at the FIRST press so the diff
  // still measures against what the agent last saw rather than against their own second
  // answer.
  const inBandRef = useRef({ timer: null })
  function onInBandAnswered(propertyId) {
    const st = inBandRef.current
    if (!snapshotRef.current || snapshotRef.current.kind !== 'inband') {
      snapshotRef.current = { kind: 'inband', sources: new Set(), map: new Map(allRows.map(m => [m.property_id, classGroupOf(m)])) }
    }
    if (propertyId) snapshotRef.current.sources.add(propertyId)
    if (st.timer) clearTimeout(st.timer)
    st.timer = setTimeout(() => { st.timer = null; bumpAnswered() }, IN_BAND_COALESCE_MS)
  }
  useEffect(() => () => { if (inBandRef.current.timer) clearTimeout(inBandRef.current.timer) }, [])
  // The meter's inline fill is this surface's one recorded-answer door, so the bar hangs off
  // it rather than off a timer or a poll. The parent's own callback still runs untouched.
  const completenessWired = (completeness?.fill && kind === 'requirement')
    ? { ...completeness, fill: { ...completeness.fill, onSaved: (label) => { beginRecount(label); completeness.fill.onSaved?.() } } }
    : completeness
  // ── The client card's write context (round-2 §3) ─────────────────────────────────────
  // `completeness.fill` is the ONE place this panel is told which requirement it is
  // anchored on and whose party that is — the meter's inline fill already writes through it,
  // so a second source for the same two ids would be a second thing to keep in sync.
  // ClientFill wants them shaped as a match row (`requirement_id` + `person.party_id`),
  // because on the matrix side its `m` genuinely IS one; here the panel's own anchor plays
  // that part. Null when the viewer may not write (no `fill` prop) — the violet rows then
  // say whose record it is instead of offering a control that would refuse.
  const fillCtx = (kind === 'requirement' && completeness?.fill)
    ? { requirement_id: completeness.fill.requirementId || id,
        person: { party_id: completeness.fill.personPartyId || null } }
    : null
  const fillAgentId = completeness?.fill?.agentId || agentId || null
  const bumpAnswered = () => setAnsweredKey(k => k + 1)
  // "ნახე ახლები" — the CONTAINER's own scroll offset. ⚠️ Never scrollIntoView (redline):
  // it scrolls every scrollable ancestor including the page behind the modal, which on a
  // card stack yanks the whole surface rather than the list.
  // §4: the press IS the consent. It opens whatever the agent shut that now holds an
  // arrival — one deliberate act by them, never a side effect of their phone call — and
  // only THEN scrolls. Reopening happens here and nowhere else.
  // ⚠️ The scroll cannot run in the same tick: a group that was shut has no card in the
  // DOM yet, so the marker query would come back null and the press would do nothing (the
  // exact dead-button shape this panel already shipped once). Both state writes batch into
  // one re-render, and the effect below runs after that commit — which is when the target
  // exists.
  const [scrollWanted, setScrollWanted] = useState(0)
  function seeNew() {
    if (newIds?.size) {
      const holding = new Set(allRows.filter(m => newIds.has(m.property_id)).map(classGroupOf))
      if (holding.size) setCollapsedGroups(s => new Set([...s].filter(k => !holding.has(k))))
    }
    setScrollWanted(n => n + 1)
  }
  useEffect(() => { if (scrollWanted) scrollToNew() }, [scrollWanted])
  function scrollToNew() {
    const el = panelRef.current?.querySelector('[data-new-card]')
    if (!el) return
    let sc = el.parentElement
    while (sc && sc !== document.body) {
      const oy = getComputedStyle(sc).overflowY
      if ((oy === 'auto' || oy === 'scroll') && sc.scrollHeight > sc.clientHeight) break
      sc = sc.parentElement
    }
    if (!sc || sc === document.body) {
      window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 80, behavior: 'smooth' })
      return
    }
    const top = el.getBoundingClientRect().top - sc.getBoundingClientRect().top + sc.scrollTop
    sc.scrollTo({ top: Math.max(0, top - 12), behavior: 'smooth' })
  }
  const activeRows = allRows.filter(m => effVerdict(m) !== 'rejected')
    .map((m, i) => [m, i]).sort((a, b) =>
      ((VERDICT_RANK[effVerdict(a[0])] ?? 1) - (VERDICT_RANK[effVerdict(b[0])] ?? 1)) ||
      (uRank(a[0]) - uRank(b[0])) || (a[1] - b[1]))
    .map(([m]) => m)
  const rejectedRows = allRows.filter(m => effVerdict(m) === 'rejected')
  // Density pass (2026-07-17, mirror direction): the PROPERTY's own gaps ("air_conditioner
  // unobserved", no owner stance) repeat as identical chips + "Confirm X before proceeding"
  // lines on nearly every client card — the remedy is ONE owner call, not N chips. A
  // verifiable unknown seen on ≥2 active rows hoists to a single strip under the header;
  // cards keep only their client-specific story (CertaintyBar still counts the gray).
  // Client-side unknowns ("no budget stated") are the client profile's job — never hoisted.
  const hoisted = new Map() // check key → { n, detail }
  if (kind === 'property') {
    for (const m of activeRows) for (const c of m.checks || []) {
      if (c.status !== 'unknown' || whoAnswers(c) === 'client') continue
      const h = hoisted.get(c.key) || { n: 0, detail: c.detail }
      h.n += 1
      hoisted.set(c.key, h)
    }
    for (const [k, h] of hoisted) if (h.n < 2) hoisted.delete(k)
  }
  const hoistKeys = hoisted.size ? new Set(hoisted.keys()) : null
  // Triage pipeline (advice round 3): demand→listings only — the mirror stays urgency-first
  // (holding a property, the question is WHO to call, not what's wrong with inventory).
  // One unified chip strip: workflow + blocker groups supersede each other — a row lives in
  // exactly one bucket (📌 pinned beats its blocker group; 📤 proposed/deal and ❌ rejected
  // leave the triage queue entirely and sit behind their toggles).
  const [groupFilter, setGroupFilter] = useState(null)   // 'proposed' | 'rejected' | null
  // Per-group expand-in-place (design §8: five cards per group). Resets with the anchor.
  const [expandedGroups, setExpandedGroups] = useState(() => new Set())
  // Round-2 §4 presupposes a group the agent can SHUT — the rule "never open a closed fold"
  // is only meaningful if closing one is possible. A shut group keeps its header (dot, name,
  // count, clause) and, when a recount lands inside it, an amber `+N ახალი`; it does not
  // open by itself, ever. `ნახე ახლები` is the one thing that opens it, because that is the
  // agent asking.
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set())
  // Q1 (client-surface redline): the CARD expands in place into the band card — the same
  // four-column grammar MatchMatrix's expanded row renders — keyed by property_id because
  // on this surface the rows ARE properties. Resets with the anchor, like the groups.
  const [expandedRows, setExpandedRows] = useState(() => new Set())
  useEffect(() => { setExpandedGroups(new Set()); setExpandedRows(new Set()); setGroupFilter(null) }, [id])
  // Classification groups (see CLASS_GROUPS): the same unit the matrix panel and the
  // stored match_edge rows use, so the instant layer's counts ARE the live layer's counts.
  // Verdicts still outrank classification: pinned on top, proposed/rejected behind chips.
  const groupOf = m => {
    const v = effVerdict(m)
    if (v === 'shortlist') return 'pinned'
    if (v === 'proposed' || v === 'deal') return 'proposed'
    return classGroupOf(m)
  }
  const byGroup = new Map()
  if (kind === 'requirement') for (const m of activeRows) {
    const g = groupOf(m)
    if (!byGroup.has(g)) byGroup.set(g, [])
    byGroup.get(g).push(m)
  }
  // RULE 5 (redline): every classification group renders, zero or not — a missing heading
  // reads as "no such thing" rather than "nothing here yet, and here is what would fill it".
  // Pinned is a verdict, not a classification: it appears only when the agent made one.
  // Q4 redline: one card, one place, with a GHOST COUNT. A pinned card leaves its
  // classification group; the source header admits it (`+N შერჩეულში`) so the count keeps
  // equalling what expanding delivers; the card itself keeps its classification as a tag
  // (pinned is a bookmark, not a fifth verdict).
  const pinnedRows = (byGroup.get('pinned') || []).map(m => ({ ...m, __pinnedTag: true }))
  const sections = kind === 'requirement' ? [
    ...(pinnedRows.length ? [{ key: 'pinned', labelKey: 'desk_match_grp_pinned', tone: 'text-foreground', dot: 'bg-primary', rows: pinnedRows }] : []),
    ...CLASS_GROUPS.map(g => ({ ...g, rows: byGroup.get(g.key) || [],
      ghost: pinnedRows.filter(m => classGroupOf(m) === g.key).length })),
  ] : []
  const proposedRows = byGroup.get('proposed') || []
  const visibleSections = groupFilter == null ? sections
    : groupFilter === 'proposed' ? (proposedRows.length ? [{ key: 'proposed', labelKey: 'desk_match_grp_proposed', tone: 'text-foreground', dot: 'bg-primary', rows: proposedRows }] : [])
    : []
  // design §10 ("READY is the sparse card, not the rich one"): a big READY under a
  // half-empty profile gets its permanent counterweight — filling the profile SHRINKS
  // this number, and that is the correct direction.
  const readyCounterweight = kind === 'requirement' && completeness?.total
    && (completeness.total - (completeness.missing?.length || 0)) / completeness.total <= 0.5
    && (byGroup.get('READY')?.length || 0) >= 20
  if (!id) return null
  // Score vs coverage (D20): "90% on 62% verified" reads as a hypothesis, "90% on 95%
  // verified" as a match. Since the 2026-07-16 CertaintyBar that distinction is VISUAL
  // (the gray segment is exactly the unverified share), so the coverage number moved
  // from a second header chip into this chip's tooltip (2026-07-17 density pass).
  // §11: readiness outranks score — a 98% on a blocked card must not tempt the agent away
  // from a ready 70%. The score is a within-tier TIEBREAKER: small muted text with its
  // verified share inline, never a colored chip.
  const scoreChip = (s, coverage) => (
    <span className="text-xs text-muted-foreground shrink-0 cursor-help whitespace-nowrap"
      title={t('desk_match_coverage_title')}>
      {Math.round((s ?? 0) * 100)}%{coverage != null && <> · {tLog('desk_match_verified_short', { pct: Math.round(coverage * 100) })}</>}
    </span>
  )
  return (
    <div className={card} ref={panelRef}>
      {/* Stack on mobile (2026-07-18): a long heading collapses into a narrow tall
          column and floats the short meter in empty vertical space when kept on one
          row — column below sm keeps the heading full-width and drops the meter under it. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 sm:gap-2 mb-2">
        <p className="text-xs font-semibold text-muted-foreground">
          <Icon name="target" className="mr-1" />
          {kind === 'requirement'
            ? t('desk_match_heading_properties')
            : t('desk_match_heading_clients')}
          {/* "checked 0, 0 may fit" is a LIE under too_broad — the gate returns zeros
              because it never ran the scorer, so printing it next to "N listings fit"
              contradicts the panel's own box (owner-caught 2026-07-22). */}
          {data && !data.too_broad && <span className="font-normal"> · {tLog('desk_match_eligible_of', { eligible: data.eligible_count ?? 0, considered: data.considered })}</span>}
          {/* The no-spinner rule (design §9): while the live pass runs, the stored layer
              says what it is — stored, and how old. Never a percentage, never a spinner. */}
          {detailPending && stored.computed_at && <span className="font-normal"> · {tLog('msug_updated', { ago: timeAgo(stored.computed_at) })}</span>}
        </p>
        {/* B41: the anchor side's own completeness, right where its matches are judged —
            a weak list under a half-empty profile means "go fill", not "no market". */}
        {completenessWired && <CompletenessMeter {...completenessWired} />}
      </div>
      {/* §5: SORT INSTEAD OF PAGINATE. Four orders, one active. "Show 50 more" is refused
          on purpose — at 640 average and 2,353 worst case, paging through is not a
          workflow; if the 50 are wrong then either the sort is wrong or the requirement is
          too broad, and both of those have their own control. Rendered only when there is
          something to reorder AND the cap actually bit: on a list of 12 the control would
          be four buttons that rearrange the same twelve rows. */}
      {kind === 'requirement' && data && !data.too_broad && (data.eligible_count ?? 0) > allRows.length && (
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1.5 -mx-1 px-1">
          <span className="text-xs text-muted-foreground shrink-0">{t('msug_sort_label')}</span>
          {SORT_ORDERS.map(o => (
            <button key={o.key} type="button"
              className={pill('none', { door: true, extra: `min-h-[36px] sm:min-h-0 transition-colors ${sort === o.key
                ? 'border-primary bg-primary/10 text-primary font-medium'
                : 'border-border text-muted-foreground hover:border-primary/40'}` })}
              onClick={() => pickSort(o.key)}>
              {t(o.labelKey)}
              {!o.instant && <span className="ml-1 opacity-60">{t('msug_sort_slow')}</span>}
            </button>
          ))}
        </div>
      )}
      {/* The rematch moment. Directly under the header, above the first group, sticky with
          it — one bar that updates in place, never a second one (redline Q2). */}
      <DiffBar diff={diff} onSeeNew={seeNew} onDismiss={() => { setDiff(null); setNewIds(null) }} />
      {err && <p className="text-xs text-destructive">{tLog('desk_match_lookup_failed', { err })}</p>}
      {!src && !err && <p className="text-xs text-muted-foreground"><IconText>{t('desk_match_scoring')}</IconText></p>}
      {data?.note && data.note !== 'no criteria row' && <p className="text-xs text-muted-foreground">{data.note}</p>}
      {/* B78 shallow-requirement soft gate: the server declined the heavy run and sent
          ask-hints instead (owner's order: rooms/area -> district -> budget). Nothing is
          locked: "show anyway" re-requests with force=1 (logged server-side). The
          make-binding tap is the HUMAN-confirmed promotion of pre-flip soft districts
          (legacy 28) -- one requirement at a time, never a bulk reinterpretation. */}
      {data?.too_broad && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-3 mb-2 space-y-2">
          {/* ⚠️ t() takes NO vars — only tLog() fills {placeholders}. Using t() here shipped
              a literal "{count}" to the agent's screen (owner-caught 2026-07-22). */}
          <p className="text-xs font-semibold text-foreground">{tLog('desk_match_too_broad_title', { count: data.candidate_count?.toLocaleString?.() ?? data.candidate_count })}</p>
          {/* "Ask the client (any one of these):" with an EMPTY list under it is the worst
              state in this panel — it names an action and offers nothing. The server no
              longer gates when it has no hints, but old/cached responses can still arrive
              hint-less, so the instruction line renders only when there IS a question. */}
          {!!(data.ask || []).length && <p className="text-xs text-muted-foreground">{t('desk_match_too_broad_sub')}</p>}
          <ul className="space-y-1">
            {(data.ask || []).map(a => {
              if (a === 'district_make_binding') {
                // no district dictionary in this component's scope -- prettify the code itself
                const names = (data.district_soft_codes || []).map(c => String(c).replace(/^TBS_/, '').replace(/_/g, ' ')).join(', ')
                const fill = completeness?.fill
                return (
                  <li key={a}>
                    {fill ? (
                      <button type="button" disabled={bindBusy}
                        className="text-xs px-2 py-1 rounded-lg border border-green-600/50 bg-green-600/10 text-green-500 hover:bg-green-600/20 disabled:opacity-50"
                        title={t('desk_match_make_binding_hint')}
                        onClick={async () => {
                          if (bindBusy) return
                          setBindBusy(true)
                          try {
                            const ops = [
                              { field: 'district_must_codes', op: 'replace', value: data.district_soft_codes || [] },
                              { field: 'district_codes', op: 'replace', value: [] },
                            ]
                            const cmd = { type: 'amend_requirement', target_requirement_id: id, person_party_id: fill.personPartyId, ops, human_edited: true }
                            const resp = await commitIntakeEnvelope({
                              raw_text: `[make districts binding via match panel] requirement ${id}`,
                              channel: 'scheduler', envelope: { decision: 'noop', confidence: 1, commands: [cmd] },
                              anchors: null, intake_id: null, agent_id: fill.agentId,
                            })
                            if ((resp.results || []).some(r => !r.executed)) throw new Error('refused')
                            fill.onSaved?.()
                          } catch (e) { setErr(String(e?.message || e)) } finally { setBindBusy(false) }
                        }}>
                        {bindBusy ? '…' : `✓ ${t('desk_match_make_binding', { names })}`}
                      </button>
                    ) : (
                      <span className="text-xs text-muted-foreground">· {t('desk_match_make_binding_hint')}</span>
                    )}
                  </li>
                )
              }
              const label = a === 'rooms_or_area' ? t('desk_match_ask_rooms_or_area')
                : a === 'district' ? t('desk_match_ask_district')
                : a === 'budget_max' ? t('desk_match_ask_budget') : a
              return <li key={a} className="text-xs text-foreground">· {label}</li>
            })}
          </ul>
          <button type="button" className="text-xs px-2 py-1 rounded-lg border border-border text-muted-foreground hover:bg-accent/40"
            onClick={() => setForceBroad(true)}>
            {t('desk_match_show_anyway')}
          </button>
        </div>
      )}
      {/* B569, the Desk side. Two different empty screens (design §10): a client we know
          nothing about gets an EMPTY FORM, not an empty market; a real empty result gets
          the constraint ladder — each row a DIRECTION (დაახლ.), never a promise, and the
          row that opens zero is kept because "money doesn't help here" is often the
          useful sentence. */}
      {data && !data.too_broad && !(data.matches || []).length && (
        data.note === 'no criteria row'
          ? (
            <div className="rounded-lg border border-dashed border-border p-3 space-y-1">
              <p className="text-sm font-medium">{t('msug_empty_form_title')}</p>
              <p className="text-xs text-muted-foreground">{t('msug_empty_form_body')}</p>
            </div>
          )
          : <ConstraintLadder diag={data.constraint_diagnosis} requirementId={id} onOpenListing={onOpenListing} />
      )}
      {/* Hoisted property gaps (density pass 2026-07-17): the shared "ask the owner" list,
          shown ONCE with how many matches each answer unblocks — cards below omit these.
          §11: the highest-leverage element on this panel — one owner call unblocks N
          matches — so it gets real presence (amber = confirm-something family) instead of
          reading as noise. Cards below never repeat these questions (they show one muted
          "blocked by…" line instead). The strip itself moved out of this component
          (2026-08-08, B568) so the v2 matrix panel renders the same one. */}
      <HoistedStrip hoisted={hoistKeys ? hoisted : null} canRecord={canRecord}
        listingId={listingId} propertyId={id} agentId={agentId}
        onAnswered={afterAnswer} onError={setErr} />
      {/* Done-lists chips only (advice round 3's 📤/❌ survive; the triage chips are gone —
          every classification group now renders its heading in place, zero or not, so a
          filter chip per group would just duplicate the headings one line above them). */}
      {kind === 'requirement' && (proposedRows.length > 0 || rejectedRows.length > 0) && (() => {
        const gChip = (key, label, count) => (
          <button key={key} type="button"
            className={pill('none', { door: true, extra: `transition-colors ${groupFilter === key
              ? 'border-primary bg-primary/10 text-primary font-medium'
              : 'border-border text-muted-foreground hover:border-primary/40'}` })}
            onClick={() => setGroupFilter(f => f === key ? null : key)}>
            <Icon name={GROUP_ICON[key]} size={11} />{label} · {count}
          </button>
        )
        return (
          <div className="flex items-center gap-1.5 overflow-x-auto pb-2 -mx-1 px-1">
            {proposedRows.length > 0 && gChip('proposed', t('desk_match_grp_proposed'), proposedRows.length)}
            {rejectedRows.length > 0 && gChip('rejected', t('desk_match_grp_rejected'), rejectedRows.length)}
          </div>
        )
      })()}
      <div className="space-y-2">
        {(kind === 'requirement'
          ? (allRows.length === 0 ? [] : visibleSections).flatMap(sec => {
              // Five cards per group (design §8); the rest expand IN PLACE, pushing the
              // list down — the matrix overflow row's rule, never a popover. Pinned and
              // proposed are the agent's own selections: never truncated.
              // ── Round-2 §4: THE CAP IS THE PART THAT GIVES ────────────────────────────
              // Five-per-group is a READING cap, not a data rule, so an arrival the agent
              // just caused is never the thing it hides. Auto-open (built earlier today)
              // was redlined for the right reason: a fold the agent closed is a statement,
              // and reopening it is the same violation as reordering under their cursor.
              // Instead the group renders `5 + arrivals` — the extra cards appended in
              // sort order ABOVE the `კიდევ N` row, each carrying the amber rule — so the
              // bar's number equals what is on screen without moving anything.
              // ⚠️ The excess is ONE READ LONG: `newIds` is released with the bar, so the
              // next recount or reload returns the group to five and the arrivals fall
              // into normal order. A courtesy for the read in progress, never a new cap.
              const shut = collapsedGroups.has(sec.key)
              const open = expandedGroups.has(sec.key) || sec.key === 'pinned' || sec.key === 'proposed'
              const arrivalsHere = sec.rows.filter(m => newIds?.has(m.property_id))
              const capped = sec.rows.slice(0, GROUP_CAP)
              // appended in SORT ORDER, and only the ones the cap would have hidden
              const overflowArrivals = arrivalsHere.filter(m => !capped.includes(m))
              const shown = shut ? [] : open ? sec.rows : [...capped, ...overflowArrivals]
              const hiddenCount = sec.rows.length - (shut ? 0 : shown.length)
              return [
                { __hdr: sec, count: sec.rows.length, shut,
                  // the header says what the VIEW is; the count beside the name stays the
                  // group total, so neither number has to be read as the other
                  extra: shut ? (arrivalsHere.length ? { kind: 'new', n: arrivalsHere.length } : null)
                    : overflowArrivals.length ? { kind: 'plus', base: capped.length, n: overflowArrivals.length } : null },
                ...(shut || sec.rows.length === 0 ? [] : [
                  ...(sec.rows.length === 0 ? [{ __empty: sec }] : []),
                  ...(sec.key === 'READY' && readyCounterweight ? [{ __cw: true }] : []),
                  ...shown,
                  ...(hiddenCount > 0 ? [{ __more: sec, n: hiddenCount }] : []),
                ]),
                ...(!shut && sec.rows.length === 0 ? [{ __empty: sec }] : []),
              ]
            })
          : (() => {
              // §11: the CLIENT direction groups by READINESS TIER — today's work first
              // (ready → confirm → concession); the server's score order survives WITHIN
              // a tier as the tiebreaker.
              const tiers = { ready: [], confirm: [], concession: [] }
              activeRows.forEach(m => tiers[readinessTierOf(m)].push(m))
              return ['ready', 'confirm', 'concession'].filter(k => tiers[k].length)
                .flatMap(k => [{ __tier: k, count: tiers[k].length }, ...tiers[k].map(m => ({ ...m, __tierKey: k }))])
            })()
        ).map(m => m.__hdr ? (
          // RULE 5 grammar: dot · name · count · clause. A zero group greys out but keeps
          // both — the clause is what tells the agent what the group IS.
          // Q5: the heading STICKS on a phone, where a five-card group is most of the
          // viewport and the classification scrolls away from the cards it classifies.
          // ⚠️ It must lose to the diff bar, which is already `sticky top-0 z-10` and sits
          // ABOVE this list: `z-[5]` puts the heading under the bar when they meet, so the
          // bar's sentence is never half-covered by a group name. `bg-card` is required —
          // a transparent sticky element smears the cards scrolling beneath it. `sm:static
          // sm:bg-transparent` returns the desktop heading to the plain flow it has today.
          <button key={`hdr-${m.__hdr.key}`} type="button"
            title={t(m.shut ? 'msug_grp_open' : 'msug_grp_shut')}
            onClick={() => setCollapsedGroups(s => { const n = new Set(s); n.has(m.__hdr.key) ? n.delete(m.__hdr.key) : n.add(m.__hdr.key); return n })}
            className={`w-full text-left text-xs font-semibold pt-1.5 first:pt-0 min-h-[36px] sm:min-h-0 sticky top-0 z-[5] bg-card sm:static sm:z-auto sm:bg-transparent ${m.count === 0 ? 'text-muted-foreground/60' : m.__hdr.tone || 'text-muted-foreground'}`}>
            <span className="text-muted-foreground/70 mr-1">{m.shut ? '›' : '⌄'}</span>
            <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle ${m.count === 0 ? 'bg-muted-foreground/40' : m.__hdr.dot || 'bg-muted-foreground'}`} />
            {t(m.__hdr.labelKey)} <span className="font-normal text-muted-foreground">· {m.count}</span>
            {m.__hdr.ghost > 0 && <span className="font-normal text-primary"> {tLog('msug_ghost_pinned', { n: m.__hdr.ghost })}</span>}
            {/* §4: the header count NEVER inflates — it stays the group total. What the
                view is showing is said separately, in words, so neither number has to be
                read as the other. */}
            {m.extra?.kind === 'plus' && (
              <span className="font-normal text-amber-600 dark:text-amber-400">
                {' '}— {tLog('msug_grp_showing_plus', { base: m.extra.base, n: m.extra.n })}
              </span>
            )}
            {m.extra?.kind === 'new' && (
              <span className="font-normal text-amber-600 dark:text-amber-400"> {tLog('msug_grp_new_inside', { n: m.extra.n })}</span>
            )}
            {m.__hdr.meansKey && !m.extra && <span className="font-normal text-muted-foreground"> — {t(m.__hdr.meansKey)}</span>}
          </button>
        ) : m.__empty ? (
          <p key={`empty-${m.__empty.key}`} className="text-xs text-muted-foreground/60 pl-3">
            {t(m.__empty.emptyKey || 'msug_grp_none')}
          </p>
        ) : m.__cw ? (
          <p key="ready-cw" className="text-xs text-amber-600 dark:text-amber-400 pl-3">
            {t('msug_ready_counterweight')}
          </p>
        ) : m.__more ? (
          // Q5 hit target: this row is ~30px at `py-1.5`, so it joins the 36px floor on a
          // phone. `flex items-center` is only there to keep the label vertically centred
          // inside the taller box; `sm:block sm:min-h-0` hands the row back its current
          // desktop geometry exactly.
          <button key={`more-${m.__more.key}`} type="button"
            className="w-full text-left text-xs text-muted-foreground border border-dashed border-border rounded-lg px-3 py-1.5 hover:border-primary/40 hover:text-foreground transition-colors min-h-[36px] flex items-center sm:block sm:min-h-0"
            onClick={() => setExpandedGroups(s => new Set(s).add(m.__more.key))}>
            {tLog('msug_more_cards', { n: m.n })} · {t('mv2_expand')}
          </button>
        ) : m.__tier ? (
          <p key={`tier-${m.__tier}`} className={`text-xs font-bold tracking-[.02em] pt-1.5 first:pt-0 ${READINESS_TIER[m.__tier].text}`}>
            <span className={`inline-block w-2 h-2 rounded-[3px] mr-1.5 align-middle ${READINESS_TIER[m.__tier].dot}`} />
            {t(READINESS_TIER[m.__tier].labelKey)} <span className="font-normal text-muted-foreground">· {m.count}</span>
          </p>
        ) : kind === 'requirement' ? (
          // Q1 redline, skeleton rule: a stored-only card is NOT tappable while the loading
          // bar shows — expanding a row whose detail never arrived teaches the agent the
          // card is broken. Interactivity returns with the live pass.
          // Q1: a row tap only ever EXPANDS (into the band card below) — it never opens the
          // property modal, which is now the ghost action in the card's action row.
          <div key={m.listing_id}
            data-new-card={newIds?.size && m.property_id === newIds.values().next().value ? '1' : undefined}
            className={`border border-border rounded-lg px-3 py-2 transition-colors ${
              /* §2's two arrival colours: GREEN is the card the agent answered on, AMBER is
                 a card that moved because of that answer. Without the split they cannot tell
                 their own card from its consequences. Both hold 4s and fade over 400ms. */
              greenIds?.has(m.property_id)
                ? `border-l-[3px] duration-[400ms] ${newFading ? 'border-l-transparent' : 'border-l-emerald-500'}`
              : newIds?.has(m.property_id)
                ? `border-l-[3px] duration-[400ms] ${newFading ? 'border-l-transparent' : 'border-l-amber-500'}` : ''} ${
              (m.stored && !m.checks?.length) ? '' : 'cursor-pointer hover:border-primary/40'}`}
            onClick={(m.stored && !m.checks?.length) ? undefined : () => setExpandedRows(s => {
              const next = new Set(s)
              if (next.has(m.property_id)) next.delete(m.property_id); else next.add(m.property_id)
              return next
            })}>
            {/* Media-left (2026-07-16): the photo is the first thing an agent shows a client —
                hiding it forced an open-modal round trip per row. Lazy: off-screen rows cost
                nothing (egress lesson, Phase 2d). */}
            <div className="flex gap-2.5">
              {m.thumb_url && <img loading="lazy" src={m.thumb_url} alt=""
                className="w-20 h-20 rounded-md object-cover shrink-0" />}
              <div className="flex-1 min-w-0">
                {/* Mobile overflow (owner-caught 2026-07-20, MEASURED): the meta group is
                    ~212px (link + age + a Georgian class badge + score) but the column beside
                    an 80px thumbnail is ~179px on a 393px phone — all four are shrink-0, so
                    the row pushed the card ~30px past the modal and the whole modal scrolled
                    sideways (the sticky footer visibly slid under the left border). Fix is
                    two-level wrap: the group drops to its own full-width line on mobile
                    (basis-full → sm:basis-auto keeps desktop identical), and wraps INTERNALLY
                    because even a full line is narrower than the group. basis-0 on the title
                    is what keeps desktop from wrapping — without it the title's max-content
                    hypothetical size breaks the line at every width. */}
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-medium truncate flex flex-1 basis-0 items-center gap-1.5 min-w-0">
                    <NewBadge show={m.is_new} />
                    {/* The stored arm ships NO address (D18), so the skeleton title is the
                        DISTRICT — through the dictionary, never the raw TBS_* code (the
                        designer vocab rule; "no location beats an internal identifier"). */}
                    <span className="truncate"><Icon name="house" /> {addressForDisplay(m.address) || (m.district_code ? districtDisplay(m.district_code, districts) : '') || `${m.source}:${m.source_listing_id}`}</span>
                    {/* cluster-card redesign: same physical unit, N competing agents —
                        one queue slot, the offers table below lists all of them. */}
                    {m.cluster_size > 1 &&
                      <span className="ml-1.5 text-xs text-muted-foreground font-normal align-middle">×{m.cluster_size}</span>}
                  </span>
                  <span className="flex flex-wrap items-center justify-end gap-1.5 shrink-0 basis-full sm:basis-auto">
                    {/* Share this property to another agent (Phase 1a) — copies a deep link;
                        ungated because the link itself leaks nothing (server enforces on open). */}
                    <CopyListingLink compact listingId={m.listing_id} />
                    <RegisteredAgo iso={m.listed_at} activeIso={m.updated_at} titleKey="desk_match_registered_listing_title" />
                    {/* No ClassBadge, no percentage (design §9): the row already sits under
                        a group heading that says its classification. ONE exception, Q4 of
                        the client-surface redline: a PINNED card keeps its classification
                        as a tag — otherwise the pin strips exactly the information it was
                        pinned for (that it still needs a call). */}
                    {m.__pinnedTag && (() => {
                      const g = CLASS_GROUPS.find(x => x.key === classGroupOf(m))
                      return g ? <span className={`text-xs ${g.tone}`}>{t(g.labelKey)}</span> : null
                    })()}
                    {/* Q1: the expand indicator. On a stored-only card the loading bar
                        below is the state signal, so no chevron sits here — a chevron on an
                        inert card would promise a tap the row refuses. */}
                    {!(m.stored && !m.checks?.length) && (
                      <span className="text-xs text-muted-foreground/70">
                        {expandedRows.has(m.property_id) ? '⌃' : '⌄'}
                      </span>
                    )}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {[m.kind, m.rooms != null ? tLog('desk_entry_rooms_abbrev', { rooms: m.rooms }) : null, m.area_sqm != null ? tLog('desk_entry_area_abbrev', { area: m.area_sqm }) : null,
                    m.price != null ? `${m.price} ${m.currency || ''}`.trim() : null, m.source,
                    // Cross-posted family members folded server-side (one row per property) —
                    // the other platforms still show, just as a suffix instead of extra rows.
                    m.also_on?.length ? tLog('desk_match_also_on', { list: [...new Set(m.also_on.map(a => a.source))].join(', ') }) : null,
                  ].filter(Boolean).join(' · ')}
                </p>
                {/* Q5: წყარო collapses to ONE line here on mobile. Per-row source is a desk
                    affordance (the fifth band track), and on a 375px card that track is four
                    truncated characters — so the card states its provenance once, under the
                    facts, and the rows drop the column. First decorated check wins: the
                    endpoint stamps them from the same listing pass, so "most-cited" and
                    "first" are the same string. Renders NOTHING when no check carries a
                    source — a blank provenance line lies about the scorer the same way a
                    blank column does. */}
                {(() => {
                  const src = (m.checks || []).find(c => c && c.source)
                  const line = src ? sourceOf(src, bandAns) : ''
                  return line ? <p className="text-xs text-muted-foreground/70 sm:hidden">{line}</p> : null
                })()}
                {/* CertaintyDetail (the % bar) left this card 2026-08-12 — design §9: score
                    and coverage exist but are never rendered as a quality claim; the group
                    heading is the classification and MatchExplain below carries the
                    reasons in words. The per-check breakdown lives in the designed
                    expanded card (see the client-surface brief), not behind a bar. */}
                {/* v2 answers Q3: the detail-arrives-late token. A stored row has the
                    structure (address slot, facts, verdict) and no checks yet — the
                    pulsing shape says "coming", which neither `–` nor `?` may claim. */}
                {m.stored && !m.checks?.length && (
                  <p className="mt-1 flex items-center gap-1.5"><LoadingBar />
                    <span className="text-xs text-muted-foreground/70">{t('mv2_details_loading')}</span></p>
                )}
              </div>
            </div>
            {/* OwnershipLine used to sit here. It said the same thing as the footer row
                below (agent name + the same reveal control), so a one-offer card asked
                "who do I call" twice in two layouts. One answer, one place: the footer. */}
            <MatchTermsLine cooperation={m.cooperation} restrictions={m.restrictions} />
            {(() => {
              const certGaps = certaintyGaps(m.checks)
              const hide = certGaps.length ? new Set(certGaps.map(c => c.key)) : null
              // Q5: the MARKS become a chip on the collapsed card. MatchExplain only ever
              // shows what is wrong or open (it filters `status !== 'pass'`), so on a phone —
              // where the expansion's ✓ band is a tap away rather than in view — the card
              // never says how much DID match. One chip, before the existing chips, using the
              // same pill factory: no new geometry, and the count is the plain number of
              // passing checks the ✓ band would list.
              const matched = (m.checks || []).filter(c => c && c.status === 'pass').length
              return (<>
                {matched > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1 sm:hidden">
                    <span className={pill('none', { extra: 'border-emerald-500/40 text-emerald-600 dark:text-emerald-400' })}>
                      ✓ {tLog('msug_m_matched', { n: matched })}
                    </span>
                  </div>
                )}
                <MatchExplain checks={m.checks} deltas={m.deltas} hideKeys={hide} />
                <ReducedBecause gaps={certGaps} />
              </>)
            })()}
            {/* B193: primary_action is now {action, policy, confidence} — the action
                itself rides inside, so the choice can name the policy that made it. */}
            <PrimaryAction action={m.primary_action?.action} />
            {/* Follow-through chip + verdicts share one row (2026-07-18), mirroring
                the client-direction layout. */}
            <div className="flex flex-wrap items-center justify-between gap-2 mt-1.5">
              <ProposedFollowUp verdict={m.verdict} />
              <span className="flex-1" />
              {/* Q1: the property modal is a GHOST action now — the card's own tap expands
                  in place (the band card below), so the modal moved here, one deliberate
                  press among the row actions rather than the row's default meaning.
                  Q5 hit targets: all four icon buttons on this row are `w-9 h-9` (36px) on a
                  phone and `sm:w-7 sm:h-7` (the current 28px) from 640px up. 28px is under
                  every touch minimum and these four sit shoulder to shoulder — a mis-tap here
                  fires a share or a verdict. Desktop is a mouse and must not grow. */}
              <button type="button" title={t('msug_open_property')}
                className="shrink-0 w-9 h-9 sm:w-7 sm:h-7 rounded-lg border border-border text-muted-foreground hover:border-primary/40 hover:text-foreground inline-flex items-center justify-center"
                onClick={e => { e.stopPropagation(); onOpenListing?.(m.listing_id) }}>
                <Icon name="external-link" size={12} />
              </button>
              {/* Entry point 3, demand direction (MEETINGS_MODEL §3) — the mirror of
                  the button on the client rows. Same seed builder, opposite sides. */}
              {/* Send THIS property to the client whose demand this panel is
                  anchored on. This is where the workflow actually starts (owner,
                  2026-07-24): the property is offered to the client, who decides
                  whether it is worth meeting over — so the share belongs next to
                  the booking, not only on the listing card. */}
              <button type="button" title={t('share_tooltip')}
                className="shrink-0 w-9 h-9 sm:w-7 sm:h-7 rounded-lg border border-border text-muted-foreground hover:border-primary/40 hover:text-foreground inline-flex items-center justify-center"
                onClick={e => { e.stopPropagation(); setShareSeed({
                  propertyId: m.property_id, propertyLabel: m.address || m.district_code || null,
                  seedPrice: m.price ?? null, seedCurrency: m.currency || null,
                }) }}>
                <Icon name="link" size={12} />
              </button>
              <button type="button" title={t('meeting_create_btn')}
                className="shrink-0 w-9 h-9 sm:w-7 sm:h-7 rounded-lg border border-border text-muted-foreground hover:border-primary/40 hover:text-foreground inline-flex items-center justify-center"
                onClick={e => { e.stopPropagation(); setMeetingSeed(seedFromMatchRow(kind, m, meetingAnchor)) }}>
                <Icon name="calendar" size={12} />
              </button>
              {/* The price story for THIS property with THIS client. The client
                  party is not resolved here on purpose — the server derives it
                  from the requirement, the same single place createMeeting uses.
                  ⚠️ Icon is a BANKNOTE, not message-circle (owner 2026-07-24: 💬
                  "looks like a messenger app emoji"). This opens a price thread,
                  not a chat — and a chat bubble promises replies we don't have.
                  Keep both entry points on the same icon; NegotiationModal's own
                  tag/scale already mean asking price / floor INSIDE the thread. */}
              <button type="button" title={t('nego_tooltip')}
                className="shrink-0 w-9 h-9 sm:w-7 sm:h-7 rounded-lg border border-border text-muted-foreground hover:border-primary/40 hover:text-foreground inline-flex items-center justify-center"
                onClick={e => { e.stopPropagation(); setNegoSeed({
                  propertyId: m.property_id, requirementId: id, intent: m.intent || null,
                  propertyLabel: m.address || m.district_code || null,
                }) }}>
                <Icon name="banknote" size={12} />
              </button>
              <VerdictActions inline state={effVerdict(m)} busy={verdictBusy} onSet={s => applyVerdict(m, s)} />
            </div>
            <MatchOffersTable offers={m.offers} onOpenListing={onOpenListing} myCooperation={m.my_cooperation}
              address={addressForDisplay(m.address)} />
            {/* Q1: the expanded band card — the SAME four-column grammar MatchMatrix's
                expanded row renders (matchBands.jsx), so the two surfaces read identically.
                Differences are this surface's honest limits, not styling:
                  • წყარო (source) is CONDITIONAL — the requirement direction's payload only
                    carries check.source since B573 step 3, so the column renders (same
                    header + sourceOf cell as the matrix) only when at least one of this
                    row's checks actually carries one; against an older server the column is
                    omitted entirely, because a column of blanks reads as missing evidence
                    rather than as a column that has not shipped.
                ⚠️ THE THIRD BAND'S FOURTH COLUMN IS NO LONGER EMPTY (round-2 §3). The note
                that stood here said GapRow/QuietRow "carry NO capture controls — the
                listing's write doors need listing context this panel does not hold". They
                hold it now: the doors moved to matchBands.jsx and the context is fetched
                PER EXPANDED CARD by ClientCardBands, which is also why that component
                exists at all. `ans` is likewise no longer vocab-only in there.
                MOUNTED ONLY WHILE OPEN, deliberately: the mount IS the fetch. */}
            {expandedRows.has(m.property_id) && (
              <ClientCardBands m={m} bandAns={bandAns} agentId={fillAgentId}
                onOpenListing={onOpenListing} openDemand={onRefineCriteria || undefined}
                fillCtx={fillCtx} onAnswered={() => onInBandAnswered(m.property_id)} />
            )}
          </div>
        ) : (
          <div key={m.requirement_id}
            className={`border border-border rounded-lg px-3 py-2 cursor-pointer hover:border-primary/40 transition-colors ${m.__tierKey ? `border-l-[3px] ${READINESS_TIER[m.__tierKey].border}` : ''}`}
            onClick={() => onOpenDemand?.(m.requirement_id)}>
            {/* Same two-level wrap as the listing card above — this side has no thumbnail
                but carries an urgency badge + a represented-by handshake, so it runs out of
                room on a phone the same way. */}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm font-medium truncate flex flex-1 basis-0 items-center gap-1.5 min-w-0">
                <NewBadge show={m.is_new} />
                {/* Urgency driver (2026-07-16, mirror redesign): no photos on this side — the
                    left "thumbnail" slot is the client's urgency, the primary call-first signal. */}
                {(m.urgency === 'immediate' || m.urgency === 'soon') && (
                  <span className={`shrink-0 rounded px-1.5 py-0.5 text-xs font-bold ${m.urgency === 'immediate'
                    ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                    : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'}`}>
                    {m.urgency === 'immediate' ? t('desk_match_urgency_now') : t('desk_match_urgency_soon')}
                  </span>
                )}
                <span className="truncate"><Icon name="user-check" /> {m.person?.name && m.person.name !== 'Unknown contact' ? m.person.name : (m.title || t('desk_match_fallback_client'))}
                {m.person?.is_agent ? ` ${t('desk_match_agent_suffix')}` : ''}</span>
                {/* Masked handshake (2026-07-16): another agent's client — the ACTION is
                    "talk to the representing agent", so say who, prominently, instead of a
                    dead-end anonymous row. */}
                {m.ownership?.represented_by?.agent_name && (
                  <Badge variant="yellow" label={<>
                    <Icon name="handshake" size={11} /> {tLog('desk_match_via_agent', { name: m.ownership.represented_by.agent_name })}
                  </>} />
                )}
                {/* Representing agent for UNMASKED views too (owner request 2026-07-16:
                    clients get a representative tag just like properties do) — admin and
                    own-client rows never hit the masked branch above, so they showed no
                    agent at all. Quiet tag, not the handshake: no action implied. */}
                {!m.ownership?.represented_by && m.ownership?.owner_agent?.agent_name && !m.ownership?.is_own && (
                  <span className="shrink-0 text-xs text-muted-foreground"><Icon name={ROLE_ICON.agent} size={11} /> {m.ownership.owner_agent.agent_name}</span>
                )}
              </span>
              <span className="flex flex-wrap items-center justify-end gap-1.5 shrink-0 basis-full sm:basis-auto">
                <RegisteredAgo iso={m.created_at}
                  activeIso={confirmedAt[m.requirement_id] || m.last_activity_at}
                  titleKey="desk_match_registered_client_title" />
                <ClassBadge classification={m.classification} />
                {scoreChip(m.score, m.coverage)}
              </span>
            </div>
            {/* urgency text dropped from this line (2026-07-17 density pass) — the NOW/SOON
                badge in the title row already says it; twice per card read as noise. */}
            {/* labels.js + district names (designer vocab rule 2026-07-23): m.intent and
                district codes rendered RAW here — the exact "speaks database" complaint. */}
            <p className="text-xs text-muted-foreground">{[m.title, intentLabel(m.intent, 'demand')].filter(Boolean).join(' · ')}
              {/* Mirror direction shows the client's KEY constraints inline (budget/rooms/
                  districts) — agents opened every client just to read these (2026-07-16). */}
              {m.demand && <> · {[
                m.demand.budget_usd?.max != null ? `≤ $${Number(m.demand.budget_usd.max).toLocaleString()}` : null,
                m.demand.rooms?.min != null || m.demand.rooms?.max != null ? tLog('desk_entry_rooms_abbrev', { rooms: [m.demand.rooms.min, m.demand.rooms.max].filter(x => x != null).join('–') }) : null,
                m.demand.districts?.length ? m.demand.districts.slice(0, 3).map(c => districtDisplay(c, districts)).join(', ') : null,
              ].filter(Boolean).join(' · ')}</>}
            </p>
            {(() => {
              // §11 card face: certainty (labeled bar, breakdown behind it), the one
              // "blocked by shared owner questions ↑" line (the questions themselves live
              // ONLY in the hoisted strip), the violet client-gaps headline, and the
              // score-drag line. The per-check chip grammar (MatchExplain) left the face —
              // every check stays enumerable in the breakdown.
              const clientGaps = (m.checks || []).filter(c => c.status === 'unknown' && whoAnswers(c) === 'client')
              const certGaps = certaintyGaps(m.checks)
              const ownerBlocked = hoistKeys ? (m.checks || []).filter(c => c.status === 'unknown' && hoistKeys.has(c.key)).length : 0
              return (<>
                <CertaintyDetail checks={m.checks} />
                {ownerBlocked > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    <Icon name="arrow-up" size={11} /> {tLog('desk_match_blocked_by_shared', { n: ownerBlocked })}
                  </p>
                )}
                <ClientGapsChip gaps={clientGaps} />
                <ReducedBecause gaps={certGaps} />
              </>)
            })()}
            {/* §11 ONE dominant action per tier: ready → OFFER (WhatsApp/call the client +
                mark proposed); confirm/concession → open the client; masked → contact the
                representing agent. Pin = bookmark icon; reject lives behind ⋯. */}
            {(() => {
              const st = effVerdict(m)
              const viaAgent = m.ownership?.represented_by?.agent_name
              const tier = m.__tierKey || readinessTierOf(m)
              const a = m.primary_action?.action   // B193: unwrap {action, policy, confidence}
              const ownerAction = a?.kind === 'verify' && hoistKeys?.has(a.delta_ref)
              const clientLabel = !ownerAction && a?.label ? a.label : null
              // §11 outreach (owner 2026-07-24: "Propose must actually reach the client, not
              // just tag it"). An UNMASKED own client ships its phone in the payload (D14/D26),
              // so the ready action DOES the outreach — WhatsApp or call — and marks proposed
              // in the same tap. wa.me wants digits only; a bare 9-digit GE number gets 995
              // (same convention as WhatsAppShare). No phone on file → the button opens the
              // client instead (where a number can be added), never a dead tag.
              const clientPhone = !viaAgent ? (m.person?.phone || null) : null
              const waDigits = clientPhone ? String(clientPhone).replace(/\D/g, '') : null
              const waNum = waDigits ? (waDigits.length === 9 ? `995${waDigits}` : waDigits) : null
              const offerText = encodeURIComponent(address || '')
              const reach = (channel) => {
                applyVerdict(m, 'proposed')  // tag: pipeline + the follow-through reminder
                const href = channel === 'wa'
                  ? `https://wa.me/${waNum}${offerText ? `?text=${offerText}` : ''}`
                  : `tel:${clientPhone}`
                window.open(href, '_blank', 'noopener,noreferrer')
              }
              const primary = viaAgent
                ? { label: tLog('desk_match_contact_agent', { name: viaAgent }), cls: 'border border-border text-foreground hover:bg-accent/50', go: () => onOpenDemand?.(m.requirement_id) }
                : tier === 'confirm'
                  ? { label: clientLabel || t('desk_match_tier_confirm_action'), cls: 'border border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400 hover:bg-amber-500/20', go: () => onOpenDemand?.(m.requirement_id) }
                  : tier === 'concession'
                    ? { label: clientLabel || t('desk_match_tier_concession_action'), cls: 'border border-border text-muted-foreground hover:bg-accent/50', go: () => onOpenDemand?.(m.requirement_id) }
                    // ready but no phone on file — open the client so a number can be added
                    : { label: t('desk_match_tier_ready_action'), cls: 'bg-green-600 text-white hover:bg-green-600/90', go: () => onOpenDemand?.(m.requirement_id) }
              const readyOutreach = tier === 'ready' && !viaAgent && !!clientPhone
              return (<>
                <div className="flex flex-wrap items-center gap-1.5 mt-1.5 empty:hidden">
                  <ProposedFollowUp verdict={m.verdict} />
                  <ConfirmActiveButton m={m} confirmedAt={confirmedAt[m.requirement_id]}
                    onConfirmed={id => setConfirmedAt(v => ({ ...v, [id]: new Date().toISOString() }))} />
                </div>
                <div className="flex items-center gap-2 mt-2" onClick={e => e.stopPropagation()}>
                  {readyOutreach ? (<>
                    {/* WhatsApp is the primary outreach (dominant in GE); the ☎ calls. Both
                        mark proposed. Once proposed the green shows a ✓ but STILL reaches out
                        again (re-contacting a proposed client is normal follow-up). */}
                    <button type="button" disabled={verdictBusy} title={t('desk_match_offer_whatsapp')}
                      className="flex-1 min-w-0 truncate text-xs font-bold rounded-lg px-3 py-2 bg-green-600 text-white hover:bg-green-600/90 disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
                      onClick={() => reach('wa')}>
                      <Icon name="message-circle" size={13} />
                      {st === 'proposed' ? `✓ ${t('desk_match_act_proposed')}` : t('desk_match_tier_ready_action')}
                    </button>
                    <button type="button" disabled={verdictBusy} title={t('desk_match_offer_call')}
                      className="shrink-0 w-8 h-8 rounded-lg border border-green-600/50 text-green-600 dark:text-green-500 hover:bg-green-600/10 inline-flex items-center justify-center disabled:opacity-50"
                      onClick={() => reach('tel')}>
                      <Icon name="phone" size={13} />
                    </button>
                  </>) : (
                    <button type="button" disabled={verdictBusy} title={primary.label}
                      className={`flex-1 min-w-0 truncate text-xs font-bold rounded-lg px-3 py-2 disabled:opacity-50 ${primary.cls}`}
                      onClick={primary.go}>
                      {primary.label}
                    </button>
                  )}
                  <RowActions items={[
                    { key: 'pin', icon: 'pin', title: t('desk_match_act_shortlist'), busy: verdictBusy,
                      active: st === 'shortlist',
                      onClick: () => applyVerdict(m, st === 'shortlist' ? 'clear' : 'shortlist') },
                    // Mirror direction: rows are CLIENTS, the anchor is the property.
                    // Sharing sends the anchor property to THIS client — the recipient is
                    // known here, so it lands on the share and "already sent" can name who.
                    // person is null when the client is masked; the share still works, just
                    // without a recorded recipient.
                    meetingAnchor.propertyId && { key: 'share', icon: 'link', title: t('share_tooltip'),
                      onClick: () => setShareSeed({
                        propertyId: meetingAnchor.propertyId,
                        propertyLabel: meetingAnchor.propertyLabel,
                        recipientPartyId: m.person?.party_id || null,
                        recipientName: m.person?.name || null,
                      }) },
                    // Entry point 3 (MEETINGS_MODEL §3): everything is already known on this
                    // row — property, client, and who represents each — so the whole booking
                    // is one press. No searching, no typing.
                    { key: 'meeting', icon: 'calendar', title: t('meeting_create_btn'),
                      onClick: () => setMeetingSeed(seedFromMatchRow(kind, m, meetingAnchor)) },
                    // The price story with this client. person is null when the client is
                    // masked — the requirement id still resolves the party server-side, so
                    // the thread opens either way (and the modal shows the representing
                    // agent instead of a name it may not release).
                    meetingAnchor.propertyId && { key: 'nego', icon: 'banknote', title: t('nego_tooltip'),
                      onClick: () => setNegoSeed({
                        propertyId: meetingAnchor.propertyId,
                        clientPartyId: m.person?.party_id || null,
                        requirementId: m.requirement_id,
                        intent: m.intent || null,
                        propertyLabel: meetingAnchor.propertyLabel,
                        clientLabel: m.person?.name || null,
                      }) },
                    { key: 'reject', icon: 'x', busy: verdictBusy,
                      title: t(st === 'rejected' ? 'desk_match_act_undo' : 'desk_match_act_reject'),
                      onClick: () => applyVerdict(m, st === 'rejected' ? 'clear' : 'rejected') },
                  ]} />
                </div>
              </>)
            })()}
          </div>
        ))}
        {/* Rejected matches collapse to one grey line each — visible enough to undo, quiet
            enough to stop resurfacing (0102). Demand direction: hidden behind the ❌ chip
            (advice round 3 — "done" lists leave the triage scroll); mirror keeps them at
            the bottom as before. */}
        {rejectedRows.length > 0 && (kind !== 'requirement' || groupFilter === 'rejected') && (
          <div className="pt-1 space-y-1">
            {rejectedRows.map(m => (
              <div key={vKey(m)} className="flex items-center gap-2 text-xs text-muted-foreground opacity-70">
                <span className="truncate"><Icon name="x" size={11} /> {kind === 'requirement'
                  ? (addressForDisplay(m.address) || m.district_code || m.source)
                  : (m.person?.name || m.title || t('desk_match_fallback_client'))}
                  {effReason(m) ? ` — ${effReason(m)}` : ''}</span>
                <button type="button" className="shrink-0 underline decoration-dotted hover:text-foreground"
                  onClick={e => { e.stopPropagation(); applyVerdict(m, 'clear') }}>
                  {t('desk_match_act_undo')}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* B113 — the escape hatch. Matching stays deliberately narrow: it only ever considers
          the agency's own supply, and widening it to ~18k crawler leads would drown the
          panel it exists to keep sharp. But "nothing fits" is exactly when an agent needs
          the wider pool, and until now their only route was to leave and browse a portal by
          hand. This turns the client's own stated requirement into a Leads search.
          It rides the ordinary committed-search URL, so there is NO new apply code — the
          same restore path saved searches use (see requirementToCommitted). */}
      {/* NO SILENT CAP. Measured on live data 2026-08-12: a client averages 640 eligible
          edges and one carries 2,353, against a 50-row server cap — so the cap bites on
          nearly every client, and a panel that shows 50 while its header counts 640 reads
          as "this is all there is". Same shape and same words as the mirror panel's
          `capped` line, deliberately: two surfaces, one grammar. */}
      {/* §5: NAME THE CUT. A bare `50 / 640` is honest and useless — it says the list is a
          cut without saying WHICH cut, and the only inference left is "the other 590 are
          worse", which is exactly the quality claim this panel refuses to make. Naming the
          SORT is a statement about the list; a score would be a claim about a property, and
          only that is forbidden. ⚠️ The sort CONTROL (ახლოს · ახალი განცხადება · იაფი ·
          ნაკლები კითხვა) is not here yet: each order has to change WHICH 50 arrive, so it
          is a server argument applied before the limit slice, not a client-side re-sort of
          the 50 we already hold — see B679. */}
      {kind === 'requirement' && data && !data.too_broad && (data.eligible_count ?? 0) > allRows.length && (
        <p className="text-xs text-muted-foreground/70 mt-1">
          {tLog('msug_cut_named', { n: allRows.length, total: data.eligible_count })}
        </p>
      )}
      {/* §5's last piece: B569's ladder RUN IN REVERSE. The empty-result ladder asks what
          one relaxation would open; at the cap the honest sentence is the mirror — this
          many matches means the requirement never narrowed. The server sends `breadth`
          ONLY when the cap bit, so this can never appear over a list of twelve.
          ⚠️ The advice half is dropped when `unset` is empty: a requirement that states
          everything and still returns 640 gets the plain sentence, never invented advice.
          ⚠️ The control appears only when this viewer can actually edit the criteria —
          the parent passes `onRefineCriteria` only then. A button that names an action the
          viewer does not have is the defect this panel has already shipped twice. */}
      {/* ⚠️ ROUND 3 §6 — TWO LINES, ONE FIELD CHECK. The first build printed "the requirement
          is too broad" at the cap regardless, and the designer's ruling is that this is
          FALSE when all four dimensions are stated — *blaming the record for a fact about
          the market*, which is precisely the failure the second empty state exists to
          avoid. There is no query, no probe and no count behind the choice: `unset` is a
          field check the server already did.
          A (all four stated) is the COMMON case, not the rare one — the cap bites on nearly
          every client, so "well specified, and the market is wide" is the normal state of
          this surface. It carries NO button: the sort control directly above IS the action,
          and the sentence's last clause points at it in words.
          📌 And never suggest narrowing a STATED condition: B569's ladder relaxes conditions
          when nothing matches, but there is no symmetric right to tighten one the client
          gave us — this surface's whole grammar is that a stated condition binds until the
          client lifts it. */}
      {kind === 'requirement' && data?.breadth?.at_cap && (() => {
        const unset = data.breadth.unset || []
        const wellSpecified = unset.length === 0
        return (
          <div className={`mt-1.5 rounded-lg border px-2.5 py-1.5 flex flex-wrap items-center gap-2 ${
            wellSpecified ? 'border-border bg-muted/30' : 'border-amber-500/40 bg-amber-500/[.06]'}`}>
            <p className="text-xs text-foreground flex-1 min-w-0">
              {wellSpecified ? t('msug_well_specified') : (<>
                {tLog('msug_too_broad', { n: data.eligible_count })}
                {' '}
                {/* B names the missing fields — known without a query, and it turns the
                    button from a shrug into a specific instruction. */}
                <span className="text-muted-foreground">
                  {tLog('msug_too_broad_unnamed', {
                    list: unset.slice(0, 2).map(k => t(BREADTH_NOUN[k] || k)).join(t('msug_and_join')),
                  })}
                </span>
              </>)}
            </p>
            {!wellSpecified && onRefineCriteria && (
              <button type="button" className={`${btn('outline')} text-xs whitespace-nowrap min-h-[36px] sm:min-h-0`}
                onClick={onRefineCriteria}>{t('msug_refine')}</button>
            )}
          </div>
        )
      })()}
      {kind === 'requirement' && criteria && <LeadEscapeHatch criteria={criteria} intent={intent} propertyCategory={propertyCategory} requirementId={id} clientName={clientName} onLeave={onLeaveToSearch} leadSince={leadSince} />}
      {/* No refresh prop reaches this panel, so a booking made here announces itself with a
          window event; IntakeTab listens and refreshes the feed + 📅 chips (review 2026-09-23). */}
      {meetingSeed && <MeetingModal seed={meetingSeed} onClose={() => setMeetingSeed(null)}
        onCreated={m => window.dispatchEvent(new CustomEvent('sntv:meeting-created', { detail: { meeting: m || null } }))} />}
      {shareSeed && <ShareModal {...shareSeed} onClose={() => setShareSeed(null)} />}
      {negoSeed && <NegotiationModal {...negoSeed} onClose={() => setNegoSeed(null)} />}
    </div>
  )
}

// ── Exports — everything IntakeTab.jsx (or a sibling desk module) still references ──
export {
  listingDeepLink, CopyListingLink, WhatsAppShare,
  EDITABLE_CRITERIA_FIELDS, ARRAY_EDIT_FIELDS, diffCriteriaOps,
  TIER_STYLE, TIER_ICON, STANCE_CYCLE, STANCE_STYLE, STANCE_ICON, StanceChip,
  BetterToKnow, clientGaps, CLIENT_COMPLETENESS_TOTAL, GapFillRow, CompletenessMeter,
  listingCompleteness, propertyGaps,
  // B568: the v2 matrix panel (MatchMatrix.jsx) renders the SAME checks in a different
  // shape. It imports these rather than re-deriving them — one definition of a check's
  // mark, its human label, its delta sentence, and of "is this gap the client's side"
  // (B273: two implementations in two components is the drift, not the shape).
  // ⚠️ CHECK_MARK / humanMatchKey / deltaLabel / checkTier / bareCheckKey /
  // logUnmappedMatchKey now LIVE in matchBands.jsx (Q1 extraction) and are re-exported
  // here unchanged, so every existing importer keeps working without edits.
  CHECK_MARK, humanMatchKey, deltaLabel, checkTier, bareCheckKey, logUnmappedMatchKey,
  CLIENT_GAP_RE, RowActions, HoistedStrip, ownerAnswerKind,
  // The owner's three answer doors, as DATA rather than as three copies of a popover: the
  // v2 matrix answers a gap from inside the band as well as from the strip, and a second
  // hand-written {pets: 'pets_allowed'} map in that file is exactly the drift B273 forbids.
  // (NumTermInput is deliberately NOT exported alongside them: the matrix renders its own
  // InlineNumber, shared by the owner and client sides, and nothing imports this one.)
  // ⚠️ TERM_CHIPS / NUM_TERM_CHIPS / ownerAnswerKind / STANCE_* now LIVE in matchBands.jsx
  // (round-2 §3, with the controls that read them) and are re-exported here unchanged.
  TERM_CHIPS, NUM_TERM_CHIPS,
  ClassBadge, MatchExplain, PrimaryAction, OpenTouchNudge,
  AttemptOutcome, ContactReveal, AccessRequestPanel, AccessRequestsNotice,
  MatchOffersTable, MatchTermsLine, AgentPostingsGroup,
  CertaintyBar, CertaintyDetail, ClientGapsChip, ReducedBecause,
  RegisteredAgo, NewBadge, ConfirmActiveButton, ProposedFollowUp,
  VerdictActions, MatchSuggestions,
}
